/*
 * @Copyright 2023-2025. Institute for Future Intelligence, Inc.
 */

export const i18n_en = {
  translation: {
    name: {
      IFI: 'Institute for Future Intelligence',
      AIMS: 'AIMS',
      Tagline: 'Artificial Intelligence for Molecular Sciences — Institute for Future Intelligence',
    },

    cookie: {
      Statement: 'By clicking Accept, you agree to our use of cookies to improve your experience with AIMS.',
      Accept: 'Accept',
      AgreementPrefix: 'By using AIMS, you also agree to our',
      AgreementConnector: 'and',
      AgreementSuffix: '.',
    },

    legal: {
      WelcomeToAIMS: 'Welcome to AIMS',
      AcceptancePromptPrefix: 'Before you continue, please review our',
      AcceptancePromptSuffix: '. You must accept both to use AIMS.',
      IAgreeToTheTermsAndPrivacy: 'I have read and agree to the Terms of Service and Privacy Policy.',
      Accept: 'Accept',
      Decline: 'Decline',
      AcceptanceFailedTryAgain: 'Failed to record your acceptance. Please try again',
    },

    tooltip: {
      gotoIFI: 'Go to Institute for Future Intelligence',
      clickToOpenMenu: 'Click to open main menu',
      clickToAccessAccountSettings: 'Click to access account settings',
      openExamples: 'Browse built-in examples',
      openCommons: 'Browse the Commons in a new tab',
      openTutorial: 'Open the tutorial in a new tab',
      atomCount: 'The total number of atoms in the molecule.',
      bondCount: 'The total number of covalent bonds in the molecule.',
      molecularMass: 'The mass of the molecule, calculated as the sum of all of its atoms.',
      logP: 'The partition coefficient in logarithmic form. The partition coefficient is the ratio of concentrations of a compound in a mixture of two immiscible solvents at equilibrium, which is used to compare the solubilities of the solute in these two solvents.',
      hydrogenBondDonorCount:
        'The number of hydrogen bond donors in this molecule. A hydrogen bond donor is a chemical group that provides the hydrogen atom in a hydrogen bond.',
      hydrogenBondAcceptorCount:
        'The number of hydrogen bond acceptors in this molecule. A hydrogen bond acceptor is a chemical group that participates in a hydrogen bond but does not supply the shared hydrogen atom.',
      rotatableBondCount:
        'The number of single-order non-ring bonds where atoms on either side of the bond are bound to non-hydrogen atoms. Rotation around such a bond axis changes the overall shape of the molecule.',
      polarSurfaceArea: 'The sum of the surface areas over all the polar atoms of the molecule.',
      heavyAtomCount: 'The number of heavy atoms in the molecule. A heavy atom is any atom but hydrogen.',
      complexity: 'The complexity of the molecule measured from both composition and structure.',
      density: 'The mass per unit of volume of a substance.',
      boilingPoint:
        'The temperature at which the compound changes state from liquid to gas at a given atmospheric pressure.',
      meltingPoint:
        'The temperature at which the compound changes state from solid to liquid at a given atmospheric pressure.',
      fsp3: 'Fraction of sp³ hybridized carbons (Lovering, 2009). Higher Fsp³ is associated with three-dimensional structure and downstream clinical success.',
      ringCount:
        'Total number of rings (cyclomatic number on the heavy-atom graph). Counts independent cycles, so naphthalene is 2 and biphenyl is 2.',
      aromaticRingCount: 'Number of fully aromatic rings, the subset of total rings whose bonds are all aromatic.',
      qed: 'Quantitative Estimate of Drug-likeness (Bickerton 2012). Weighted geometric mean of eight desirability functions, mapped to [0, 1]; ≥0.5 is the common rule-of-thumb threshold.',
      smilesFormat:
        'Simplified Molecular Input Line Entry System (SMILES) is a format\nthat represents a molecular structure as a text string. The following\nshows the search results based on SMILES.',
      inchiFormat:
        'The IUPAC International Chemical Identifier (InChI) is a format\nthat represents a molecular structure as a text string. The following\nshows the search results based on InChI.',

      // Crystal-property tooltips. Keys match CrystalProperties field names
      // so verticalAxis.tsx's dynamic lookup (t('tooltip.' + variable))
      // resolves them. density / meltingPoint / boilingPoint are reused
      // from the molecule entries above.
      sublimationPoint:
        'The temperature at which a solid changes directly to a gas at a given pressure without passing through the liquid phase.',
      specificHeat:
        'Heat capacity per unit mass — the energy in joules required to raise 1 gram of the substance by 1 K.',
      thermalConductivity:
        'How readily heat flows through the material, in W/(m·K). Metals are high; insulators and molecular crystals are low.',
      thermalExpansion: 'Linear thermal-expansion coefficient at room temperature, in units of 10⁻⁶ /K.',
      debyeTemperature:
        'A characteristic temperature in lattice-vibration theory; the upper bound of phonon frequencies, in K.',
      formationEnthalpy:
        'Standard enthalpy of formation (ΔH°f) of the bulk solid from its elements in their reference states, in kJ/mol.',
      youngsModulus: 'Elastic stiffness in tension/compression — stress divided by strain, in GPa.',
      bulkModulus: 'Resistance to uniform compression — pressure change divided by volumetric strain, in GPa.',
      shearModulus: 'Resistance to shape change under shear stress, in GPa.',
      poissonRatio: 'The negative ratio of transverse strain to axial strain — dimensionless, typically 0.1–0.45.',
      mohsHardness: 'Mineralogical scratch-resistance scale from 1 (talc) to 10 (diamond).',
      vickersHardness:
        'Hardness measured with a pyramidal diamond indenter, expressed as load over indentation area in GPa.',
      bandGap:
        'Energy gap between the valence and conduction bands, in eV. Zero for metals; ~1 eV for semiconductors; >3 eV for wide-gap insulators.',
      electricalResistivity:
        'How strongly the material opposes electric current, in Ω·m. Metals ~10⁻⁸; insulators >10¹⁰.',
      workFunction: 'Minimum energy needed to remove an electron from the surface to vacuum, in eV.',
      dielectricConstant:
        'Relative static permittivity — by how much the material reduces the electric field of a charge embedded in it. Dimensionless.',
      refractiveIndex: 'Ratio of the speed of light in vacuum to its speed in the medium, at visible wavelengths.',
      magneticSusceptibility:
        'Degree of magnetization in response to an applied field; positive = paramagnetic, negative = diamagnetic. Molar units ×10⁻⁶ cm³/mol.',
      curieTemperature:
        'Above the Curie temperature, ferromagnets become paramagnetic (and ferroelectrics become paraelectric). Stored in K.',
      molarMass: 'Mass per mole of formula units, in g/mol — derived from the basis.',
      atomsPerCell: 'Number of atoms in the conventional unit cell.',
      cellVolume: 'Volume of the conventional unit cell, in Å³.',
      packingFraction:
        'Fraction of cell volume occupied by atomic spheres (0..1) — 0.74 for ideal FCC/HCP close packing.',
    },

    url: {
      atomCount: 'https://en.wikipedia.org/wiki/Atom',
      bondCount: 'https://en.wikipedia.org/wiki/Covalent_bond',
      molecularMass: 'https://en.wikipedia.org/wiki/Molecular_mass',
      logP: 'https://en.wikipedia.org/wiki/Partition_coefficient',
      hydrogenBondDonorCount: 'https://en.wikipedia.org/wiki/Hydrogen_bond',
      hydrogenBondAcceptorCount: 'https://en.wikipedia.org/wiki/Hydrogen_bond',
      rotatableBondCount: 'https://en.wikipedia.org/wiki/Conformational_isomerism',
      polarSurfaceArea: 'https://en.wikipedia.org/wiki/Chemical_polarity',
      heavyAtomCount: '',
      complexity: 'https://pubmed.ncbi.nlm.nih.gov/37675804/',
      density: 'https://en.wikipedia.org/wiki/Density',
      boilingPoint: 'https://en.wikipedia.org/wiki/Boiling_point',
      meltingPoint: 'https://en.wikipedia.org/wiki/Melting_point',
      qed: 'https://en.wikipedia.org/wiki/Druglikeness',
      aromaticRingCount: 'https://en.wikipedia.org/wiki/Aromaticity',
      ringCount: 'https://en.wikipedia.org/wiki/Cyclic_compound',
      fsp3: 'https://en.wikipedia.org/wiki/Orbital_hybridisation',

      // Crystal-property reference links. Empty string suppresses the
      // "more info" affordance for fields without a canonical Wikipedia
      // page (predicted/derived ones).
      sublimationPoint: 'https://en.wikipedia.org/wiki/Sublimation_(phase_transition)',
      specificHeat: 'https://en.wikipedia.org/wiki/Specific_heat_capacity',
      thermalConductivity: 'https://en.wikipedia.org/wiki/Thermal_conductivity',
      thermalExpansion: 'https://en.wikipedia.org/wiki/Thermal_expansion',
      debyeTemperature: 'https://en.wikipedia.org/wiki/Debye_model',
      formationEnthalpy: 'https://en.wikipedia.org/wiki/Standard_enthalpy_of_formation',
      youngsModulus: 'https://en.wikipedia.org/wiki/Young%27s_modulus',
      bulkModulus: 'https://en.wikipedia.org/wiki/Bulk_modulus',
      shearModulus: 'https://en.wikipedia.org/wiki/Shear_modulus',
      poissonRatio: 'https://en.wikipedia.org/wiki/Poisson%27s_ratio',
      mohsHardness: 'https://en.wikipedia.org/wiki/Mohs_scale_of_mineral_hardness',
      vickersHardness: 'https://en.wikipedia.org/wiki/Vickers_hardness_test',
      bandGap: 'https://en.wikipedia.org/wiki/Band_gap',
      electricalResistivity: 'https://en.wikipedia.org/wiki/Electrical_resistivity_and_conductivity',
      workFunction: 'https://en.wikipedia.org/wiki/Work_function',
      dielectricConstant: 'https://en.wikipedia.org/wiki/Relative_permittivity',
      refractiveIndex: 'https://en.wikipedia.org/wiki/Refractive_index',
      magneticSusceptibility: 'https://en.wikipedia.org/wiki/Magnetic_susceptibility',
      curieTemperature: 'https://en.wikipedia.org/wiki/Curie_temperature',
      molarMass: 'https://en.wikipedia.org/wiki/Molar_mass',
      atomsPerCell: 'https://en.wikipedia.org/wiki/Crystal_structure',
      cellVolume: 'https://en.wikipedia.org/wiki/Crystal_structure',
      packingFraction: 'https://en.wikipedia.org/wiki/Atomic_packing_factor',
    },

    exportModel: {
      Title: 'Export 3D-Printable Model',
      AsDisplayedNote:
        'Exports the molecule exactly as shown — set its style and coloring in the lab/gallery first. Pick a color format (PLY or glTF/GLB) to keep colors.',
      Representation: 'Representation',
      FileFormat: 'File format',
      Scale: 'Scale (mm per Å)',
      FitToBed: 'Fit to printer bed',
      BedSize: 'Bed size',
      FitToBedNote:
        'The model is auto-scaled so its longest side fills ~90% of the bed size above — the print always fits (no out-of-bounds), whatever the molecule. Uncheck to set an exact mm-per-Å scale instead.',
      SurfaceResolution: 'Surface resolution',
      Smoothing: 'Smoothing (Å)',
      BuildingMesh: 'Building mesh…',
      EncodingFile: 'Encoding file…',
      PreparingPrint: 'Preparing 3D-printable model…',
      ScaleNote:
        'The scale is millimeters per Ångström — the print size a slicer reads. Small molecules print well around 8; a whole protein spans ~100 Å, so keep the scale near 1–2 to fit a printer bed (~256 mm).',
      Oversize:
        'This print is {{x}}×{{y}}×{{z}} mm — larger than a typical printer bed (~{{bed}} mm), so a slicer will flag it as out of bounds. Lower the Scale (try ≈{{suggest}}) to fit.',
      SurfaceNote:
        'Higher resolution = smoother but heavier. Smoothing inflates each atom: raise it (try 2–3) to close channels/holes through the molecule into a solid print; lower it toward 0 for a bumpier, more detailed (but possibly holey) surface.',
      ColorLossNote:
        'This format stores no color — the displayed colors (e.g. an electrostatic-potential surface) will be lost. Choose 3MF to keep them all the way into a slicer (PLY/GLB keep color only for web viewers — slicers ignore it).',
      RepSpaceFilling: 'Space-filling (CPK)',
      RepSpaceFillingHint:
        'Overlapping van der Waals spheres fuse into one solid — the most reliably printable. For a protein this is the vdW surface.',
      RepBallStick: 'Ball-and-stick',
      RepBallStickHint:
        'Atom spheres joined by thick bond cylinders. Looks like the lab view; bonds are fattened so nothing prints as a loose piece.',
      RepSurface: 'Molecular surface (smooth)',
      RepSurfaceHint:
        'A smooth marching-cubes blob over the atom centers — best for proteins. Higher resolution is smoother but a bigger file.',
      RepAsDisplayed: 'As displayed — exact + colors',
      RepAsDisplayedHint:
        'Exports the exact surface currently rendered in the lab, including its colors (e.g. an electrostatic-potential surface). Use 3MF to keep them in a slicer.',
      FormatStl: 'STL (universal, no color)',
      Format3mf: '3MF (color, for slicers)',
      FormatPly: 'PLY (color, for viewers)',
      FormatObj: 'OBJ (wide support)',
      FormatGlb: 'glTF / GLB (color, for viewers)',
      NoMolecule: 'There is no molecule to export.',
      NoAtom: 'There is no atom to export.',
      EmptySurface: 'The surface came out empty — try a higher resolution.',
      Failed: 'Export failed.',
      NothingRendered: 'Nothing is rendered in the lab to export.',
      DotSurfaceNotPrintable:
        'A dot surface is just points — it cannot be 3D-printed. Switch to a solid style (e.g. Space-filling, Ball-and-stick, or a Molecular Surface) first.',
      OpenInLabFirst: 'Open the molecule in the lab view first, then export “as displayed”.',
      Exported: '{{ext}} exported ({{n}} triangles{{note}}).',
      WithColors: ', with colors',
      ColorsDropped: ' — colors dropped (use 3MF to keep color in a slicer)',
    },

    word: {
      Prompt: 'Prompt',
      Low: 'Low',
      Medium: 'Medium',
      High: 'High',
      CopyLink: 'Copy Link',
      ShareVia: 'Share via Other Apps',
      Share: 'Share',
      ElectronConfiguration: 'Electron Configuration',
      Category: 'Category',
      Probability: 'Probability',
      Press: 'Press',
      Mode: 'Mode',
      MoreInformation: 'More information',
      Mute: 'Mute',
      Formula: 'Formula',
      AtomsLowerCasePlural: 'atoms',
      Save: 'Save',
      Skip: 'Skip',
      Result: 'Result',
      Results: 'Results',
      Import: 'Import',
      Generate: 'Generate',
      Send: 'Send',
      Loading: 'Loading',
      Total: 'Total',
      Heat: 'Heat',
      Cool: 'Cool',
      Relative: 'Relative',
      Dimensionless: 'Dimensionless',
      KeyboardKey: 'Key',
      None: 'None',
      All: 'All',
      Size: 'Size',
      Small: 'Small',
      Large: 'Large',
      Name: 'Name',
      Codename: 'Codename',
      Time: 'Time',
      Energy: 'Energy',
      Speed: 'Speed',
      KineticEnergy: 'Kinetic Energy',
      PotentialEnergy: 'Potential Energy',
      TotalEnergy: 'Total Energy',
      Type: 'Type',
      Open: 'Open',
      Rename: 'Rename',
      Delete: 'Delete',
      Cut: 'Cut',
      Copy: 'Copy',
      CopySelection: 'Copy selection',
      CopyAll: 'Copy all',
      Paste: 'Paste',
      Export: 'Export',
      Warning: 'Warning',
      Caution: 'Caution',
      Title: 'Title',
      Description: 'Description',
      MaximumCharacters: 'Maximum characters',
      Unknown: 'Unknown',
      Maximum: 'Maximum',
      Max: 'Max',
      Minimum: 'Minimum',
      Yes: 'Yes',
      No: 'No',
      OK: 'OK',
      Cancel: 'Cancel',
      Run: 'Run',
      Close: 'Close',
      Stop: 'Stop',
      Clear: 'Clear',
      Retry: 'Retry',
      Maximize: 'Maximize',
      Restore: 'Restore',
      Action: 'Action',
      Version: 'Version',
      VersionInitial: 'V',
      Built: 'Built',
      Anonymous: 'Anonymous',
      Commons: 'Commons',
    },

    term: {
      DrugDiscovery: 'Drug Discovery',
      QSARModeling: 'QSAR/QSPR Modeling',
      MolecularModeling: 'Molecular Modeling',
      CrystalBuilder: 'Crystal Builder',
      SemiconductorDesign: 'Semiconductor Design',
      NanomaterialDesign: 'Nanomaterial Design',
      PolymerBuilder: 'Polymer Builder',
      ProteinLab: 'Protein Lab',
      DnaRnaLab: 'DNA/RNA Lab',
      CommonMolecules: 'Common Molecules',
      HydrocarbonMolecules: 'Hydrocarbon Molecules',
      Biomolecules: 'Biomolecules',
      DrugMolecules: 'Drug Molecules',
      Monatomic: 'Monatomic',
      Crystal: 'Crystal',
      Any: 'Any',
    },

    polymer: {
      BuildChain: 'Build Chain',
      BuildChainTooltip: 'Build a polymer chain from the current monomer',
      ChainLength: 'Chain length (N)',
      BeadChain: 'Bead chain',
      BuildChainNoMonomerTooltip: 'Build a chain of beads, or pick a monomer first for an all-atom chain',
      BeadModeNote:
        'A chain of coarse-grained beads instead of atoms. Every bead type is in real units, fitted to a reference: B is one styrene unit, ROD a 5CB-like liquid-crystal rod, H and P amino-acid residues, HD and TL lipid beads, and CH4, CH3, CH2 and CH united-atom groups. Chains are placed as random walks, and the thermostat is set to the model’s reference temperature.',
      BeadSequence: 'Bead sequence',
      BeadSequenceTip:
        'Bead type names separated by spaces, commas or hyphens, each with an optional repeat: "B" (repeated to the chain length), "CH3 CH2*8 CH3" (united-atom decane), "ROD" with chain length 1 for free rods. Single-letter codes run together: "HPHHPP" is a hydrophobic–polar protein chain. "HD TL TL" is a lipid (head + two tails) — place many to watch micelles form; "HS TL TL TL" has a small head, for inverse micelles in oil (Experiment Settings › Force Field › Implicit Solvent). Known types: B, ROD, H, P, HD, HS, TL, CH4, CH3, CH2, CH, CQ.',
      RodsAligned: 'Start aligned (a nematic lattice)',
      BeadBondLength: 'Bond length ({{unit}})',
      ChainLengthFromSequence: 'set by the sequence',
      UnknownBeadType: 'Unknown bead type: {{name}}',
      TorsionJitter: 'Torsion jitter (°)',
      NumberOfChains: 'Number of chains',
      PredictProperties: 'Predict polymer properties (Tg, Tm, density, …)',
      PredictPropertiesSuccess: 'Polymer properties predicted',
      PredictPropertiesFailure: 'Polymer property prediction failed',
      PropertiesHint: 'Click Predict in the gallery to estimate Tg, Tm, density, solubility, Mw.',
      Density: 'ρ',
      PolymerMw: 'Mw (polymer)',
      MonomerMw: 'Mw (monomer)',
      TheoreticalLp: 'lp (theory)',
      AIPredictedHint: 'AI-predicted value — verify against literature for known polymers.',
      AtomCountPreview: 'Atoms: {{n}}',
      AtomCountOverCap: '(exceeds cap of {{cap}})',
      NeedMonomerFirst: 'Generate a monomer first',
      Monomer: 'Monomer',
      Head: 'Head',
      Tail: 'Tail',
      Unnamed: '(unnamed)',
      GenerateMonomer: 'Generate Monomer',
      MonomerPrompt: 'Describe the monomer',
      HeadAtomIndex: 'Head atom index',
      TailAtomIndex: 'Tail atom index',
      AutoPickEndpoints: 'Auto-pick',
      AutoPickEndpointsTooltip:
        'Picks the head and tail atoms for chain growth by finding the two backbone-class atoms (C, N, O, S, Si, P) farthest apart in 3D — these are typically the polymerization sites. Halogens and other substituents are excluded as backbone candidates. The leaving atom at each end (deleted to form the inter-unit bond) is then chosen as the closest neighbor: hydrogen if available, otherwise any closest non-backbone atom (e.g. F for PTFE). You can override the head and tail indices manually below.',
      MonomerSavedTo: 'Saved monomer:',
      SavedMonomerNote: 'Monomer parsed. Pick head/tail atoms in the Build Chain dialog.',
      // Polymer-Studio analysis-panel strings (Rg / Ree / lp single-chain
      // observables). Wired into src/view/polymerAnalysisPanel.tsx.
      RadiusOfGyration: 'Radius of gyration Rg (Å)',
      EndToEndDistance: 'End-to-end distance Ree (Å)',
      PersistenceLength: 'Persistence length lp (Å)',
      BondTangentAutocorrelation: 'Bond-tangent autocorrelation ⟨t·t⟩(s)',
      ContourSeparation: 'Contour separation s',
      NoChainYet: 'Build a polymer chain to see analyses',
      PersistenceLengthTooltip:
        'Decay length of the backbone tangent autocorrelation. Fit from ⟨t·t⟩(s) ≈ exp(-s·l_bond/lp) over short s.',
      // Copolymer Build Chain modal strings.
      MonomerList: 'Monomers',
      RemoveMonomer: 'Remove this monomer from the copolymer',
      Sequence: 'Sequence',
      Alternating: 'Alternating',
      Random: 'Random',
      Block: 'Block',
      Topology: 'Topology',
      Linear: 'Linear',
      Ring: 'Ring',
      Star: 'Star',
      StarArms: 'Arms',
      Comb: 'Comb',
      CombSpacing: 'Side chain spacing',
      CombSideLength: 'Side chain length',
      Branched: 'Branched',
      BranchDensity: 'Branch density',
      BranchLength: 'Branch length',
      Dendritic: 'Dendritic',
      DendriticInitialArms: 'Initial arms',
      DendriticGenerations: 'Generations',
      DendriticBranchFactor: 'Branch factor',
      DendriticArmLength: 'Arm length',
      Hyperbranched: 'Hyperbranched',
      HyperbranchedProbability: 'Branch probability',
      AtomCountOverHardCap: '(exceeds hard cap of {{cap}} — Build disabled)',
      OverCapHint: 'Chain exceeds the recommended atom cap. The lab will run slowly but the chain will still build.',
      OverHardCapHint:
        'Chain exceeds the hard atom cap and cannot be built. Reduce chain length, arm count, or pick a smaller monomer.',
      OverCapHintStar:
        'Star topology multiplies total atoms by the number of arms — try fewer arms or a shorter chain.',
      OverCapHintCombBranched: 'Side chains add atoms per branch — try fewer / shorter branches.',
      OverCapHintRing: 'Rings need at least one full chain length of atoms — try a shorter chain.',
      OverCapHintDendritic:
        'Dendritic atom count grows exponentially with generations — try fewer generations, a smaller branch factor, or shorter arms.',
    },

    crystalBuilder: {
      UndoTargetReplaced: 'That step was made on a lab that has since been replaced, so nothing was changed.',
      Tooltip: 'Crystal builder',

      // Parallel-coordinates property labels (materials-science bundle).
      // Title Case so labels read naturally on radar / scatter axis titles.
      prop_density: 'Density',
      prop_meltingPoint: 'Melting Point',
      prop_boilingPoint: 'Boiling Point',
      prop_sublimationPoint: 'Sublimation Point',
      prop_specificHeat: 'Specific Heat',
      prop_thermalConductivity: 'Thermal Conductivity',
      prop_thermalExpansion: 'Thermal Expansion',
      prop_debyeTemperature: 'Debye Temperature',
      prop_formationEnthalpy: 'Formation Enthalpy',
      prop_youngsModulus: "Young's Modulus",
      prop_bulkModulus: 'Bulk Modulus',
      prop_shearModulus: 'Shear Modulus',
      prop_poissonRatio: 'Poisson Ratio',
      prop_mohsHardness: 'Mohs Hardness',
      prop_vickersHardness: 'Vickers Hardness',
      prop_bandGap: 'Band Gap',
      prop_electricalResistivity: 'Electrical Resistivity',
      prop_workFunction: 'Work Function',
      prop_dielectricConstant: 'Dielectric Constant',
      prop_refractiveIndex: 'Refractive Index',
      prop_magneticSusceptibility: 'Magnetic Susceptibility',
      prop_curieTemperature: 'Curie Temperature',
      prop_molarMass: 'Molar Mass',
      prop_atomsPerCell: 'Atoms per Cell',
      prop_cellVolume: 'Cell Volume',
      prop_packingFraction: 'Packing Fraction',
      PredictedTag: 'Predicted',

      // Gallery view modes — list / compare / chat for crystal projects.
      ChatInputPlaceholder: 'Ask a question or describe a crystal…',
      NanoChatInputPlaceholder: 'Ask a question or describe a nanostructure…',
      NoNanostructures: 'No nanostructures yet',
      // Nanomaterial Design: the same gallery, worded for nanostructures.
      NanoAiModalTitle: 'Generate host crystal with AI',
      NanoImportTooltip: 'Select host crystals to import from the internal database',
      RemoveSelectedNanostructureFromGallery: 'Remove the selected nanostructure from the gallery',
      RemoveAllNanostructuresFromGallery: 'Remove all nanostructures from the gallery',
      DoYouWantToRemoveAllNanostructuresFromGallery: 'Do you want to remove all nanostructures from the gallery?',
      ToggleDragAndDropNanostructureMode: 'Toggle drag-and-drop mode for placing nanostructures in the lab',
      ClickToIncludeNanostructure: 'Click to include this nanostructure',
      ClickToExcludeNanostructure: 'Click to exclude this nanostructure',
      NanostructureName: 'Nanostructure Name',
      NanostructureFormula: 'Nanostructure Formula',
      // Finite-structure card rows (a nanostructure's box is vacuum padding,
      // so it shows its atom count and extent instead of cell data).
      Atoms: 'Atoms',
      Extent: 'Extent',
      NanostructureKind: 'Kind',
      HostCrystal: 'Host crystal',
      EditorFiniteNote:
        'A finite nanostructure: the lattice is its vacuum-padded box and the space group a placeholder, so both are locked. Atoms can still be edited.',
      SameColorForAllNanostructures: 'Same color for all nanostructures',
      OneColorForEachNanostructure: 'One color for each nanostructure',
      ChatEmptyHint: 'No crystals yet — your generated ones will appear here.',
      CompareEmptyHint: 'Select crystals above to compare.',

      // Section headers for the crystal properties popover
      SectionThermodynamic: 'Thermodynamic',
      SectionMechanical: 'Mechanical',
      SectionElectronic: 'Electronic',
      SectionOptical: 'Optical',
      SectionMagnetic: 'Magnetic',
      SectionCrystallographic: 'Crystallographic',

      // Graph-area labels (parallel-coordinates data-coloring popover)
      SameColorForAllCrystals: 'Same color for all crystals',
      OneColorForEachCrystal: 'One color for each crystal',

      // Gallery (crystalGalleryGrid)
      NoCrystals: 'No crystals yet',
      LoadPresets: 'Load presets',
      LoadButtonLabel: 'Load →',
      DefaultName: 'Crystal',
      ImportTooltip: 'Select crystals to import from the internal database',
      ImportCrystals: 'Import crystals',

      // Import-crystal modal
      SelectCrystalFromInternalDatabase: 'Select crystal from internal database',
      CrystalName: 'Crystal Name',
      CrystalFormula: 'Crystal Formula',
      CrystalType: 'Crystal Type',

      // Card popover + right-click context menu
      SpaceGroup: 'Space group',
      Formula: 'Formula',
      Volume: 'Volume',
      GeneratedByAgent: 'Generated by AI agent',
      Rename: 'Rename',
      Duplicate: 'Duplicate',
      ExportCif: 'Export as CIF',
      MinimizeEnergy: 'Minimize Energy',
      MinimizingEnergy: 'Minimizing the energy of {{name}}…',
      MinimizeEnergyDone:
        'Relaxed {{name}} in {{steps}} steps: {{before}} → {{after}} eV per atom; atoms moved up to {{shift}} Å.',
      MinimizeEnergyFailed: 'Could not minimize {{name}}: {{error}}',
      Delete: 'Delete',
      DeleteSelected: 'Delete selected',
      CardActions: 'Actions',
      ClickToIncludeCrystal: 'Click to include this crystal',
      ClickToExcludeCrystal: 'Click to exclude this crystal',
      TypeToSearch: 'Type a name or chemical formula in the above field to search for a crystal',
      PeriodicImageGhost: 'Periodic image (ghost) — acts on the source atom',
      GhostAtomCountNote:
        '{{count}} of the visible spheres are ghost atoms (periodic images / boundary completion) drawn for clarity — they are not part of the simulated model.',
      BoundaryConditions: 'Boundary conditions',
      BoundaryPeriodic: 'Periodic',
      BoundarySlab: 'Slab',
      BoundaryFinite: 'Finite',
      BoundaryPeriodicNote:
        'Periodic boundary conditions in all three directions: the dashed box repeats forever, so an atom near one face interacts with the atoms just inside the opposite face. The model is an infinite bulk crystal with no surface — this is why a perfect lattice can superheat well above its melting point.',
      BoundarySlabNote:
        'Periodic along a and b, with a {{gap}} Å vacuum gap along c: the dashed box is taller than the crystal, so its top and bottom faces are real free surfaces. Melting can nucleate there.',
      BoundaryFiniteNote:
        'No periodic boundaries: this crystal has no registered force field for a periodic cell, so it is simulated as a finite cluster with real surfaces on every side.',
      BoundaryFiniteNanoNote:
        'A finite nanostructure: one object in a box padded with more vacuum than the force field reaches, so nothing wraps around and every surface is a real one.',
      PeriodicCell: 'Periodic cell',
      PeriodicCellNote:
        'Under periodic boundaries the simulation cell is the container: its size follows the lattice and the supercell (nx × ny × nz below), so there is no separate box to size.',
      ForceFieldAlternativesNote:
        '{{count}} other force field(s) can model this crystal — switch in the settings (⚙) popover, or ask the assistant which potentials it has.',

      // Translated display names for built-in presets. Keys are the English
      // names in presets.ts; the import dropdown and card labels look up the
      // localized form via getCrystalDisplayName(). Unmapped names (custom,
      // CIF imports, AI generations) fall through to the raw name.
      preset_SodiumChloride: 'Sodium Chloride',
      preset_MagnesiumOxide: 'Magnesium Oxide',
      preset_CesiumChloride: 'Cesium Chloride',
      preset_CalciumFluoride: 'Calcium Fluoride',
      preset_Diamond: 'Diamond',
      preset_Silicon: 'Silicon',
      preset_GalliumArsenide: 'Gallium Arsenide',
      preset_CubicBoronNitride: 'Cubic Boron Nitride',
      preset_Pyrite: 'Pyrite',
      preset_Copper: 'Copper',
      preset_Iron: 'Iron',
      preset_Magnesium: 'Magnesium',
      preset_Graphite: 'Graphite',
      preset_HexagonalBoronNitride: 'Hexagonal Boron Nitride',
      preset_ZincOxide: 'Zinc Oxide',
      preset_Rutile: 'Rutile',
      preset_Anatase: 'Anatase',
      preset_StrontiumTitanate: 'Strontium Titanate',
      preset_Aluminum: 'Aluminum',
      preset_Gold: 'Gold',
      preset_Tungsten: 'Tungsten',
      preset_Lithium: 'Lithium',
      preset_Zinc: 'Zinc',
      preset_Germanium: 'Germanium',
      preset_LeadSulfide: 'Lead Sulfide',
      preset_ZincSulfide: 'Zinc Sulfide',
      preset_GalliumNitride: 'Gallium Nitride',
      preset_LithiumFluoride: 'Lithium Fluoride',
      preset_CesiumIodide: 'Cesium Iodide',
      preset_Nickel: 'Nickel',
      preset_Silver: 'Silver',
      preset_Platinum: 'Platinum',
      preset_Sodium: 'Sodium',
      preset_Vanadium: 'Vanadium',
      preset_Titanium: 'Titanium',
      preset_Cobalt: 'Cobalt',
      preset_Beryllium: 'Beryllium',
      preset_PotassiumChloride: 'Potassium Chloride',
      preset_SiliconCarbide: 'Silicon Carbide',
      preset_AluminumNitride: 'Aluminum Nitride',
      preset_DryIce: 'Dry Ice',
      preset_Nitrogen: 'Nitrogen',
      preset_Argon: 'Argon',
      preset_Ice: 'Ice',
      preset_Methane: 'Methane',
      preset_Hydrogen: 'Hydrogen',
      preset_CarbonMonoxide: 'Carbon Monoxide',
      preset_Krypton: 'Krypton',
      preset_Xenon: 'Xenon',
      preset_Neon: 'Neon',
      preset_Ammonia: 'Ammonia',
      preset_SulfurHexafluoride: 'Sulfur Hexafluoride',
      preset_Acetylene: 'Acetylene',
      preset_CarbonTetrafluoride: 'Carbon Tetrafluoride',
      preset_CarbonTetrachloride: 'Carbon Tetrachloride',
      preset_NitrousOxide: 'Nitrous Oxide',
      preset_HydrogenCyanide: 'Hydrogen Cyanide',
      preset_WhitePhosphorus: 'White Phosphorus',
      preset_Cyanogen: 'Cyanogen',
      preset_Silane: 'Silane',
      preset_Phosphine: 'Phosphine',
      preset_CarbonTetrabromide: 'Carbon Tetrabromide',
      preset_Germane: 'Germane',
      preset_CarbonDisulfide: 'Carbon Disulfide',
      preset_Arsine: 'Arsine',
      preset_GalliumPhosphide: 'Gallium Phosphide',
      preset_IndiumPhosphide: 'Indium Phosphide',
      preset_IndiumArsenide: 'Indium Arsenide',
      preset_CadmiumTelluride: 'Cadmium Telluride',
      preset_Lonsdaleite: 'Lonsdaleite',
      preset_AluminumPhosphide: 'Aluminum Phosphide',
      preset_IndiumAntimonide: 'Indium Antimonide',
      preset_ZincSelenide: 'Zinc Selenide',
      preset_GrayTin: 'Gray Tin',
      preset_Cristobalite: 'Cristobalite',
      preset_AluminumArsenide: 'Aluminum Arsenide',
      preset_GalliumAntimonide: 'Gallium Antimonide',
      preset_BoronPhosphide: 'Boron Phosphide',
      preset_BoronArsenide: 'Boron Arsenide',
      preset_WurtziteZincSulfide: 'Wurtzite Zinc Sulfide',
      preset_SodiumFluoride: 'Sodium Fluoride',
      preset_PotassiumBromide: 'Potassium Bromide',
      preset_CalciumOxide: 'Calcium Oxide',
      preset_LithiumOxide: 'Lithium Oxide',
      preset_StrontiumFluoride: 'Strontium Fluoride',
      preset_BariumTitanate: 'Barium Titanate',
      preset_SodiumIodide: 'Sodium Iodide',
      preset_StrontiumOxide: 'Strontium Oxide',
      preset_BariumFluoride: 'Barium Fluoride',
      preset_MagnesiumFluoride: 'Magnesium Fluoride',
      preset_TinDioxide: 'Tin Dioxide',
      preset_CalciumTitanate: 'Calcium Titanate',
      preset_Cu3Au: 'Copper-Gold (Cu3Au)',
      preset_Au3Cu: 'Gold-Copper (Au3Cu)',
      preset_CuAu: 'Copper-Gold (CuAu)',
      preset_BetaBrass: 'Beta-Brass',
      preset_NickelAluminide: 'Nickel Aluminide',
      preset_Ni3Al: 'Nickel Aluminide (Ni3Al)',
      preset_Molybdenum: 'Molybdenum',
      preset_Palladium: 'Palladium',
      preset_Zirconium: 'Zirconium',
      preset_CadmiumSulfide: 'Cadmium Sulfide',
      preset_NickelOxide: 'Nickel Oxide',
      preset_LeadTitanate: 'Lead Titanate',
      preset_Chromium: 'Chromium',
      preset_Tantalum: 'Tantalum',
      preset_AluminumAntimonide: 'Aluminum Antimonide',
      preset_ZincTelluride: 'Zinc Telluride',
      preset_BerylliumOxide: 'Beryllium Oxide',
      preset_ManganeseOxide: 'Manganese Oxide',

      // Bonding categories — used as OptGroup labels in the import dropdown.
      categoryIonic: 'Ionic crystals',
      categoryCovalent: 'Covalent crystals',
      categoryMetallic: 'Metallic crystals',
      categoryMolecular: 'Molecular crystals',

      // Tooltips / messages that the molecule-themed gallery toolbar shares
      // with crystal projects. Mirrors message.* and projectPanel.* keys
      // used by GalleryDescriptionPanel — same role, crystal wording.
      GeneratingCrystal: 'Generating a crystal — this may take a while',
      RemoveSelectedCrystalFromGallery: 'Remove the selected crystal from the gallery',
      RemoveAllCrystalsFromGallery: 'Remove all crystals from the gallery',
      DoYouWantToRemoveAllCrystalsFromGallery: 'Do you want to remove all crystals from the gallery?',
      ToggleDragAndDropCrystalMode: 'Toggle drag-and-drop mode for placing crystals in the lab',

      // Builder panel (crystalBuilder)
      AddVia: 'Add via:',
      LoadPresetPlaceholder: 'Load preset...',
      AiButton: 'AI...',
      EmptySelectHint: 'Select a crystal in the gallery to edit it.',
      LatticeParameters: 'Lattice parameters',
      BasisAtoms: 'Basis atoms',
      AddAtom: 'Add atom',
      // Crystal Editor add-atom box: the typed text is not a chemical symbol.
      UnknownElement: '"{{symbol}}" is not a chemical element symbol.',
      NothingToImport: 'Those crystals are already in the gallery — nothing was imported.',
      ChamberEmpty: 'Lab empty',
      Chamber: 'Lab',
      LoadToChamber: 'Load to lab',
      Edit: 'Edit',
      EditCrystalTitle: 'Crystal Editor',
      SpaceGroupLabel: 'Space group',
      ExpandSymmetry: 'Fill cell',
      ExpandSymmetryHint:
        'Treat the basis as the asymmetric unit and apply the space group to fill the conventional cell (result is stored as P1). Supported: Pm-3m, Fm-3m, Im-3m, F-43m, Fd-3m, P4/mmm, I4/mmm, P6/mmm, P6_3/mmc.',
      ExpandLatticeMismatch:
        'The lattice parameters are inconsistent with this space group (cubic needs a = b = c, α = β = γ = 90°; tetragonal a = b, all angles 90°; hexagonal a = b, α = β = 90°, γ = 120°). Fix the lattice before filling the cell.',
      ExpandTooManyAtoms:
        'Filling the cell would create {{count}} atoms, exceeding the maximum allowed. Use a smaller asymmetric unit or a higher-symmetry setting.',
      GalleryEmptyHint: 'Gallery is empty — pick a preset above to seed it.',

      // Supercell-size modal
      ModalTitle: 'Load {{name}} to lab',
      ModalTitleGeneric: 'Load crystal to lab',
      SelectFirst: 'Select a crystal in the gallery first.',
      Supercell: 'Supercell:',
      Basis: 'Basis',
      SupercellTotal: 'Supercell total',
      AtomsLowerCase: 'atoms',
      AtomLowerCase: 'atom',
      OverLimitWarning:
        'Supercell exceeds the {{limit}}-atom limit and cannot be loaded. Reduce nx, ny or nz until the total is under the limit.',
      OverSoftLimitWarning: 'Over {{limit}} atoms: the lab will load and run it, but the simulation may be slow.',
      // Toast from the lab toolbar's nx/ny/nz fields when a typed value would
      // push the supercell over the HARD cap; the field reverts to its old value.
      SupercellOverLimit:
        'A {{nx}} × {{ny}} × {{nz}} supercell would be {{count}} atoms, over the {{limit}}-atom limit.',
      // Toast from the gallery "Load to lab" action when the previous tiling
      // had to be shrunk to fit this crystal under the cap.
      SupercellShrunkToFit:
        'Loaded as {{nx}} × {{ny}} × {{nz}} ({{count}} atoms) to stay under {{limit}} atoms. Enlarge it in the lab toolbar if you want a bigger run.',
      // Used by the lab toolbar's Experiment Settings popover when
      // showing the supercell extent inputs (nx/ny/nz).
      SupercellHint: 'Atoms per cell × nx × ny × nz',
      SupercellFiniteNote: 'Finite nanostructure, not tiled (1 × 1 × 1)',

      // Lab empty-state hints (crystalViewer)
      EmptyWithSelection:
        '{{name}} is not in the lab yet. Drag its card here (turn on drag mode in the gallery toolbar), or:',
      EmptyNoSelection: 'Select a crystal in the gallery and drag it here, or ask the chat to load one.',
      EmptyNoSelectionNano: 'Build a nanostructure in the chat, or drag one here from the gallery.',

      // AI generation modal
      AiModalTitle: 'Generate crystal with AI',
      WhatCrystalDoYouWant: 'What crystal do you want?',
      AgentModeTooltip:
        'When enabled, the AI plans multi-step crystal tasks using tools (lookup, substitution, symmetry validation, charge balance). Currently routes to direct generation — crystal agent tools land later.',
      ProviderNotSupported:
        'This AI provider is not yet wired for crystal generation. Please switch to OpenAI in the dropdown.',
      GeneratingMayTakeAWhile: 'Generating a crystal may take a while.',
      ContentFiltered: 'The provider blocked this prompt as policy-violating. Try rephrasing.',
      PromptPlaceholder:
        'Describe the crystal you want: a known structure by name (e.g. "NaCl rock salt"), a substitution from a starting compound (e.g. "Start from SrTiO3 and substitute Sr with Ba"), or a freeform description (e.g. "2D hexagonal lattice of carbon").',
      // Task keys retained for future Agent Mode wiring (agent tools, not
      // user-selectable). Not referenced by the current modal.
      Task: 'Task:',
      TaskTemplate: 'Template lookup',
      TaskTemplateHint: 'Look up a known crystal by name. Best for well-characterized structures.',
      TaskSubstitution: 'Substitution / doping',
      TaskSubstitutionHint:
        'Modify a starting structure by substituting atoms. Describe the host and the substitution.',
      TaskSymmetry: 'Symmetry validator',
      TaskSymmetryHint: 'Validate that a basis is consistent with its claimed space group; return a corrected CIF.',
      TaskChargeBalance: 'Charge balance check',
      TaskChargeBalanceHint: 'Verify that an ionic crystal balances; propose corrections if not.',
      TaskFreeform: 'Freeform',
      TaskFreeformHint: 'Generate any crystal matching your description.',
      PlaceholderTemplate: 'NaCl rock salt structure with room-temperature lattice parameters',
      PlaceholderSubstitution: 'Start from SrTiO3 perovskite and substitute one Sr with Ba',
      PlaceholderSymmetry: 'Check whether my P 4/mmm tetragonal structure is missing any required basis atoms',
      PlaceholderChargeBalance: 'Check the charge balance of Ca2+ and PO4 3- in apatite, and suggest a P1 basis',
      PlaceholderFreeform: 'A 2D hexagonal lattice of carbon with 1.42 Å bonds, single layer',
      Generating: 'Generating...',
      CallingAI: 'Calling AI...',
      AddToGallery: 'Add to gallery',
      AiCrystalDefaultName: 'AI crystal',
      EmptyResponse:
        'The model replied but included no crystal structure — common with faster models. Try again, or switch to a stronger model (e.g. Gemini 3.8-flash).',
      NonJsonOutput: 'Model returned non-JSON output.',
      CifParseFailed: 'Model returned CIF but parsing failed',
      SignInRequired: 'Sign-in required.',
      OutputTruncated: 'Output was truncated. Try a shorter or simpler request.',
      GenerationFailed: 'Generation failed.',
      PreviewLabel: 'Preview (unit cell)',
      CifLabel: 'CIF',
      NotesLabel: 'Notes',
    },

    // Semiconductor Design project type. Sibling of crystalBuilder; reuses
    // most crystal-pipeline labels but adds keys for actions specific to
    // the semiconductor flow (AI property prediction, dopants, etc.).
    semiconductor: {
      PredictProperties: 'Predict properties with AI',
      PredictingProperties: 'Predicting properties…',
      PredictPropertiesSuccess: 'Predicted properties saved',
      PredictPropertiesFailure: 'Could not predict properties',
      Doping: 'Doping',
      NoDopants: 'No dopants added yet.',
      NoDopableHost: "This crystal's elements aren't in the dopant catalog.",
      NoSelectedCrystal: 'Select a crystal first.',
      HostElement: 'Host element',
      DopantElement: 'Dopant element',
      Concentration: 'Concentration (at.%)',
      DopantType: 'Type',
      Defects: 'Defects',
      Vacancy: 'vacancy',
      Interstitial: 'interstitial',
      DefectKind: 'Defect kind',
      DefectElement: 'Element',
      NoDefects: 'No defects added yet.',
      NoDefectableHost: 'No host elements available for defects in this crystal.',
      BuildSlab: 'Build slab',
      SlabHost: 'Host crystal',
      MillerIndices: 'Miller indices',
      SlabThickness: 'Thickness',
      SlabLayers: 'unit cells',
      SlabVacuum: 'Vacuum gap',
      SlabPassivate: 'Passivate',
      SlabPassivateHint: 'Cap dangling bonds with H (diamond/zincblende only)',
      SlabBuildFailed: 'Could not build slab',
      DeviceBuilder: 'Build device (heterostructure / superlattice)',
      DeviceName: 'Device name',
      DeviceLayers: 'Layers',
      DeviceAddLayer: 'Add layer',
      DeviceLateralCells: 'Lateral cells',
      DeviceRepeats: 'Repeats',
      DeviceMismatch: 'In-plane mismatch vs layer 1',
      DeviceUnknownHost: 'Unknown host — pick a crystal',
      DeviceEstimatedAtoms: 'Estimated atoms',
      DeviceSimulable: 'Simulation in the lab: {{forceField}}',
      DeviceViewOnly: 'View-only in the lab: no force field covers {{elements}} together.',
      DeviceNameRequired: 'Device needs a name',
      DeviceNoLayers: 'Device needs at least one layer',
      DeviceNoCubicCrystals: 'No cubic crystals available. Add a cubic crystal first.',
      DeviceBuildFailed: 'Could not build device',
      BandStructure: 'Band structure',
      BandGapComputed: 'Computed gap',
      BandGapExperimental: 'Experiment',
      AlloyVca: 'virtual-crystal approximation',
      Strain: 'Strain',
      StrainSection: 'Strain (grown on)',
      StrainSubstrate: 'Substrate',
      StrainNone: 'Relaxed (no substrate)',
      StrainRemove: 'Remove strain',
      StrainMismatch: 'Mismatch',
      StrainCritical: 'Critical thickness',
      StrainValleys: 'Strained valleys',
      StrainValence: 'Valence splitting',
      StrainHeavyHole: 'heavy-hole-like on top',
      StrainLightHole: 'light-hole-like on top',
      StrainDegenerate: 'degenerate',
      StrainCoherent: 'Coherent below this thickness; thicker layers relax by forming misfit dislocations.',
      StrainDeltaNote:
        'Computed from the strained bonds, not from fitted deformation potentials. The valence splitting and the hydrostatic gap shift are quantitative; the split between the growth-axis and in-plane Δ valleys is underestimated by this basis, so read it as a direction rather than a number.',
      Alloy: 'Alloy',
      AlloyComposition: 'Composition',
      AlloyPartner: 'Alloy with',
      AlloyNone: 'No alloy (pure host)',
      AlloyRemove: 'Remove alloy',
      AlloyUnavailable: 'This crystal has no alloy partners in the tight-binding registry.',
      AlloyBowingNote:
        'The computed bands use the virtual-crystal approximation, which averages away the local disorder that makes real alloy gaps bow below the straight line between the endpoints. Expect the computed gap to sit above the measured one, most of all near the middle of the composition range.',
      AlloyNoBowingData: 'No measured bowing parameter for this pair — no experimental reference shown.',
      BandDirect: 'direct',
      BandIndirect: 'indirect',
      BandEnergy: 'Energy',
      UseComputedGap: 'Save band gap',
      BandGapWritten: 'Computed band gap saved to properties',
      BandUnsupported: 'No tight-binding parameters for this material.',
      BandMethodNote:
        'Empirical sp³s* tight-binding (Vogl 1983). Energies relative to the valence-band maximum (0 eV). Blue = valence bands, orange = conduction bands.',
      BandTab: 'Band structure',
      DosTab: 'Density of states',
      AbsorptionTab: 'Optical absorption',
      DosAxis: 'DOS (states / eV)',
      DosComputing: 'Computing density of states…',
      DosNote:
        'Electronic density of states g(E) over the Brillouin zone, relative to the valence-band maximum (0 eV). The empty window between the dashed lines is the band gap; the peaks are van Hove singularities.',
      EffectiveMass: 'Effective mass',
      MassModelLimited: 'X/L valley (model-limited)',
      DopingNType: 'n-type',
      DopingPType: 'p-type',
      DopingIntrinsic: 'intrinsic',
      DopingNet: 'Net doping',
      DopingUndoped: 'Undoped',
      DopingIntrinsicLevel: '(intrinsic level, mid-gap)',
      DopingShallowApprox: 'shallow level approximate (model-limited mass)',
      AbsorptionOnset: 'Absorption onset',
      AbsorptionComputing: 'Computing absorption over the Brillouin zone…',
      AbsorptionPhotonEnergy: 'Photon energy',
      AbsorptionEps2: 'ε₂ (absorption, rel.)',
      AbsorptionIndirectNote: 'Indirect material',
      AbsorptionIndirectTooltip:
        'This onset is the lowest direct gap, not the band gap: bridging the indirect band gap needs a phonon, so its absorption is too weak to model here.',
      AbsorptionNote:
        'Imaginary dielectric function ε₂(ω) from interband transitions across the Brillouin zone (relative units). The onset is the minimum direct (vertical) gap. Excitonic and phonon-assisted effects are not included.',
      BandAlignment: 'Band alignment',
      AlignType: 'Type',
      AlignEffectiveGap: 'Effective gap',
      AlignSpatiallyDirect: 'spatially direct (electrons and holes in the same layer)',
      AlignSpatiallyIndirect: 'spatially indirect (electrons and holes in different layers)',
      AlignConduction: 'Conduction edge',
      AlignValence: 'Valence edge',
      AlignGrowthAxis: 'Growth axis (layers)',
      AlignInterface: 'Interface',
      AlignUnsupported: 'No parameters for',
      AlignNote:
        "Heterojunction band diagram from Anderson's rule (band edges from tight-binding gaps + electron affinities). Type I = both carriers confined together; type II = electrons and holes confined in different layers. Interface dipoles are neglected, so offsets are approximate.",
      AlignTab: 'Band alignment',
      MinibandTab: 'Minibands',
      MinibandConfinedGap: 'Confined gap',
      MinibandPlanes: 'planes',
      MinibandAxis: 'Superlattice wavevector k_z',
      MinibandComputing: 'Diagonalizing the superlattice supercell…',
      MinibandClamped: 'Period shortened to keep the supercell computable.',
      MinibandNote:
        'Tight-binding minibands of the [001] superlattice: the bulk bands of each layer folded into the smaller superlattice zone and split by quantum confinement. The confined gap (between the dashed lines) exceeds the well material’s bulk gap because confinement raises the electron level. Computed at in-plane k = 0.',
    },

    // Named structural features for the Feature Correlations view —
    // keys mirror NAMED_FEATURES[].id. Top-level namespace so i18next's
    // dot-as-separator resolution finds them via `t('feature.<id>')`.
    feature: {
      benzeneRing: 'Benzene ring',
      pyridineRing: 'Pyridine ring',
      furanRing: 'Furan ring',
      thiopheneRing: 'Thiophene ring',
      pyrroleRing: 'Pyrrole ring',
      cyclohexane: 'Cyclohexane',
      cyclopentane: 'Cyclopentane',
      cyclopropane: 'Cyclopropane',
      carbonyl: 'Carbonyl (C=O)',
      carboxylicAcidLike: 'Carboxylic acid / ester (O–C=O)',
      ester: 'Ester (C(=O)O–C)',
      amide: 'Amide (C(=O)–N)',
      nitrile: 'Nitrile (C#N)',
      nitroGroup: 'Nitro group',
      aniline: 'Aniline-like (aromatic C–N)',
      etherLinkage: 'Ether linkage (C–O–C)',
      phenolLike: 'Phenol-like (aromatic C–O)',
      alkene: 'Alkene (C=C)',
      alkyne: 'Alkyne (C#C)',
      fluorine: 'Fluorine',
      chlorine: 'Chlorine',
      bromine: 'Bromine',
      iodine: 'Iodine',
      trifluoromethyl: 'Trifluoromethyl (CF₃)',
      sulfonyl: 'Sulfonyl (S(=O)(=O))',
    },

    aboutUs: {
      ProductBroughtToYouBy: 'This product is brought to you by',
      TermsOfService: 'Terms of Service',
      PrivacyPolicy: 'Privacy Policy',
      TermsShort: 'Terms',
      PrivacyShort: 'Privacy',
      Software: 'Software',
      Content: 'Content',
      Research: 'Research',
      Support: 'Support',
      Acknowledgment: 'Acknowledgment',
      FundingInformation:
        'The National Institutes of Health (NIH) generously provides funding for the research and development of this product through grants R25GM150143. Any opinions, findings, and conclusions or recommendations expressed in this product, however, are those of the authors and do not necessarily reflect the views of NIH. We thank Dylan Bulseco, Kent Crippen, William Furiosi, Jason Ravitz, and Minji Yun for their important feedback and suggestions.',
      Contact: 'For more information, please contact Charles Xie (charles@intofuture.org).',
    },

    message: {
      FailInGeneratingMolecule: 'Failed in generating a molecule. Please try again. Click me to close.',
      FailInGeneratingAnswer: 'Failed to get an answer. Please try again. Click me to close.',
      GeneratedMoleculeMissingHydrogens:
        'The generated molecule has no hydrogen atoms. Try regenerating to get a complete structure.',
      CannotCreateNewProjectWhileGeneratingMolecule: 'Cannot create a new project while the AI assistant is working.',
      CannotOpenProjectWhileGeneratingMolecule: 'Cannot open other projects while the AI assistant is working.',
      GeneratingMolecule: 'Generating molecule...',
      GenerationRateLimitNotice: 'AI generation is limited to 100 requests per day per user.',
      GenerationRateLimitExceeded:
        "You've reached the limit of 100 AI generations for today. Please try again tomorrow.",
      // A chat / dialog AI turn that finished after the user opened another project.
      ResultDroppedProjectSwitched: '"{{prompt}}" finished after you switched projects, so it was not added.',
      GenerationUsageRemaining: '{{remaining}} of {{limit}} AI generations left today.',
      GenerationUsageUnlimited: 'AI generation: unlimited for your account.',
      GeneratingAMoleculeMayTakeAWhile:
        'The result is limited to molecules that have less than 1,000 atoms. Think about your prompt carefully before pressing OK.',
      GeneratedResultNotAccepted: 'Sorry, the generated result is not accepted. Click me to close.',
      BiomoleculeRenderFailed:
        'Sorry, this structure could not be rendered — it may be an obsolete, Cα-only, or otherwise incomplete PDB entry. It stays in the gallery; try a different structure. Click me to close.',
      BrowserStorageFull:
        'Browser storage is full — this project could not be cached for reload. What you loaded is still here in this session; Save it to the cloud, or remove some structures, to free space.',
      BiomoleculeCalphaTrace:
        'This is a Cα-only structure (no full backbone atoms), so it is shown as a backbone trace — the Cartoon style needs complete backbone atoms.',
      BiomoleculeFallbackStyle:
        'The selected style produced no geometry for this structure, so it is shown in a simpler representation (backbone trace or ball-and-stick).',
      GenerationOutputUnparseable:
        'The AI returned text that could not be parsed as a molecule. Try again, or switch to a different model.',
      GeneratedMoleculeFragments:
        'The generated molecule splits into {{count}} disconnected fragments — it is likely malformed (the model left out the bonds linking its groups). Click Generate again to retry.',
      RevisionNotAccepted: 'Sorry, the revision is not accepted.',
      DoNotShowAgain: "Don't show again",
      NewVersionAvailable: 'A new version is available',
      DoYouWantToRemoveAllMoleculesFromGallery: 'Do you want to remove all molecules from the gallery?',
      TooManyAtomsMayCauseSimulationToRunSlowly: 'Too many atoms may cause a simulation to run slowly',
      CannotSignIn: 'Cannot sign in',
      CannotSignOut: 'Cannot sign out',
      SigningOutAnonymousAccount: 'Are you sure you want to sign out? This anonymous account will be lost.',
      SigningInAnonymousAccount:
        'Sign in anonymously? Your projects will be tied to this browser only and permanently lost if you sign out, switch devices, or clear browser data. To keep your work, you can later click Sign in with Google (while still signed in anonymously) to upgrade this account and preserve all your projects.',
      YourAccountWasCreated: 'Your account was created.',
      YourAccountWasUpgraded: 'Your anonymous account was upgraded to Google. All your projects have been kept.',
      AnonymousLinkAccountExists:
        'A Google account with that email already exists in this app. To use it, sign out of this anonymous account and sign in with Google directly. Note: anonymous projects will not transfer.',
      CannotCreateAccount: 'Cannot create an account',
      ScreenshotSaved: 'A screenshot was saved.',
      CannotCreateNewProject: 'Cannot create a new project',
      CannotCreateNewProjectWithoutTitle: 'Cannot create a new project without a title',
      CannotOpenYourProjects: 'Cannot open your projects',
      CannotOpenProject: 'Cannot open the project',
      CannotSaveProject: 'Cannot save the project',
      NotSignedIn: 'You must be signed in to save. Sign in and try again.',
      SignInToUseAI: 'Please sign in to use AI features.',
      SignInToChat: 'Sign in to chat with the AI assistant.',
      ConservationScoring:
        "Scoring conservation — fetching this protein's family from UniProt and comparing every residue across its relatives…",
      ConservationFailed: 'Could not score conservation: {{reason}}',
      AiOnlyInOwnProject:
        'AI features are only available in your own projects. This project belongs to someone else — save your own copy first (Project menu → Save As), then you can use AI on it.',
      CannotSaveProjectWithoutTitle: 'Cannot save the project without a title',
      CannotDeleteProject: 'Cannot delete the project',
      CannotRenameProject: 'Cannot rename the project',
      CannotAddMoleculeToProject: 'Cannot add this molecule to the project',
      CannotAddMoleculeToProjectOwnedByOthers: 'Cannot add this molecule to a project owned by others',
      MoleculeRemovedFromProject: 'The molecule was successfully removed from the project',
      CannotRemoveMoleculeFromProject: 'Cannot remove this molecule from the project',
      CannotUpdateProject: 'Cannot update the project',
      CannotFetchProjectData: 'Cannot fetch project data',
      TitleUsedChooseDifferentOne: 'This title has been used. Choose a different one',
      TitleUsedInTrash: 'A project with this title is in Trash. Restore it or empty it from Trash first',
      ProjectIsInTrash: 'This project is in Trash. Restore it before opening',
      ProjectNoLongerExists: 'This project no longer exists — it may have been renamed or deleted by its owner',
      DoYouWantToSaveChanges: 'Do you want to save changes?',
      ThisCannotBeUndone: 'This cannot be undone.',
      ToSaveYourWorkPleaseSignIn: 'To save your work, please sign in.',
      NoSimilarMoleculesWereFound: 'No similar molecules were found.',
      TurnOnXYZPlanesForDroppingMolecule:
        'Turn on at least one of the X-Y, Y-Z, or X-Z planes for dropping the dragged molecule.',
      NoPlaneToPaste: 'No Plane to Paste',
      CrystalSurfaceStaticDuringSimulation:
        'This supercell has {{count}} atoms (over {{max}}), so the surface stays static during simulation. Reduce the supercell size for a surface that animates with the dynamics.',
    },

    molecularViewer: {
      Fog: 'Fog',
      Axes: 'Axes',
      Container: 'Container',
      UnitCells: 'Unit Cells',
      Mechanics: 'Mechanics',
      Trajectory: 'Trajectory',
      VanDerWaalsBonds: 'Van der Waals Bonds',
      HydrogenBonds: 'Hydrogen Bonds',
      KineticEnergyColors: 'Kinetic Energy Colors',
      DisulfideBonds: 'Disulfide Bonds',
      Pharmacophore: 'Pharmacophore',
      PharmacophoreTip:
        'Show the ligand’s pharmacophore: spheres marking aromatic rings and net-charged groups — the moieties most likely to drive specific interactions with the receptor.',
      AngularBonds: 'Angular Bonds',
      TorsionalBonds: 'Torsional Bonds',
      MomentumVectors: 'Momentum Vectors',
      ForceVectors: 'Force Vectors',
      ReactiveMode: 'Reactive Mode',
      NitrogenOxygenModel: 'N₂ and O₂ Model',
      NitrogenOxygenModelTip:
        'Two-site: two Lennard-Jones centers per molecule, no charges. Three-site with quadrupole: the full TraPPE models, which add a massless positive charge at the bond center and negative charges on the atoms. The charges carry the molecular quadrupole, which makes neighboring molecules prefer T-shaped contacts. Molecules with a charge you set by hand keep it and stay two-site.',
      TwoSiteModel: 'Two-site',
      ThreeSiteQuadrupoleModel: 'Three-site with quadrupole',
      LongRangeAttraction: 'Long-Range Attraction',
      LongRangeAttractionTip:
        'The lab computes the van der Waals (Lennard-Jones) attraction only out to 2.5 atom diameters, which leaves a liquid about 15 % less tightly bound than the full potential. Tail correction adds the missing attraction back, as if the liquid stretched sideways without end under a flat surface: each atom feels the average pull of the atoms above and below it. A liquid then evaporates less and boils hotter. Cut off is the setting the examples were measured with.',
      CutOffAttraction: 'Cut off at 2.5σ',
      TailCorrection: 'Tail correction',
      ImplicitSolvent: 'Implicit Solvent',
      ImplicitSolventTip:
        'The box holds no solvent molecules: the forces between the lipid beads stand in for the liquid. In water the oily tails attract one another and the heads repel everything, so the molecules gather tails inside, heads outside. In oil the polar heads attract one another and the tails only repel, so they gather heads inside, tails outside: inverse micelles. Other beads keep their own forces.',
      SolventWater: 'Water',
      SolventOil: 'Oil',
      SolventChipTip: 'The implicit solvent of the lipid beads. Change it under Experiment Settings › Force Field.',
      ReactiveModeTip:
        'Experimental. Routes the force calculation through a ReaxFF-style reactive force field, letting bonds form and break as atoms move (bond order emerges from interatomic distance) instead of the classical fixed-bond model. Drop the time step to ~0.2 fs for stability.',
      Spark: 'Spark',
      SparkTip:
        'Dissociate this molecule into radicals (e.g. H₂ → 2 H•) and give them an outward energy kick — an ignition spark for a cool reactive mixture. Reactive mode only.',
      EnergyGraph: 'Energy Graph',
      SpeedGraph: 'Speed Distribution',
      IrSpectrum: 'IR Spectrum',
      AnalysisPanel: 'Open Analysis Panel (Energy / Speed / IR Spectrum / …)',
      FixXAxis: 'Fix X-Axis',
      FixYAxis: 'Fix Y-Axis',
      SortByMolecule: 'Sort by Molecule',
      GlobalStyle: 'Global Style',
      ProteinStyle: 'Protein Style',
      ProteinStyleTip:
        'The 3D rendering style for the receptor — cartoon, surface, ball-and-stick, and so on. Cartoon emphasizes secondary structure; ball-and-stick shows every atom.',
      LigandStyle: 'Ligand Style',
      LigandStyleTip:
        'The 3D rendering style for the docked ligand — ball-and-stick, space-filling, stick, wireframe, and so on.',
      Style: 'Style',
      StyleTip:
        'The 3D rendering style used for each molecule in the gallery — ball-and-stick, space-filling, wireframe, and so on.',
      Color: 'Color',
      Sketch2D: '2D Sketch',
      Sketch2DCharge: '2D Sketch (Charges)',
      BallAndStick: 'Ball-and-Stick',
      Wireframe: 'Wireframe',
      AtomIndex: 'Atom Index',
      Stick: 'Stick',
      SpaceFilling: 'Space-Filling',
      Cartoon: 'Cartoon',
      Trace: 'Trace',
      Tube: 'Tube',
      Overlay: 'Overlay',
      OverlayTip:
        'An additional surface drawn on top of the main style — for example a solvent-accessible or electrostatic-potential surface. Choose None to disable.',
      NoOverlay: 'None',
      QuickSurface: 'Quick Surface',
      ContactSurface: 'Contact Surface',
      SolventAccessibleSurface: 'Solvent Accessible Surface',
      SolventExcludedSurface: 'Solvent Excluded Surface',
      ElectrostaticPotentialSurface: 'Electrostatic Potential Surface',
      DotSurface: 'Dot Surface',
      Material: 'Material',
      MaterialTip:
        'The visual material applied to atoms and bonds — controls shading, shininess, and whether the surface is solid, translucent, or glass-like.',
      Opacity: 'Opacity',
      OpacityTip:
        'How transparent the material is, from 0.05 (nearly invisible) to 0.95 (nearly opaque). Only used with translucent and glass materials.',
      DiffuseMaterial: 'Diffuse',
      SoftMaterial: 'Soft',
      GlossyMaterial: 'Glossy',
      MetalMaterial: 'Metal',
      TranslucentMaterial: 'Translucent',
      GlassMaterial: 'Glass',
      BackdropMaterial: 'Backdrop',
      ToonMaterial: 'Toon',
      FlatMaterial: 'Flat',
      BackgroundColor: 'Background Color',
      Shininess: 'Shininess',
      Element: 'Element',
      Residue: 'Residue',
      Chain: 'Chain',
      SecondaryStructure: 'Secondary Structure',
      Hydrophobicity: 'Hydrophobicity',
      Conservation: 'Conservation',
      Occupancy: 'Occupancy',
      Temperature: 'Temperature',
      Sequence: 'Sequence',
      Conformation: 'Conformation',
      Molecule: 'Molecule',
      TakeScreenshot: 'Take a Screenshot',
      TranslateMolecule: 'Translate',
      RotateMolecule: 'Rotate',
      RemoveLigand: 'Remove',
      Distance: 'Distance',
      Angle: 'Angle',
      Dihedral: 'Dihedral',
      AboutXAxis: 'About X Axis',
      AboutYAxis: 'About Y Axis',
      AboutZAxis: 'About Z Axis',
      EulerAngle: 'Euler Angle',
      Order: 'Order',
      ViewDirection: 'View Direction',
      PositiveXDirection: 'Positive X Direction',
      NegativeXDirection: 'Negative X Direction',
      PositiveYDirection: 'Positive Y Direction',
      NegativeYDirection: 'Negative Y Direction',
      PositiveZDirection: 'Positive Z Direction',
      NegativeZDirection: 'Negative Z Direction',
    },

    spaceship: {
      Spaceship: 'Spaceship',
      SpaceshipDisplay: 'Spaceship Display',
      SpaceshipDisplayTip:
        'Wrap the ligand in a small spaceship model. Outside view places the spaceship around the ligand for inspection; inside view puts the camera inside the cockpit for fly-through navigation.',
      OutsideView: 'Outside View',
      InsideView: 'Inside View',
      ResetOrientation: 'Reset Orientation',
      PositionControls: 'Position Controls',
      MoveForwardPressW: 'Move forward\nPress the W key',
      MoveBackwardPressS: 'Move backward\nPress the S key',
      MoveLeftPressA: 'Move left\nPress the A key',
      MoveRightPressD: 'Move right\nPress the D key',
      MoveUpPressZ: 'Move up\nPress the Z key',
      MoveDownPressX: 'Move up\nPress the X key',
      PitchUpPressArrowUp: 'Pitch up\nPress the Arrow Up key',
      PitchDownPressArrowDown: 'Pitch down\nPress the Arrow Down key',
      YawLeftPressArrowLeft: 'Yaw left\nPress the Arrow Left key',
      YawRightPressArrowRight: 'Yaw right\nPress the Arrow Right key',
      RollLeftPressQ: 'Roll left\nPress the Q key',
      RollRightPressE: 'Roll right\nPress the E key',
      Thrust: 'Thrust',
      ChangeThrustPower: 'Change thrust power',
      TeleportXCoordinate: 'Teleport x-coordinate',
      TeleportYCoordinate: 'Teleport y-coordinate',
      TeleportZCoordinate: 'Teleport z-coordinate',
    },

    experiment: {
      ReactiveSpecies: 'Reactive Species',
      Species: 'species',
      ReactantsProductsNote: 'Change vs. the mixture at run start: green = produced, red = consumed.',
      ExternalFields: 'External Fields',
      GravitationalField: 'Gravitational Field',
      GravitationalFieldTip:
        'A uniform external force applied to every atom (an artificial gravity), in units of 10¹² m/s² — molecular-scale magnitudes. Use it to settle/sediment molecules or pull them toward one side of the chamber. 0 = off. Set the pull direction below.',
      GravityDirection: 'Gravity Direction',
      ViewerCoordinateSystem: 'Viewer Coordinate System',
      ModelCoordinateSystem: 'Model Coordinate System',
      Piston: 'Piston',
      PistonTip:
        'A weighted plate on the top (+Z) wall that holds the gas at constant pressure. Molecules pushing up on it drive it open; the applied load pushes it closed. Heat the system and the plate rises as the liquid vaporizes — boiling emerges at the set pressure. Run with a thermostat on (constant temperature) for a proper NPT setup.',
      PistonPressure: 'Piston Pressure',
      PistonMass: 'Piston Mass',
      PistonMassTip:
        'Mass of the piston plate in amu. The default 100 amu is a light plate that visibly moves under gas collisions. 0 = auto: a heavy plate (4× the total atomic mass, at least 1000 amu) that glides smoothly but barely budges in an interactive run.',
      PistonMassAuto: 'auto (heavy)',
      PistonPosition: 'Piston Position',
      PistonPositionTip:
        'Height of the piston plate above the box floor (0 = floor, box height = ceiling). Set it above all the atoms to fix a plate that ended up cutting through the gas — e.g. after a constant-volume sweep left atoms on both sides. Applies to the live simulation and is saved with the project.',
      PistonPositionTop: 'Top',
      PistonPositionTopTip: 'Move the plate to the box ceiling, above all atoms.',
      PistonLangevin: 'Damped Piston',
      PistonLangevinTip:
        'Couple the plate to a heat bath (Langevin: friction + thermal noise) so its up-and-down breathing settles and the volume shows the correct constant-pressure (NPT) fluctuations, instead of a frictionless weight that keeps ringing. Recommended for cleaner boiling-point readings. Note: because the plate is thermalized, it jiggles a little on its own — you may see it drift back with no molecule touching it at that instant. That is the correct thermal fluctuation (plus the plate coasting on momentum from an earlier collision), not a glitch.',
      LangevinThermostat: 'Langevin thermostat',
      LangevinThermostatTip:
        'Per-atom stochastic thermostat that equipartitions every degree of freedom. Use for reactive runs: reaction energy released into vibration is bled into translation, instead of products vibrating in place. Off = global velocity rescale.',
      ThermostatRescale: 'Velocity rescale',
      ThermostatCsvr: 'CSVR (canonical)',
      ThermostatMethodTip:
        'Velocity rescale pins the kinetic energy to the setpoint every step (isokinetic, no thermal fluctuations). CSVR (Bussi–Donadio–Parrinello canonical sampling through velocity rescaling) keeps the same global rescale but lets the kinetic energy fluctuate canonically, with a 100 fs coupling time: the true NVT ensemble, for heat-capacity and fluctuation studies. Langevin is per-atom and equipartitions every degree of freedom (needed for reactive runs).',
      CrystalThermostatTip:
        'Berendsen (weak coupling, τ = 100 fs) drives the temperature to the setpoint smoothly but damps the thermal fluctuations, so it does not sample the true NVT ensemble. CSVR (Bussi-Donadio-Parrinello) adds a stochastic term so the kinetic energy fluctuates canonically. Use it for heat-capacity and fluctuation studies.',
      ThermostatGroups: 'Thermostat groups',
      ThermostatGroupsTip:
        "Thermostats that act on a chosen set of atoms instead of the whole lab, each at its own temperature: hold one end hot and the other cold to watch heat conduction, thermostat only a substrate while a film grows on it, or heat one spot. A group is a band measured from one face of the structure (bottom = −Z … front = +Y; the depth is a percentage of the structure's extent) or the atoms you picked. Each group uses the thermostat selected above. Atoms in no group follow the Constant Temperature setting: held at its temperature when it is on, otherwise energy-conserving — so the middle between a hot and a cold end conducts heat with nothing touching it.",
      AddThermostatGroup: 'Add group',
      ThermostatGroupEverywhere: 'Everywhere',
      ThermostatGroupDepthTip:
        "Depth of the band from the named face, as a percentage of the structure's extent along that axis.",
      ThermostatGroupPickedAtoms: 'picked atoms',
      ThermostatGroupMeasured: 'measured',
      ThermostatGroupAtoms: 'atoms',
      ThermostatGroupNoAtoms: 'no atoms in this group right now',
      RegionBottom: 'Bottom (−Z)',
      RegionTop: 'Top (+Z)',
      RegionLeft: 'Left (−X)',
      RegionRight: 'Right (+X)',
      RegionFront: 'Front (+Y)',
      RegionBack: 'Back (−Y)',
      RegionZmiddle: 'Middle (z)',
      RegionZedges: 'Both ends (z)',
      RegionXmiddle: 'Middle (x)',
      RegionXedges: 'Both ends (x)',
      RegionYmiddle: 'Middle (y)',
      RegionYedges: 'Both ends (y)',
      ReactiveCharges: 'Reactive charges',
      ReactiveChargesTip:
        'Give atoms partial charges from charge equilibration (QEq). Off holds every charge at zero, the setting the reactive force field was fitted in. QEq solved over the whole box moves charge between molecules that are not bonded, such as from H₂ to O₂, which real neutral molecules never do. Turn off for reactions among neutral gases.',
      FreezeReactiveCharges: 'Freeze reactive charges',
      FreezeReactiveChargesTip:
        'Solve the charge equilibration (QEq) once at the start of a run, then freeze the charges. Per-step re-solving is not energy-conserving and makes reactive systems spuriously self-heat and fall apart. Leave on unless you are specifically testing per-step charges.',
      WholeApp: 'Whole App',
      Lab: 'Lab',
      LabPurpose:
        'The Lab is your interactive 3-D workbench — build, simulate, and experiment with what you create or load here. (The gallery is where you organize and compare your work.)',
      Gallery: 'Gallery',
      Chamber: 'Lab',
      FixAtom: 'Fix',
      FixMolecule: 'Fix All Atoms',
      Restraint: 'Restraint',
      RestraintTip:
        'A harmonic spring (stiffness in eV/Å²) that holds this molecule’s atoms near their current positions. 0 = unrestrained; larger values pin it more stiffly. Use it to anchor part of the system while the rest moves.',
      DampingCoefficient: 'Damping',
      AtomicCoordinates: 'Coordinates',
      AtomicMass: 'Atomic Mass',
      BeadType: 'Bead Type',
      Resolution: 'Resolution',
      ResolutionAllAtom: 'All atom',
      ResolutionUnitedAtom: 'United atom',
      ResolutionBeadSpring: 'Coarse-grained beads',
      ResolutionEllipsoid: 'Ellipsoids',
      ResolutionMixed: 'Mixed resolution',
      ResolutionReducedUnits: 'reduced units',
      ResolutionNote:
        'A bead stands for a group of atoms. Its mass, size and well depth come from the bead table, not from the element shown in its place.',
      NematicOrder: 'Nematic order',
      RodAligningField: 'Aligning field on rods',
      RodAligningFieldTip:
        'A reduced field E* along the z axis that turns each Gay-Berne rod toward the axis, U = −½ ε₀ E*² (û·ẑ)² per rod. Switch it on in a nematic whose director lies across the axis to see the director turn (the Fréedericksz transition), off to see it relax.',
      AtomicRadius: 'Atomic Radius',
      LennardJonesEpsilon: 'LJ Well Depth (ε)',
      ElectricCharge: 'Charge',
      BondCenterCharge: 'Bond-center charge (quadrupole)',
      Model: 'Model',
      Display: 'Display',
      MolecularDynamics: 'Molecular Dynamics',
      StartSimulation: 'Start Simulation',
      PauseSimulation: 'Pause Simulation',
      ResetSimulation: 'Reset Simulation',
      PauseToUndoMove: 'Pause the simulation to undo or redo a move.',
      SimulationUnstable:
        'The simulation became unstable and was paused — the atoms flew apart. Reduce the time step (or relax/rebuild the structure), then Reset and run again.',
      MinimizeEnergy: 'Minimize Energy',
      MinimizingEnergy: 'Minimizing energy…',
      StopMinimizing: 'Stop',
      MinimizationDone: 'Structure relaxed',
      MinimizationStopped: 'Minimization stopped',
      AlreadyRelaxed: 'The structure is already relaxed — no steric clashes or strain to remove.',
      MinimizationFailed:
        'Minimization diverged — the structure was left unchanged. Try again after separating badly overlapping molecules.',
      NothingToMinimize: 'Add a molecule to the lab first.',
      PauseBeforeMinimize: 'Pause the simulation before minimizing.',
      WaitForMinimize: 'Energy minimization is in progress — wait for it to finish (or stop it) before running.',
      WaitForMinimizeEdit:
        'Energy minimization is in progress — wait for it to finish (or stop it) before adding or removing molecules.',
      HeatRefusedAtMaximum:
        'Heating by 20% would take the lab past the maximum temperature ({{max}} K); nothing was changed.',
      CoolRefusedAtRest: 'The lab is at rest (0 K); there is nothing to cool.',
      WaitForMinimizeHeat:
        'Energy minimization is in progress — it settles the atoms to rest, so heating or cooling has no effect until it finishes (or is stopped).',
      MinimizationDiscarded: 'The lab changed while it was relaxing, so the relaxed structure was discarded.',
      ExperimentSettings: 'Experiment Settings',
      Boundary: 'Boundary',
      ContainerLx: 'Container Lx',
      ContainerLxTip:
        'Width of the simulation container along the X axis, in ångströms. Atoms outside the container are clipped from view.',
      ContainerLy: 'Container Ly',
      ContainerLyTip: 'Height of the simulation container along the Y axis, in ångströms.',
      ContainerLz: 'Container Lz',
      ContainerLzTip: 'Depth of the simulation container along the Z axis, in ångströms.',
      ContainerFaceTransparency: 'Container Transparency',
      PeriodicBoundaries: 'Periodic Boundaries',
      PeriodicBoundariesTip:
        'The box repeats in all three directions: there are no walls, and a molecule leaving one face re-enters at the opposite face, so the model is a small piece of bulk matter with no surface. Keep each edge at least twice the longest cutoff (about 25 Å for atoms, 60 Å for the rods). The piston, gravity and the long-range attraction tail are off while periodic, and the reactive force field keeps its walls for now.',
      PeriodicChip: 'Periodic',
      PeriodicChipTip:
        'Periodic boundaries are on: the box has no walls and repeats in every direction, so the lab is a small piece of bulk matter.',
      Barostat: 'Hold Pressure',
      BarostatTip:
        'Hold the periodic cell at a target pressure by letting it breathe: each step the cell and every molecule’s position are scaled a little toward the pressure, so the box grows or shrinks on screen as the sample expands or contracts (a Berendsen barostat). Molecules keep their shape and the rods their orientation. With the thermostat on this is a constant-pressure, constant-temperature run: a liquid finds its own density, and a phase change shows as a jump in volume. The box the simulation ends in is saved with the project. Off in a walled box, where the piston is the pressure control, and with pinned atoms.',
      BarostatPressure: 'Target Pressure',
      BarostatTau: 'Coupling Time',
      BarostatTauTip:
        'How quickly the cell answers a pressure mismatch: the volume relaxes toward the target over about this time, for a gas and a liquid alike. Shorter follows faster but breathes more; 1 to 5 ps is usual.',
      BarostatChip: 'Constant Pressure',
      BarostatChipTip:
        'The periodic cell is held at this pressure: the box breathes as the sample expands or contracts. Its edges are saved with the project.',
      DisplaySettings: 'Display Settings',
      VdwBondCutoffRelative: 'VDW Bond Cutoff',
      MomentumScaleFactor: 'Momentum Scale Factor',
      ForceScaleFactor: 'Force Scale Factor',
      KineticEnergyScaleFactor: 'Kinetic Energy Scale Factor',
      TimeStep: 'Time Step',
      ForceField: 'Force Field',
      GuestMolecules: 'Guest Molecules',
      GuestMolecule: 'Guest molecule',
      AddGuest: 'Add guest',
      RemoveGuest: 'Remove guest',
      RelaxGuest: 'Relax into a clear pocket',
      NoGuests: 'No guest molecules. Add one above.',
      GuestHint:
        'New guests settle into the nearest open pocket. Click a guest, then drag the gizmo to move it, or use the target button to re-settle it.',
      Thermostat: 'Thermostat',
      SimulationSpeed: 'Simulation Speed',
      Mechanics: 'Mechanics',
      VelocityVectors: 'Velocity Vectors',
      ForceVectors: 'Force Vectors',
      VectorScale: 'Vector Scale',
      KineticEnergyColors: 'Kinetic Energy Colors',
      VanDerWaalsContacts: 'Van der Waals Contacts',
      ContactRange: 'Contact Range',
      Steps: 'Steps',
      Intervals: 'Intervals',
      RefreshingInterval: 'Refreshing Interval',
      CollectionInterval: 'Collection Interval',
      SpeedGraphBins: 'Speed Distribution Bin Numbers',
      Femtosecond: 'Femtosecond',
      ConstantTemperature: 'Constant Temperature',
      TemperatureUnit: 'Temperature Unit',
      TemperatureSettings: 'Temperature Settings',
      StructureMetrics: 'Structure Metrics',
      RadiusOfGyration: 'Radius of gyration',
      RmsdFromReference: 'RMSD from reference',
      StructureWholeSystem: 'Whole system',
      StructurePerMolecule: 'Per molecule',
      StructureSelectedMolecules: 'Selected molecules',
      StructureSelectedAtoms: 'Selected atoms',
      RadiusOfGyrationHelp:
        'The mass-weighted RMS distance of the atoms from the center of mass. Larger = more expanded / unfolded; smaller = more compact. A picked molecule reports its own value; with nothing picked, the whole system.',
      RmsdFromReferenceHelp:
        'Deviation from the reference frame — the as-loaded geometry, or the last minimize / reset — with overall translation removed (no rotational fit). Use it as a drift / equilibration gauge.',
      TemperatureReadoutHelp:
        "The system temperature. With a thermostat on (constant temperature) it shows the target setpoint; in NVE (thermostat off) it shows the instantaneous value computed from the atoms' kinetic energy over the movable atoms. After an energy minimization the velocities are zeroed, so it reads 0 K. Toggle °C / K in the settings below.",
      TimeReadoutHelp:
        'Elapsed simulated time = number of integration steps × timestep. This is model time inside the simulation, not wall-clock time; it returns to 0 when you reset the run.',
      Temperature: 'Temperature',
      Pressure: 'Pressure',
      Protein: 'Protein',
      ProteinTip:
        'The target receptor (the macromolecule the ligand docks into). Pick from the built-in list of curated PDB structures, or “None” to clear the receptor.',
      FromPdbId: 'From PDB ID',
      FromPdbIdTip:
        'Fetch any structure from the RCSB Protein Data Bank by its 4-character PDB ID (e.g. 1HVR, 6LU7). The structure is downloaded directly and loaded as the receptor — no need for the curated list above.',
      FromPdbIdPlaceholder: 'e.g. 1HVR',
      FromPdbIdFetch: 'Fetch',
      PdbIdInvalid: 'Invalid PDB ID. Expected 4 characters: a digit 1–9 then 3 alphanumeric.',
      PdbIdNotFound: 'PDB entry not found in RCSB',
      PdbIdNoAtoms: 'RCSB returned no atom records — entry may only ship in mmCIF format',
      PdbIdTooLarge:
        'This structure is too large to load in the browser (a very large assembly such as a ribosome or capsid). Try a single chain or a smaller representative entry',
      PdbIdTimeout: 'RCSB did not respond in time. Check your connection and try again',
      PdbIdFetchFailed: 'Could not fetch from RCSB',
      StructureDroppedProjectChanged:
        'Another project was opened while this was loading, so the structure was not added',
      PdbIdFetching: 'Fetching PDB',
      PdbIdLoaded: 'Loading PDB',
      // Biomolecular Lab "From sequence" builder (Protein Lab + DNA/RNA Lab)
      LoadProtein: 'Load protein',
      LoadDnaRna: 'Load DNA/RNA',
      LoadFromPdb: 'Load from PDB ID',
      BuildFromSequence: 'Build from sequence',
      StructureSource: 'Source',
      SourceRcsb: 'RCSB Protein Data Bank',
      SourceBuilt: 'Built in AIMS',
      BioLabPurpose:
        'The Lab is your interactive 3-D workbench — explore and experiment with what you load to study how it works. (The gallery is where you characterize and compare your whole set of structures.)',
      StructureSequence: 'Sequence',
      FromAminoAcidSequence: 'From amino-acid sequence',
      FromNucleotideSequence: 'From nucleotide sequence',
      SeqType: 'Type:',
      SeqTypeDna: 'DNA (double helix)',
      SeqTypeRna: 'RNA (single strand)',
      SeqPlaceholderPeptide: 'e.g. MKTAYIAKQR  (1-letter amino acids)',
      SeqPlaceholderDna: 'e.g. ATCGGCTAAGCT  (A/T/G/C)',
      SeqPlaceholderRna: 'e.g. AUGCAUGCGGCC  (A/U/G/C)',
      SeqHintPeptide: 'Folded into 3D by ESMFold (max {{max}} residues).',
      SeqHintDna: 'Idealized B-form double helix (max {{max}} bp).',
      SeqHintRna: 'Idealized A-form single strand (max {{max}} nt).',
      SeqBuild: 'Build',
      SeqTranslateToProtein: 'Translate → protein',
      SeqEnterFirst: 'Enter a sequence first.',
      SeqFolding: 'Folding with ESMFold…',
      SeqFoldedLoaded: 'Folded structure loaded.',
      SeqFoldedPeptideName: 'Folded peptide',
      SeqBuiltDnaDuplex: 'Built {{n}} bp DNA duplex.',
      SeqBuiltRnaStrand: 'Built {{n}} nt RNA strand.',
      SeqCouldNotBuild: 'Could not build a structure from that sequence.',
      SeqEnterCodingFirst: 'Enter a DNA or RNA coding sequence first.',
      SeqTranslateNoProtein: 'No protein could be translated (no codons before a stop).',
      SeqTranslateTooShort: 'Sequence is too short to contain a codon (need ≥ 3 bases).',
      SeqTranslateNoteAug: 'from the first AUG',
      SeqTranslateNoteFrame1: 'from frame 1 (no AUG found)',
      SeqTranslating: 'Translated {{n}} aa {{note}}; folding with ESMFold…',
      SeqTranslatedProteinName: 'Translated protein ({{n}} aa)',
      SeqTranslatedLoaded: 'Translated protein folded and loaded.',
      SeqCouldNotFoldTranslated: 'Could not fold the translated protein.',
      MutateTitle: 'Mutate & Refold',
      MutateParent: 'Parent: {{name}}',
      MutateNoStructure: 'Select a protein structure to mutate first.',
      MutateEnterFirst: 'Enter a mutation, e.g. "E6V".',
      MutateBadToken: 'Could not parse mutation',
      MutatePlaceholder: 'e.g. E6V, A24S',
      MutateHint: 'One-letter codes; comma/space separated.',
      MutateApply: 'Mutate',
      MutateRunning: 'Folding the mutant…',
      MutateInvalid: 'Mutation does not match the structure (position, residue, or chain).',
      MutateFoldFailed: 'ESMFold could not fold the mutant sequence.',
      MutateFailed: 'Could not run the mutation.',
      MutateDone: 'Folded {{muts}} mutant — Cα RMSD {{rmsd}} Å vs parent.',
      FoldErrorEmpty: 'Enter an amino-acid sequence.',
      FoldErrorTooLong: 'Too long — ESMFold caps at {{max}} residues.',
      FoldErrorInvalidResidues: 'Use the 20 standard amino-acid letters (ACDEFGHIKLMNPQRSTVWY).',
      FoldErrorBusy: 'The folding service is busy — try again shortly.',
      FoldErrorUnreachable: 'Could not reach the folding service.',
      FoldErrorFailed: 'Folding failed for that sequence.',
      Ligand: 'Ligand',
      LigandTip:
        'The small molecule being docked into the receptor. Pick from the loaded molecules, or “None” to clear the ligand.',
      Molecules: 'Molecules',
      SelectorCommands: 'Selector Commands',
      SelectorCommandsTip:
        'A Miew-style selector expression that filters which residues / atoms of the receptor are shown — e.g. “chain A and not water”, “resi 25-30”, or “all” for the full structure.',
      Information: 'Information',
      Content: 'Content',
      MovingStep: 'Moving Step',
      MovingStepTip:
        'Distance the ligand moves on each arrow-key press, in ångströms. Smaller values give finer manual placement.',
      RotationStep: 'Rotation Step',
      RotationStepTip:
        'Angle the ligand rotates on each rotation-key press, in degrees. Smaller values give finer manual orientation.',
      BindingScore: 'Binding Score',
      CollapsePanel: 'Collapse',
      ExpandPanel: 'Expand',
      BindingScoreTip:
        'Approximate ligand–protein interaction energy (van der Waals + electrostatic) for the current pose. Lower, more negative values indicate a more favorable fit. Steric clashes count atom pairs pushed into severe overlap.',
      VdwTerm: 'vdW',
      ElectrostaticTerm: 'Elec',
      StericClashes: 'Steric Clashes',
      HBondTerm: 'HB',
      ClashTerm: 'Clash',
      HydrogenBonds: 'Hydrogen Bonds',
      RelaxLigand: 'Dock',
      StopRelaxing: 'Stop',
      RelaxLigandTip:
        'Let the ligand settle into a binding pocket: an energy minimization that moves the pose downhill on the binding score. If the ligand is not yet touching the receptor, it first glides toward it until they make contact.',
      FlexibleLigand: 'Flexible ligand',
      FlexibleLigandTip:
        'When on, Dock and Search treat the ligand as flexible — its rotatable bonds are optimized along with its position and orientation, so it can change shape to fit the pocket. When off, the ligand docks as a rigid body.',
      BindingAffinityTip:
        'An estimate of the binding free energy ΔG and the dissociation constant Kd — the docking interaction energy plus a conformational-entropy penalty for the ligand’s rotatable bonds. An approximation: the docking score is not a calibrated predictor, so read it as a relative guide for comparing poses and candidates, not an absolute measurement.',
      TopContributors: 'Top contributors',
      TopContributorsTip:
        'The receptor residues that contribute most to the binding score at the current pose, ranked by their attributed interaction energy (kcal/mol). Negative values are favorable contacts; positive values are unfavorable (typically steric clashes or like-charge repulsion).',
      PharmacophoreGuided: 'Pharma-guided',
      PharmacophoreGuidedTip:
        'When on, the pose Search adds an alignment bonus to the score when ligand pharmacophore features overlap complementary receptor features — aromatic ↔ aromatic (π-π stacking) and positive ↔ negative (salt bridges). The bonus is also added to the live score so you can see how well the current pose matches the pharmacophore.',
      PharmacophoreTerm: 'Pharma',
      NoPharmacophoreFeatures: 'no aromatic / charged features',
      ScreenGallery: 'Screen Gallery',
      ScreenGalleryTip:
        'Dock every gallery molecule at the chosen binding site and rank them by estimated binding free energy ΔG. Each candidate runs an independent multi-start pose search, sequentially; click any row in the results to load that candidate and apply its best-found pose to the lab.',
      ScreenGalleryNeedsSiteTip:
        'A binding site must be chosen first (use "Find pockets" and click a pocket). Without one, each candidate would search a different region and the rankings would be incoherent.',
      Screening: 'Screening',
      ScreeningResults: 'Screening results',
      ScreeningResultsTip:
        'Gallery candidates ranked by ΔG (best first). Click a row to swap that candidate into the lab at its best-found pose.',
      ApplyScreeningResult: 'Apply this candidate + pose',
      ExportScreeningCsvTip:
        'Download the screening results as a CSV file — one row per candidate with ΔG, Kd, score breakdown, and contact counts. Best-pose coordinates are not included; rerun the screen to reload them into the lab.',
      AnalyzeScreeningTip:
        "Ask the AI to interpret the screening table — what scaffolds explain the top hits, what fails, which candidates are worth pursuing, and what modifications could improve the top hit. Streams to the reasoning panel; uses the project's selected provider.",
      AnalysisSummary: 'AI analysis',
      Agent: 'Agent',
      DockingAgent: 'Docking Agent',
      AgentTip:
        'Open the docking agent. Describe a task in natural language ("find pockets and dock aspirin into the largest one") and the agent plans + runs the tool calls, streaming progress to the reasoning panel.',
      DockingAgentDescription:
        "Describe the docking task. The agent reads the loaded receptor and ligand state, picks from the available tools (lookup / generate ligand, find pockets, run pose search, apply a result), and reports back when it's done. Progress streams into the reasoning panel.",
      DockingAgentPlaceholder:
        'e.g. "Find pockets on the loaded receptor, pick the largest, dock aspirin into it, and report the top pose."',
      MoleculeAgent: 'Molecule Agent',
      MoleculeAgentDescription:
        'Describe a task in natural language. The agent can look up or design molecules, find similar ones, add hydrogens, auto-bond, minimize energy, mirror, and run molecular dynamics — planning and running the tool calls for you. Progress streams below.',
      MoleculeAgentPlaceholder:
        'e.g. "Generate three soluble aspirin analogues, minimize each, and add them to the gallery."',
      CrystalAgent: 'Crystal Agent',
      CrystalAgentDescription:
        'Describe a task in natural language. The agent can look up or design crystals, substitute / dope elements, inspect properties (including band gap), build a supercell into the lab, compute the band structure, and check charge balance. Progress streams below.',
      CrystalAgentPlaceholder:
        'e.g. "Look up silicon, dope it with phosphorus at 5%, load it into the lab, and compute the band structure."',
      DesignLigands: 'Design',
      DesignLigandsTip:
        'Ask the AI to design drug-like ligand candidates for the selected pocket. The prompt includes the lining residues, pocket volume, and nearby pharmacophore profile so the LLM tailors chemistry to this site. Each candidate is added to the gallery; screen them with the button below to rank by ΔG.',
      DesignLigandsNeedsSiteTip:
        'Select a binding pocket first (use "Find pockets" and click one). The AI prompt is built from the selected pocket\'s residues, volume, and pharmacophore profile.',
      Designing: 'Designing',
      IterativeDesignNote: 'Learning from {{count}} prior attempt(s)',
      IterativeDesignTip:
        '{{count}} previously AI-designed candidate(s) have been screened. The next Design Ligand press will include their ΔG, contacts, clashes, and the AI screening analysis in the prompt — so the model proposes a candidate that addresses their failures instead of starting cold.',
      ConformationChange: 'Conformation change',
      ConformationChangeTip:
        'How far the docked conformation has moved from the ligand’s input conformation, as an all-atom RMSD. A large value means the ligand had to contort to fit the pocket.',
      OriginalConformation: 'Original conformation',
      OriginalConformationTip:
        'Overlay a translucent ghost of the ligand’s input conformation on the docked one, to see how its shape changed to fit the pocket.',
      FindPockets: 'Find pockets',
      DetectingPockets: 'Detecting pockets…',
      FindPocketsTip:
        'Scan the receptor for candidate binding pockets — concave clefts and buried cavities where a ligand can dock. Pick a pocket from the list to make it the search target; the search then samples poses around it instead of around the ligand’s placement.',
      NoPocketsFound: 'No pockets found',
      NoPocketSelectedTip:
        'No binding pocket is selected. Click "Find pockets" to scan the receptor, then click a row in the pocket list to make it the target.',
      NoCandidatesTip:
        'No candidates to screen. Add molecules to the gallery (or design one with "Design Ligand"), and make sure none are filtered out or deselected.',
      Pocket: 'Pocket',
      TargetPocketTip: 'Target this pocket as the search site (click again to clear)',
      HideMarkers: 'Hide markers',
      ShowMarkers: 'Show markers',
      PocketMarkersTip: 'Show or hide the pocket spheres and the selected pocket’s residue labels in the 3D view.',
      SearchPoses: 'Search',
      Searching: 'Searching…',
      Approaching: 'Approaching…',
      TopPoses: 'Top Poses',
      ApplyPose: 'Apply this pose',
      SearchRadius: 'Search Radius',
      SearchRadiusTip:
        'Radius of the sphere within which automated pose search samples trial poses, in ångströms. Centered on the selected pocket (or the ligand’s current position if none). Larger radii explore more of the receptor but are slower.',
      SearchRegion: 'Search Region',
      SearchRegionTip:
        'Show the translucent sphere that marks where automated pose search will sample. Its center is the selected pocket (or the ligand’s current position if none) and its radius is the Search Radius set in Experiment Settings.',
      SearchPosesTip:
        'Automatically search for binding poses: many trial poses are sampled within the search radius of the current ligand position and relaxed, then the best-scoring distinct ones are ranked below. Click a result to apply it. Set the search radius in Experiment Settings.',
      MoveInPositiveXDirection: 'Move test molecule in positive X direction',
      MoveInNegativeXDirection: 'Move test molecule in negative X direction',
      MoveInPositiveYDirection: 'Move test molecule in positive Y direction',
      MoveInNegativeYDirection: 'Move test molecule in negative Y direction',
      MoveInPositiveZDirection: 'Move test molecule in positive Z direction',
      MoveInNegativeZDirection: 'Move test molecule in negative Z direction',
      RotateAroundXClockwise: 'Rotate test molecule around X in clockwise direction',
      RotateAroundXCounterclockwise: 'Rotate test molecule around X in counterclockwise direction',
      RotateAroundYClockwise: 'Rotate test molecule around Y in clockwise direction',
      RotateAroundYCounterclockwise: 'Rotate test molecule around Y in counterclockwise direction',
      RotateAroundZClockwise: 'Rotate test molecule around Z in clockwise direction',
      RotateAroundZCounterclockwise: 'Rotate test molecule around Z in counterclockwise direction',
      ShowXYPlane: 'Show X-Y plane',
      ShowYZPlane: 'Show Y-Z plane',
      ShowXZPlane: 'Show X-Z plane',
      DeleteAllAtoms: 'Delete all atoms',
      DoYouReallyWantToDeleteAllAtoms: 'Do you really want to delete all atoms',
      SwitchToAtomPickMode: 'Switch to atom selection',
      SwitchToMoleculePickMode: 'Switch to molecule selection',
      GayBerneEllipsoid: 'Gay-Berne ellipsoid',
      Represents5CB: 'Represents one 5CB molecule (aspect {{aspect}})',
      OneEllipsoid: '1 ellipsoid',
      NEllipsoids: '{{n}} ellipsoids',
      ParticlePosition: 'Position',
      ParticleAxis: 'Long Axis',
      ParticleSize: 'Size',
      ParticleMass: 'Mass',
      ParticleSelected: 'particle selected',
      ParticlesSelected: 'particles selected',
      DeleteSelectedParticles: 'Delete selected particles',
      AtomSelected: 'atom selected',
      AtomsSelected: 'atoms selected',
      MoleculeSelected: 'molecule selected',
      MoleculesSelected: 'molecules selected',
      ToggleFixedOnSelected: 'Toggle fixed on selected atoms',
      TrackMeasurement: 'Track this measurement over time (adds it to the Measurement graph)',
      DeleteSelectedMolecules: 'Delete selected molecules',
      ClearSelection: 'Clear selection (Esc)',
    },

    examplesDrawer: {
      SearchPlaceholder: 'Search examples…',
      NoMatches: 'No examples match your search.',
      RecentlyOpened: 'Recently Opened',
    },

    analysisPanel: {
      TabEnergy: 'Energy',
      TabSpeed: 'Speed',
      TabIRSpectrum: 'Spectrum',
      TabPhonon: 'Phonon',
      TabVibration: 'Vibration',
      TabMSD: 'MSD',
      TabGOfR: 'g(r)',
      TabPolymer: 'Polymer',
      TabReaction: 'Reaction',
      TabMeasurement: 'Measurement',
      TabVolume: 'Volume',
    },
    pistonPanel: {
      Volume: 'Gas volume',
      IdealGas: 'Ideal gas law',
    },
    measurementPanel: {
      Record: 'Record',
      Recording: 'Recording',
      Clear: 'Clear',
      Samples: 'samples',
      RecordTip: 'Start logging every track over simulated time. Create one or more tracks first.',
      ClearTip: 'Erase the recorded traces.',
      TracksLabel: 'tracks',
      RemoveTrack: 'Remove track',
      ScaleNormalized: 'Normalized',
      ScaleRaw: 'Raw',
      Normalized: 'Normalized (0–1)',
      NoSelection:
        'No tracks yet. In atom-pick mode, pick 2–4 atoms on the canvas and click the Track button on the selection chip to add a measurement.',
      Hint: 'Plots the distance (2 atoms), angle (3), or dihedral (4) of picked atoms versus simulated time. Create a track by picking 2–4 atoms on the canvas and clicking Track on the selection chip; the atom set is fixed at that moment. Record several tracks together and overlay them — Normalized min-max scales each to 0–1 so mixed units line up (e.g. a breaking bond crossing a forming one), while Raw keeps physical units (Å on the left axis, ° on the right). Switch to Autocorrelation to read off a track’s oscillation period and frequency.',
      ViewSeries: 'Trace',
      ViewAutocorrelation: 'Autocorrelation',
      Lag: 'Lag',
      AcfYAxis: 'Autocorrelation',
      Period: 'Period',
      PeriodsPerTrack: 'Period per track shown on the chips',
      NoPeriodShort: 'no period',
      CompareIR: 'compare to the Vibration tab',
      NoPeriod:
        'No full oscillation resolved yet — record a longer trace of an oscillating quantity (e.g. a vibrating bond length).',
    },

    reactionPanel: {
      MolePercent: 'Mole percent (%)',
      Empty:
        'Press play in reactive mode to watch reactants turn into products. Species counts are sampled as the simulation runs.',
    },

    energyGraph: {
      Atom: 'atom',
      Collecting: 'collecting…',
      Samples: 'Samples',
      MeasuredFromKE: 'measured from ⟨K⟩',
      ShowPiston: 'Show the piston plate’s energy and gas + piston (the conserved total with an undamped piston)',
      PistonEnergyLine: 'Piston (÷N)',
      GasPlusPiston: 'Gas + piston',
      HeatCapacityTip:
        'Heat capacity at constant volume from total-energy fluctuations: Cᵥ = ⟨(δE)²⟩ / (kᵇ T²). Compare to 3 kᵇ/atom (classical solid, Dulong–Petit), 3/2 kᵇ/atom (monatomic gas), 5/2 kᵇ/atom (diatomic with rotation).',
      NveCaveat:
        'Thermostat is off — total energy is nearly conserved, so this formula is misleading. The variance you see is integrator drift, not thermal fluctuation. For a meaningful Cᵥ, turn on the thermostat (NVT).',
      IsokineticNote:
        'The velocity-rescaling thermostat pins the kinetic energy, so only the potential energy fluctuates. The kinetic contribution (3/2 kᵇ per mobile atom) is added analytically. Use the Langevin thermostat to measure it from fluctuations instead.',
      Analyze: 'Analyze',
      AnalyzeTip:
        'Ask an AI assistant to interpret these energy curves — equilibration, energy conservation, dominant contributions, and how the heat capacity compares to classical references.',
      AnalyzeCancel: 'Cancel analysis',
      AnalyzeCancelTip: 'Stop the in-flight analysis. Any text already received is kept.',
      AnalysisEmpty: 'The analyzer returned no text. Try again or run a longer trajectory.',
      AnalysisFailed: 'Analysis failed. Try again in a moment.',
      AnalysisNeedsSignIn: 'Sign in to use AI-assisted analysis.',
      AnalysisRateLimited: 'AI analysis quota reached. Try again in a few minutes.',
    },

    speedGraph: {
      Analyze: 'Analyze',
      AnalyzeTip:
        "Ask an AI assistant to interpret the speed distribution — Maxwell-Boltzmann fit quality, equipartition across species, and any deviations that suggest the system isn't equilibrated.",
      AnalyzeCancel: 'Cancel analysis',
      AnalyzeCancelTip: 'Stop the in-flight analysis. Any text already received is kept.',
      AnalysisEmpty: 'The analyzer returned no text. Try again or run a longer trajectory.',
      AnalysisFailed: 'Analysis failed. Try again in a moment.',
      AnalysisNeedsSignIn: 'Sign in to use AI-assisted analysis.',
      AnalysisRateLimited: 'AI analysis quota reached. Try again in a few minutes.',
    },

    gOfRPanel: {
      Distance: 'Distance',
      Snapshot: 'Snapshot',
      SnapshotTip:
        "Capture the lab's current atom configuration and add it to the running average. Click multiple times during simulation to build statistics.",
      Clear: 'Clear',
      ClearTip: 'Discard all captured snapshots and the computed g(r).',
      AveragedOver: 'Averaged over',
      Snapshots: 'snapshots',
      NoSnapshotsYet: 'No snapshots yet — click Snapshot to capture one.',
      Atoms: 'atoms',
      RMax: 'r max',
      NoAtoms: 'No atoms in the lab to sample.',
      IdealGasBaseline: 'g = 1 (ideal gas)',
      Limitations:
        'Computed on snapshots, not a time-averaged ensemble — for small systems (≲ 200 atoms) one snapshot is noisy; click Snapshot several times during simulation for smoother g(r). PBC minimum-image distances are used for crystal molecular dynamics; classical molecular dynamics uses direct distances and a bounding-box volume estimate.',
      ExportCSV: 'Export CSV',
      ExportCSVTip: 'Download the computed (r, g(r)) pairs as a CSV file.',
      ExportNothing: 'Nothing to export — take a snapshot first.',
      Analyze: 'Analyze with AI',
      AnalyzeTip:
        'Send the g(r) curve, dominant peaks, and simulation context to an AI assistant for peak assignments, structural interpretation, and suggestions. Requires sign-in.',
      AnalyzeCancel: 'Stop',
      AnalyzeCancelTip: 'Stop the AI analysis in progress. Any text already received is kept.',
      AnalysisEmpty: 'The analyzer returned an empty response. Try again.',
      AnalysisFailed: 'Analysis failed. Check your connection and try again.',
      AnalysisNeedsSignIn: 'Sign in to use AI analysis.',
      AnalysisRateLimited: 'Daily analysis limit reached. Try again tomorrow.',
    },

    msdPanel: {
      LagTime: 'Lag time',
      MSD: 'Mean square displacement',
      Record: 'Record',
      Recording: 'Recording…',
      RecordTip:
        'Toggle per-atom position recording. While on, the integrator pushes unwrapped positions for a representative subset of atoms into a ring buffer. Recording pauses with the simulation.',
      Compute: 'Compute',
      ComputeTip:
        'Compute the time-averaged mean square displacement ⟨|r(t+τ) − r(t)|²⟩ and the self-diffusion coefficient from the slope of MSD vs τ at long lag.',
      Clear: 'Clear',
      ClearTip: 'Discard buffered position samples and the computed MSD.',
      Buffer: 'Buffer',
      Samples: 'samples',
      TrackedAtoms: 'Tracked',
      NoData: 'No samples yet — turn on Record and run the simulation.',
      NeedMoreData: 'Need a longer trajectory before MSD is meaningful (≥ 2 ps suggested).',
      Limitations:
        'Diffusion is estimated by linear fit to MSD vs τ over the tail half of the lag range. Short trajectories give noisy D — run 20+ ps for a stable value. Solids show MSD plateaus; the panel reports D ≈ 0 in that regime. PBC wrap is handled at sample time for crystal molecular dynamics.',
      LogLog: 'Log',
      LogLogTip:
        'Switch both axes to log scale. The slope on log-log gives the power-law exponent α: α≈1 diffusive, α≈0 plateau (solid), α<1 sub-diffusive (glass), α≈2 ballistic.',
      ExportCSV: 'Export CSV',
      ExportCSVTip: 'Download the computed (lag, MSD) pairs as a CSV file.',
      ExportNothing: 'Nothing to export — compute an MSD first.',
      Analyze: 'Analyze with AI',
      AnalyzeTip:
        'Send the MSD curve, diffusion coefficient, and simulation context to an AI assistant for regime classification, caveats, and suggestions. Requires sign-in.',
      AnalyzeCancel: 'Stop',
      AnalyzeCancelTip: 'Stop the AI analysis in progress. Any text already received is kept.',
      AnalysisEmpty: 'The analyzer returned an empty response. Try again.',
      AnalysisFailed: 'Analysis failed. Check your connection and try again.',
      AnalysisNeedsSignIn: 'Sign in to use AI analysis.',
      AnalysisRateLimited: 'Daily analysis limit reached. Try again tomorrow.',
      NoDiffusion: '≈ 0 (solid)',
      RegimeSolid: 'solid',
      RegimeSubdiffusive: 'sub-diffusive',
      RegimeLiquid: 'liquid (diffusive)',
      RegimeBallistic: 'ballistic',
    },

    phononDosPanel: {
      Wavenumber: 'Wavenumber',
      DOS: 'Density of states',
      ArbitraryUnits: 'arb. units',
      Record: 'Record',
      Recording: 'Recording…',
      RecordTip:
        'Toggle per-atom velocity recording. While on, the integrator pushes velocities for a representative subset of atoms (one per element symbol, up to a cap) into a ring buffer. Recording pauses with the simulation.',
      Compute: 'Compute',
      ComputeTip: 'Average per-atom velocity-autocorrelation FFTs into the phonon DOS and update the chart.',
      Clear: 'Clear',
      ClearTip: 'Discard buffered velocity samples and the computed DOS.',
      Buffer: 'Buffer',
      Samples: 'samples',
      TrackedAtoms: 'Tracked',
      NoData: 'No samples yet — turn on Record and run the simulation.',
      NeedMoreData: 'Need a longer trajectory before a DOS is meaningful (≥ 2 ps suggested).',
      NyquistWarning:
        'Sampling limit at this timestep — modes above it fold back to lower wavenumbers. Use a timestep ≤ 4 fs for the full range.',
      ComputedAt: 'Computed at',
      Limitations:
        'Classical molecular-dynamics VDOS reproduces phonon frequencies within ~50–200 cm⁻¹ for most modes. Bonds frozen by SHAKE (e.g. O–H in rigid water) will not appear at all. Intensity is proportional to mode density × velocity² — not directly comparable to IR intensities.',
      LogScale: 'Log scale',
      LogScaleTip: 'Switch the DOS axis between linear and log. Log reveals weak satellite peaks.',
      Zoom: 'Range',
      ZoomIR: '0–4200',
      ZoomPhonons: '0–1000',
      ZoomFull: 'Full',
      ExportCSV: 'Export CSV',
      ExportCSVTip: 'Download the computed (wavenumber, intensity) pairs as a CSV file.',
      ExportNothing: 'Nothing to export — compute a DOS first.',
      Analyze: 'Analyze with AI',
      AnalyzeTip:
        'Send the dominant DOS peaks and your simulation context to an AI assistant for mode assignments, notable absences, caveats, and suggestions. Requires sign-in.',
      AnalyzeCancel: 'Stop',
      AnalyzeCancelTip: 'Stop the AI analysis in progress. Any text already received is kept.',
      AnalysisEmpty: 'The analyzer returned an empty response. Try again.',
      AnalysisFailed: 'Analysis failed. Check your connection and try again.',
      AnalysisNeedsSignIn: 'Sign in to use AI analysis.',
      AnalysisRateLimited: 'Daily analysis limit reached. Try again tomorrow.',
    },

    uvVisSpectrumPanel: {
      Title: 'UV-Vis Spectrum',
      Wavelength: 'Wavelength',
      Absorbance: 'Absorbance (arb. units)',
      PerceivedColor: 'Perceived color',
      Method: 'Method',
      Combined: 'combined',
      Colorless: 'Colorless (absorbs only in the UV)',
      Gap: 'HOMO–LUMO gap',
      PiElectrons: 'π electrons',
      PiCenters: 'π centers',
      Chromophores: 'chromophores',
      Chromophore: 'Chromophore',
      ExportCSV: 'Export CSV',
      ExportCSVTip: 'Download the computed absorbance vs. wavelength as a CSV file.',
      NoMolecule: 'No structure data for this molecule, so its UV-Vis spectrum cannot be computed.',
      NoPiSystem:
        'This molecule has no conjugated π system (no double/triple bonds in a connected network), so it has no visible or near-UV absorption band — it is colorless. Only σ→σ* transitions remain, deep in the vacuum UV.',
      Cumulated:
        'This molecule has a cumulated (allene / CO₂-type) π system — an sp atom with two perpendicular π bonds. Simple Hückel theory assumes one planar π system per atom, so it does not apply here and no spectrum is shown. (Such small molecules are colorless in reality, absorbing only in the deep UV.)',
      Inorganic:
        'This is an all-heteroatom π system with no carbon (O₂, N₂, O₃…). Simple Hückel is parameterized as perturbations of a carbon framework, so it does not apply and no spectrum is shown. O₂ in particular is an open-shell triplet diradical (two unpaired electrons in degenerate π* orbitals) that this closed-shell model cannot represent.',
      NoVisibleBand: 'No allowed π→π* transitions in range — colorless in this model.',
      AiLooking: 'Looking up experimental data…',
      AiNoData: 'The AI does not have reliable UV-Vis data for this molecule.',
      AiNeedsSignIn: 'Sign in to use the AI lookup.',
      AiFailed: 'AI lookup failed — try again or use a computed method.',
      AiCaveat: 'AI literature lookup — a reported experimental value, not a computation.',
      Limitations:
        'Semi-empirical PPP-CIS (Pariser–Parr–Pople SCF with configuration-interaction singles) over the π system. It reliably distinguishes colored from colorless and captures conjugation / auxochrome trends, but absolute band positions are approximate, and symmetry-forbidden or n→π* bands (some carbonyls, high-symmetry PAHs like coronene / C₆₀) are not captured. Not a substitute for TD-DFT.',
    },
    irSpectrumPanel: {
      Title: 'IR Spectrum',
      Wavenumber: 'Wavenumber',
      Intensity: 'Intensity',
      ArbitraryUnits: 'arb. units',
      Record: 'Record',
      Recording: 'Recording…',
      RecordTip:
        'Toggle dipole-current recording. While on, every molecular-dynamics step contributes a J(t) = Σᵢ qᵢ vᵢ(t) sample. Recording pauses with the simulation.',
      Compute: 'Compute',
      ComputeTip: 'Run the autocorrelation FFT on the buffered samples and update the chart.',
      Clear: 'Clear',
      ClearTip: 'Discard buffered samples and the computed spectrum.',
      Buffer: 'Buffer',
      Samples: 'samples',
      NoData: 'No samples yet — turn on Record and run the simulation.',
      NeedMoreData: 'Need a longer trajectory before a spectrum is meaningful (≥ 2 ps suggested).',
      NyquistWarning:
        'Sampling limit at this timestep — modes above it fold back to lower wavenumbers. Use a timestep ≤ 4 fs for the full IR range.',
      NoCharges:
        'All atoms have zero partial charge, so J(t) = Σᵢ qᵢ vᵢ(t) is identically zero. IR-active modes will not show.',
      ComputedAt: 'Computed at',
      Limitations:
        'Classical molecular-dynamics spectra reproduce IR frequencies within ~50–200 cm⁻¹ for most stretches. C–H / O–H / N–H bands are red-shifted ~5–10% (missing zero-point motion). Intensities are semi-quantitative — fixed charges miss bond charge-flux. For ionic crystals the LO–TO splitting is missing (needs Ewald, planned).',
      References: 'References',
      ReferencesTip:
        'Which set of reference guides to overlay on the chart. Pick the set that matches what you are simulating — Organic for hydrocarbons and functional groups, Aqueous for water / ice / aqueous solutions.',
      RefPresetOrganic: 'Organic',
      RefPresetOrganicTip: 'C–H, O–H, C=O, C=C, C–O — the common functional-group stretches.',
      RefPresetAqueous: 'Aqueous',
      RefPresetAqueousTip:
        'O–H stretch, H–O–H bend, librational and translational lattice bands of water / ice. Note: rigid-water models (SHAKE-constrained) suppress the stretch and bend — only libration (~700) and translation (~200) appear in computed spectra.',
      RefPresetNone: 'None',
      RefPresetNoneTip: 'Hide all reference guides.',
      RefHOHBend: 'H–O–H bend',
      RefLibration: 'Libration',
      RefTranslation: 'Translation',
      LogScale: 'Log scale',
      LogScaleTip: 'Switch the intensity axis between linear and log. Log helps reveal weak satellite peaks.',
      Zoom: 'Range',
      ZoomIR: '0–4200',
      ZoomIRTip: '0–4200 cm⁻¹ — the full mid-IR range covering most molecular vibrations.',
      ZoomPhonons: '0–1000',
      ZoomPhononsTip: '0–1000 cm⁻¹ — low-frequency lattice modes, optical phonons, librations.',
      ZoomFull: 'Full',
      ZoomFullTip: '0 to the Nyquist frequency set by the sample stride.',
      ExportCSV: 'Export CSV',
      ExportCSVTip: 'Download the computed (wavenumber, intensity) pairs as a CSV file.',
      ExportNothing: 'Nothing to export — compute a spectrum first.',
      Analyze: 'Analyze with AI',
      AnalyzeTip:
        'Send the dominant peaks and your simulation context to an AI assistant for peak assignments, notable absences, caveats, and suggestions. Requires sign-in.',
      AnalyzeCancel: 'Stop',
      AnalyzeCancelTip: 'Stop the AI analysis in progress. Any text already received is kept.',
      AnalysisEmpty: 'The analyzer returned an empty response. Try again.',
      AnalysisFailed: 'Analysis failed. Check your connection and try again.',
      AnalysisNeedsSignIn: 'Sign in to use AI analysis.',
      AnalysisRateLimited: 'Daily analysis limit reached. Try again tomorrow.',
      RefCHStretch: 'C–H stretch',
      RefOHStretch: 'O–H stretch',
      RefCarbonylStretch: 'C=O stretch',
      RefAromaticCC: 'C=C aromatic',
      RefCOStretch: 'C–O stretch',
    },

    projectListPanel: {
      CopyTitle: 'Copy Title',
      TitleCopiedToClipBoard: 'The title was copied to the clip board',
      SearchByTitle: 'Search by title',
      MyProjects: 'My Projects',
      Trash: 'Trash',
      TrashIsEmpty: 'Trash is empty',
      TrashRetentionExplanation: 'Deleted projects are kept here for {DAYS} days, then permanently removed.',
      PurgesIn: 'Purges in',
      NDays: '{N} days',
      ProjectRestored: 'Project restored',
      ProjectAlreadyPurged: 'This project was already permanently deleted and cannot be restored.',
      DoYouReallyWantToDeleteProjectForever: 'Permanently delete Project',
      DeleteForever: 'Delete forever',
      EmptyTrash: 'Empty Trash',
      DoYouReallyWantToEmptyTrash: 'Permanently delete all projects in Trash?',
      GenerateProjectLink: 'Generate Project Link',
      ProjectLinkGeneratedInClipBoard: 'A link to the project was generated in the clip board',
      DoYouReallyWantToDeleteProject: 'Do you really want to delete Project',
      IfSharedOrPublishedRenamingProjectBreaksExistingLinks:
        'If this project has been shared or published, renaming it breaks existing links',
      ShareToCommons: 'Share to the Commons',
      RemoveFromCommons: 'Remove from the Commons',
      InCommons: 'Shared',
      DoYouReallyWantToShareProject: 'Do you really want to share project',
      DoYouReallyWantToRemoveProjectFromCommons: 'Do you really want to remove project from the Commons',
      ShareProjectExplanation:
        'Anyone visiting the Commons will be able to open this project read-only. Edits you make after sharing will appear immediately to viewers.',
      ProjectShared: 'Project shared to the Commons',
      ProjectRemovedFromCommons: 'Project removed from the Commons',
      CopySharedLink: 'Copy Shared Link',
      SharedLinkCopiedToClipBoard: 'A shareable link to the project was copied to the clip board',
    },

    projectPanel: {
      FilterOptions: 'Filter Options',
      EnableFilters: 'Enable Filters',
      SetMinimumFiltersToPassAllData: 'Set Minimum Filters That Let All Data Pass',
      SecondsSinceGenerationStarted: 'Seconds since generation started',
      IncludeAllMolecules: 'Include all molecules in the Gallery in graph',
      ExcludeAllMolecules: 'Exclude all molecules in the Gallery in graph',
      AutoBond: 'Auto Bond',
      AutoBondTooltip: 'Detect missing bonds from atomic distances and bake them into the structure',
      AutoBondSuccess: 'Bonds rebuilt.',
      AutoBondFailed: 'Could not rebuild bonds.',
      MinimizeEnergy: 'Minimize Energy',
      MinimizeEnergyTooltip: 'Relax the geometry with the MMFF94 force field',
      MinimizeEnergySuccess: 'Structure relaxed (lower-energy geometry).',
      MinimizeEnergySuccessApprox:
        'Structure relaxed. Note: MMFF94 has no parameters for {{subs}}, so each was modeled as its nearest supported element — the geometry around those atoms is approximate.',
      MinimizeEnergySuccessRebonded:
        'Structure relaxed. The original bonds were invalid, so they were rebuilt from the 3D geometry before minimizing — please double-check the connectivity looks right.',
      MinimizeEnergyFailed: 'Could not minimize energy.',
      MinimizeEnergyFailedParse: 'Could not minimize: structure data is invalid.',
      MinimizeEnergyFailedOpenValences:
        'Could not minimize: some atoms are missing hydrogens. Try Add Hydrogens first.',
      MinimizeEnergyFailedDegenerate: 'Could not minimize: two or more atoms occupy the same position.',
      MinimizeEnergyFailedUnsupported:
        'Could not minimize: this molecule contains atoms or bonds the force field does not support.',
      MinimizeEnergyFailedUnsupportedAtom:
        'Could not minimize: atom {{index}} ({{element}}) is not supported by MMFF94.',
      MinimizeEnergyFailedAtomTyping:
        'Could not minimize: atom {{index}} ({{element}}) has an invalid bonding pattern (e.g. too many bonds for this element). The structure is likely malformed — try regenerating it, or run Auto-Bond to rebuild bonds from the geometry.',
      LimitedToInternalDatabase: 'Results limited to molecules from the internal database.',
      AIMemory: 'AI Memory',
      AIMemoryTip:
        'How many of the most recently prompted molecules in this project are replayed to the AI as context when generating a new one. None = each prompt is independent; All = the full history is included (more tokens, slower, more expensive).',
      ShowPrompts: 'Show Prompts',
      SwitchToChatView: 'Switch to chat view',
      SwitchToGridView: 'Switch to grid view',
      SwitchToCompareView: 'Switch to compare view',
      SwitchToListView: 'Switch to list view',
      CompareEmptyState: 'Pick molecules above to compare them side by side.',
      Overlay: 'Overlay',
      OverlayTooltip:
        'Superimpose the two selected molecules in one viewer. One molecule stays fixed (reference); the other is rotated and translated to align with it.',
      OverlayPairFailed: 'failed',
      OverlayMatchedAtoms: 'Matched {{matched}} atoms (of {{refTotal}} / {{mobTotal}} heavy)',
      OverlayRmsd: 'RMSD: {{rmsd}} Å',
      OverlayErrorNoCommonSubstructure:
        'No common substructure found between these two molecules. Overlay only works when one molecule contains the other as a substructure.',
      OverlayErrorTooFewMatchedAtoms: 'Fewer than 3 matched atoms — too few for a unique 3D alignment.',
      OverlayErrorParseFailed: 'Could not parse one of the molecules.',
      Selected: 'Selected',
      GeneratedByAI: 'Generated by AI',
      ClickForMoreInfo: 'Click for more information...',
      ClickToClosePopup: 'Click to close popup',
      MoleculeName: 'Name',
      RenameMoleculeFailed: 'Cannot rename: name is empty or already taken',
      RecentPrompts: 'Recent prompts',
      AIProvider: 'Provider',
      AIProviderTip:
        'Which AI model handles prompts for this project. ● fast chat models answer in one shot and are cheaper; ✦ reasoning models think before answering and handle multi-step or chemically tricky prompts better but are slower and cost more. Used by the gallery chat, the Generate dialog, and Design Ligand.',
      AgentMode: 'Agent mode',
      AgentModeTip:
        'When on, the AI plans multi-step tasks using tools (lookup, molecular dynamics, similarity, edit ops). When off, it tries a public-DB lookup first, then falls back to single-shot molecule generation. Applies to the gallery chat, the Generate dialog, and Design Ligand.',
      PromptEnhance: 'Enhance',
      PromptEnhanceTooltip: 'Ask the AI to rewrite this prompt to be more specific and chemically clearer',
      PromptEnhanceFailed: 'Could not enhance the prompt. Try again.',
      PromptEnhanceQuotaExhausted:
        'The selected AI provider is out of quota. Try a different provider in the dropdown.',
      ShowReasoning: 'Show reasoning',
      ModelReasoning: 'Model reasoning',
      AgentActivity: 'Agent activity',
      WaitingForReasoning: 'Waiting for the model to start thinking…',
      StreamingFallbackNotice: 'Live view paused — your browser stalled the stream; finishing without it…',
      ClarifyPrompt:
        'I\'m not sure what you\'d like me to do with that. You can ask me to **generate** a molecule (e.g. "a fluorinated analogue of aspirin"), **look up** a known compound (e.g. "caffeine"), **run an action** on the scene (e.g. "run MD for 500 steps" or "minimize the energy"), or **ask a chemistry question** (e.g. "why is benzene aromatic?"). Could you rephrase it that way?',
      ClarifyCrystalPrompt:
        'I\'m not sure what you\'d like me to do with that. You can ask me to **generate** a crystal (e.g. "a perovskite with a band gap near 2 eV"), **look up** a known crystal (e.g. "rutile" or "NaCl"), **run an action** on the scene (e.g. "expand the supercell to 2×2×2" or "compute the band structure"), or **ask a materials question** (e.g. "why is silicon a semiconductor?"). Could you rephrase it that way?',
      InterpreterUnavailable:
        "I couldn't interpret that just now — the AI that reads your request is temporarily unavailable (it may be busy, or you may have reached today's limit). I didn't want to guess and build the wrong molecule. If you'd like one, name a specific compound (e.g. \"caffeine\") or open the **Generate Molecule** dialog — or just try again in a moment.",
      InterpreterUnavailableCrystal:
        "I couldn't interpret that just now — the AI that reads your request is temporarily unavailable (it may be busy, or you may have reached today's limit). I didn't want to guess and build the wrong crystal. If you'd like one, name a specific material (e.g. \"NaCl\") or open the **Generate Crystal** dialog — or just try again in a moment.",
      StopGeneration: 'Stop',
      AssistantWorking: 'AI assistant is working — this may take a while',
      SwitchTo2DSketch: 'Switch to 2D sketches',
      SwitchTo3DView: 'Switch to 3D view',
      GalleryMode3D: '3D view (click to switch to 2D)',
      GalleryMode2D: '2D sketches (click to switch to 3D)',
      GenAIPrompt: 'Prompt used to generate this molecule',
      CopyPrompt: 'Copy this prompt',
      CopyResult: 'Copy this result',
      CopyReasoning: 'Copy the model reasoning',
      About: 'About',
      CopyInfo: 'Copy this information',
      InfoInClipBoard: 'The information was copied and is ready to paste.',
      InfoFromInternalDb:
        '{{name}} was loaded from the AIMS built-in molecule library — a curated set of common structures bundled with the app. Its 3D geometry comes directly from the stored record, not from an AI model.',
      InfoFromPublicDb:
        '{{name}} was retrieved from a public chemistry database ({{source}}). The structure was downloaded and prepared for display, not generated by an AI model.',
      InfoInterpretedBy: 'The prompt was interpreted by {{model}}.',
      InternalDbNoExplanation: 'No AI explanation — this structure was loaded from the internal database.',
      YouMayEditDataAbove: 'You may edit the data above.',
      AddHydrogens: 'Add Hydrogens',
      AddHydrogensTooltip: 'Add explicit hydrogen atoms with 3D positions',
      MirrorMolecule: 'Mirror Molecule',
      MirrorMoleculeTooltip: 'Reflect every atom through the xz-plane — flips chirality at every stereocenter',
      AlignPrincipalAxes: 'Align to Principal Axes',
      AlignPrincipalAxesTooltip:
        'Rotate the molecule so its longest axis points along world X and shortest along world Z (useful for comparing two molecules side by side)',
      Export3DPrintable: 'Export 3D-Printable Model',
      Export3DPrintableTooltip: 'Export this molecule as a 3D-printable file (STL / PLY / OBJ / glTF)',
      PredictColor: 'Predict Color (UV-Vis)',
      PredictColorTooltip:
        'Compute this molecule’s UV-Vis (visible/near-UV) electronic absorption spectrum by simple Hückel π-MO theory and show its predicted color. A per-molecule property — qualitative, captures conjugation/color trends but not exact band positions.',
      DuplicateMolecule: 'Duplicate',
      CoarseGrain: 'Coarse-grain…',
      CoarseGrainTitle: 'Coarse-grain {{name}}',
      CoarseGrainNote:
        'Reduce this molecule to beads. The original card stays; a new card is added next to it and names this one as its parent.',
      MappingUnitedAtom: 'United atom — one bead per heavy atom, hydrogens folded in (real units)',
      MappingEllipsoid: 'Ellipsoid — the whole molecule as one Gay-Berne rod (real units)',
      MappingHeadTail: 'Head and tail — about four heavy atoms per bead, heteroatoms make the head (reduced units)',
      CoarseGrainPreview: 'Result',
      CoarseGrainNotReady: 'The structure is still loading…',
      CoarseGrainAdded: 'Added {{name}}: {{composition}}',
      CoarseGrainSelectFirst: 'Select a molecule card first, or type /coarse-grain <name>.',
      CreateCard: 'Create card',
      BeadTable: 'Bead types',
      BeadTableGoNote:
        'This is a Gō model: ε here sets only how hard its residues repel. What holds the fold is the well of each native contact, {eps} K, which this table does not change. Build the model again with a new depth to change it.',
      BeadTableNote:
        'Edits apply to this card. Copies already in the lab keep the model they were loaded with until loaded again.',
      SlashCoarseGrainDescription:
        'Coarse-grain the selected molecule into beads (/coarse-grain <name> for another card)',
      SlashBuildDescription: 'Open Build Chain: an all-atom chain or a chain of beads (Polymer Builder)',
      SlashBuildPolymerOnly: 'Build Chain is in the Polymer Builder project type.',
      Export3DFailed: 'Could not prepare this molecule for export.',
      AddHydrogensSuccess: 'Hydrogen atoms added.',
      AddHydrogensFailed: 'Could not add hydrogens (parse failed).',
      AssignPartialCharges: 'Assign Partial Charges',
      AssignPartialChargesTooltip: 'Compute Gasteiger partial charges for each atom',
      AssignPartialChargesSuccess: 'Partial charges assigned.',
      AssignPartialChargesFailed: 'Could not assign partial charges.',
      PromptInClipBoard: 'The prompt was copied and is ready to paste.',
      ResultInClipBoard: 'The result was copied and is ready to paste.',
      ReasoningInClipBoard: 'The reasoning was copied and is ready to paste.',
      SmilesInClipBoard: 'SMILES was copied and is ready to paste.',
      InChIInClipBoard: 'InChI was copied and is ready to paste.',
      StructureData: 'Molecular structure data in the SDF format',
      NoMolecule: 'No molecule',
      Project: 'Project',
      ProjectType: 'Type',
      ProjectTitle: 'Project Title',
      TitleAlreadyExists: 'A project with this title already exists — choose a different one',
      ClickToRenameProject: 'Click to rename this project',
      ProjectDescription: 'Description',
      ProjectSettings: 'Project Settings',
      GallerySettings: 'Gallery Settings',
      DisplayWindowSize: 'Display Window Size',
      OutputSelectedMoleculeToTest: 'Output selected molecule to test',
      ToggleDragAndDropMoleculeMode:
        'To add a molecule to the lab on the right, click this button\nand then drag-and-drop it to the X-Y, Y-Z, or X-Z plane.',
      NumberOfColumns: 'Columns',
      NumberOfColumnsTip: 'How many columns the molecule gallery uses to lay out its tiles.',
      FindMoleculesMostSimilarToSelectedOneToImportIntoGallery:
        'Find molecules in the internal database most similar to the selected one to import into gallery',
      FindSimilarViaAI: 'Ask the AI to generate a structural analogue of the selected molecule',
      FindSimilarViaAIConfirm:
        'This will use one AI generation request. Continue and ask the model for a structural analogue?',
      MostSimilarMolecules: 'Molecules Most Similar to',
      Descriptor: 'Descriptor',
      LevenshteinDistance: 'Levenshtein Distance',
      SelectMoleculeToImportIntoGallery: 'Select a molecule from the internal dabase to import into gallery',
      SelectMoleculeFromInternalDatabase: 'Select a Molecule from the Internal Database',
      GenerateMolecule: 'Generate a molecule using AI',
      WhatMoleculeDoYouWant: 'What molecule do you want?',
      AskOrDescribeMolecule: 'Ask a question or describe a molecule…',
      PersonaTooltip: 'Choose the role the AI plays in this chat.',
      PersonaAssistant: 'Assistant',
      PersonaAssistantDesc: 'Neutral helper — does what you ask with the full toolset.',
      PersonaTeacher: 'Teacher',
      PersonaTeacherDesc: 'Explains the why and guides you to the answer.',
      PersonaDesigner: 'Designer',
      PersonaDesignerDesc: 'Proposes and builds structures, offers variations.',
      PersonaAnalyst: 'Analyst',
      PersonaAnalystDesc: 'Read-only: measures and characterizes what is already loaded.',
      PersonaAnalystReadOnlyNotice:
        'Analyst mode is read-only — it characterizes what is already loaded. Switch to Designer or Assistant to create or modify structures.',
      AskAboutProtein: 'Ask about this protein — structure, folding, function…',
      AskAboutDnaRna: 'Ask about this DNA/RNA — base pairing, transcription, translation…',
      AskAboutNamedProtein: 'Ask about {{name}} — structure, folding, function…',
      AskAboutNamedDnaRna: 'Ask about {{name}} — base pairing, transcription, translation…',
      AskAboutProteinEmpty: 'Load a protein by PDB ID (e.g. 1CRN) or fold a sequence — or just ask…',
      AskAboutDnaRnaEmpty: 'Build DNA/RNA from a sequence — or just ask about genes…',
      BioChatFollowUp: 'Ask a follow-up…',
      BioChatEmptyProtein: 'Ask a question about the loaded protein, or proteins in general.',
      BioChatEmptyDnaRna: 'Ask about DNA, RNA, and how genes become proteins.',
      BioAnalysisComposition: 'Composition',
      BioAnalysisConfidence: 'Confidence',
      BioAnalysisPlddt: 'pLDDT',
      BioAnalysisBFactor: 'B-factor',
      BioAnalysisMean: 'mean',
      BioAnalysisChains: 'chains',
      BioAnalysisResidues: 'residues',
      BioAnalysisBases: 'bases',
      BioAnalysisAtoms: 'atoms',
      BioAnalysisGc: 'GC',
      BioAnalysisNoStructure: 'Load a structure to see its analysis.',
      BioGalleryEmptyProtein: 'No proteins yet — add one with the beaker (PDB ID or sequence).',
      BioGalleryEmptyDnaRna: 'No DNA/RNA yet — add one with the beaker (PDB ID or sequence).',
      BioThumbnailPending: 'Open to preview',
      BioInspectTool: 'Inspect & measure — click atoms',
      BioInspectHint: 'Click atoms in the structure: 2 = distance, 3 = angle, 4 = dihedral.',
      BioOverlayDisulfides: 'Disulfide bonds',
      BioOverlaySaltBridges: 'Salt bridges',
      BioOverlayHbonds: 'Hydrogen bonds',
      BioClearPicks: 'Clear',
      BioCompareTool: 'Compare states',
      BioCompareHint: 'Overlay a second structure aligned onto this one (e.g. oxy vs deoxy, wild-type vs mutant).',
      BioCompareWith: 'Compare with',
      BioCompareChoose: 'Choose a structure',
      BioCompareAlignOn: 'Align on',
      BioCompareWhole: 'Whole structure',
      BioCompareChain: 'Chain',
      BioCompareRmsd: 'RMSD',
      BioMorphPlay: 'Play / pause morph',
      BioMorphHint: 'Drag to scrub between the two states; play to animate the transition.',
      BioCompareColorByDev: 'Color by displacement',
      BioCompareTopMovers: 'Top movers',
      BioSimTool: 'Simulate region (partial MD)',
      BioSimHint:
        'Pick a region to simulate: a sphere appears in the 3-D view — drag it to the site and resize it (orange handle or the slider). Quick-place it at a pocket, a bound cofactor/ligand, or the structure center below. A separate biomolecular MD engine relaxes the atoms inside while the surrounding shell is held fixed. Reduced force field — illustrative local dynamics, not quantitative.',
      BioSimSeedFrom: 'Place the region at a site',
      BioSimRadius: 'Region radius',
      BioSimSeedLigand: 'Around the ligand',
      BioSimIncludeLigand: 'Include the ligand (with hydrogens)',
      BioSimSeeds: 'Region around',
      BioSimPlaceRegion: 'Select region',
      BioSimRecenterRegion: 'Recenter region',
      BioSimPlaceRegionHint:
        'Put a region sphere on the structure, then drag it (move gizmo) and resize it (orange handle or the slider) in the 3-D view to cover the site you want to simulate.',
      BioSimCustomRegion: 'Custom region',
      BioSimClearRegion: 'Clear region',
      BioSimTemperature: 'Temperature',
      BioSimStart: 'Simulate',
      BioSimPause: 'Pause',
      BioSimResume: 'Resume',
      BioSimStop: 'Stop',
      BioSimMobile: 'mobile',
      BioSimStep: 'step',
      BioSimTime: 'Time',
      BioSimEnergy: 'Potential energy',
      BioSimKE: 'Kinetic energy',
      BioSimTotalE: 'Total energy',
      BioSimMinimize: 'Energy minimize — relax the region before running (removes built-in strain)',
      BioSimMinimizing: 'Minimizing…',
      BioSimSolvent: 'Implicit solvent — click to switch H₂O (screened electrostatics) ↔ vacuum',
      BioSimSettings: 'Experimental settings',
      BioSimSolventLabel: 'Implicit solvent',
      BioSimSolventHint: 'Distance-dependent dielectric screens electrostatics, vs vacuum.',
      BioSimMetalWarning:
        '{{elements}} (metals / cofactors) are not in the simulation force field — they are dropped, so binding and metal coordination are NOT modeled. The region MD relaxes the protein/nucleic scaffold only.',
      BioLigandTool: 'Add a ligand (binding study)',
      BioLigandHint:
        'Add a small molecule (substrate / inhibitor / cofactor / gas) and study how it interacts with the structure. Type a name or a description — looked up in the built-in library and PubChem first, then AI-generated de novo if it is not a known compound.',
      BioLigandPlaceholder: 'name or description, e.g. citrate',
      BioLigandAdd: 'Add',
      BioLigandRemove: 'Remove',
      BioLigandDock: 'Dock',
      BioLigandPlacePicks: 'Move to picked site',
      BioLigandPlaceCenter: 'Center on structure',
      BioLigandNotFound: 'Could not find or generate molecule',
      BioLigandGenerating: 'Not in databases — generating with AI',
      BioLigandScore: 'Interaction score',
      BioLigandContacts: 'contacts',
      BioLigandHbonds: 'H-bonds',
      BioLigandClashes: 'clashes',
      BioLigandReportHbonds: 'H-bond partners',
      BioLigandReportContacts: 'Key contacts',
      BioLigandReportClashes: 'Clashing residues',
      BioLigandNote:
        'Reduced classical score (van der Waals + electrostatics + H-bonds + clashes; ligand Gasteiger charges) — illustrative binding/fit, not affinity or catalysis. Drag or rotate the ligand with the on-canvas gizmo; pick a site first to drop it there.',
      BioLigandTarget: 'Target site (where to place it)',
      BioLigandBoundSite: 'Bound site',
      BioLigandReplaceSite: 'Replace the bound ligand',
      BioLigandBesideSite: 'Beside the bound cofactor',
      BioLigandFindPockets: 'Find pockets',
      BioLigandPocket: 'Pocket',
      BioLigandNoPockets: 'No clear pockets found',
      BioSimTooLarge: 'Region too large — pick fewer residues or a smaller site.',
      BioSimNoSeed: 'Inspect-pick atom(s) in the region first.',
      BioLoad: 'Study in Lab',
      BioLoadIntoLab: 'Study this structure in the lab',
      BioInLab: 'In lab',
      BioGalleryPurpose: 'Characterization',
      BioCompareStructure: 'Structure',
      BioChart_metrics: 'Metrics',
      BioChart_composition: 'Composition',
      BioChart_similarity: 'Similarity',
      BioChart_profiles: 'Profiles',
      BioChart_ramachandran: 'Ramachandran',
      BioChart_contacts: 'Contact Map',
      BioChart_interactions: 'Interactions',
      BioChart_properties: 'Properties',
      BioChart_sasa: 'SASA Profile',
      BioChart_flexibility: 'Flexibility (GNM)',
      BioChart_correlations: 'Dynamic Correlations',
      BioChart_hotspots: 'Hot Spots (Alanine Scan)',
      BioChart_compare: 'Compare to Parent',
      BioHotSpotCount: 'hot spots',
      BioTopHotSpot: 'top hot spot',
      BioColorByDdg: 'Color by ΔΔG',
      BioChartHelp_metrics:
        'One bar per structure for a chosen scalar metric (residues, chains, atoms, GC%, or mean confidence) — compares the whole curated set at a glance.',
      BioChartHelp_composition:
        'Grouped amino-acid (or base) frequencies across the structures, so you can see how their building-block makeup differs.',
      BioChartHelp_similarity:
        'Alignment-free k-mer cosine similarity heatmap between every pair of sequences (0–100%) — clusters related structures without a full alignment.',
      BioChartHelp_profiles:
        'Per-residue pLDDT / B-factor confidence overlaid across structures, revealing which regions are well- or poorly-determined.',
      BioChartHelp_ramachandran:
        'φ/ψ backbone dihedral scatter for the selected structure, with the α-helix and β-sheet basins shaded. Points outside the basins are geometry outliers.',
      BioChartHelp_contacts:
        'Cα–Cα distance matrix for the selected structure: dark cells are residue pairs that are close in space, so off-diagonal patches reveal contacts and folds.',
      BioChartHelp_interactions:
        'Typed stabilizing interactions in the selected structure: disulfide bonds (Cys–Cys), salt bridges (acidic↔basic sidechains), and hydrogen bonds. H-bonds are approximate (heavy-atom distance, no explicit H).',
      BioChartHelp_properties:
        "ProtParam-style physicochemical properties from the selected structure's sequence: molecular weight, isoelectric point (pI), net charge at pH 7, GRAVY hydropathy, aromaticity, and the 280 nm extinction coefficient.",
      BioChartHelp_sasa:
        'Per-residue solvent-accessible surface area (Shrake–Rupley) for the selected structure — high values are exposed residues, low values are buried in the core.',
      BioChartHelp_flexibility:
        'Gaussian Network Model prediction of per-residue mobility for the selected structure, compared to experimental B-factors. The r value rates the agreement.',
      BioChartHelp_correlations:
        'Dynamic cross-correlation map from the GNM: red pairs of residues move together, blue pairs move oppositely — exposes coupled domains and hinges.',
      BioChartHelp_compare:
        'Rigid Cα superposition of the selected structure onto its parent, plotting per-residue deviation. The RMSD summarizes how far the mutant drifted.',
      BioChartHelp_hotspots:
        'Computational alanine scan: per-residue estimate of how much mutating each residue to alanine would destabilize the fold (sidechain burial + H-bonds + salt bridges + disulfides). A heuristic ranking of hot spots, not a force-field ΔΔG. "Color by ΔΔG" paints the structure (hot = hot spot).',
      BioChartHelp: 'About this chart',
      BioBfactorCorr: 'B-factor r',
      BioHinges: 'hinges',
      BioMostFlexible: 'most flexible',
      BioPredictedFlex: 'Predicted (GNM)',
      BioExperimentalB: 'Experimental B',
      BioAnimateMotion: 'Animate',
      BioStopMotion: 'Stop',
      BioColorByMobility: 'Color by mobility',
      BioResetColor: 'Reset color',
      BioAnimTooLarge: 'Too large to animate ({{n}} residues; limit {{limit}}). Flexibility analysis still works.',
      BioChartTopN: 'first {{n}}',
      BioChartCount: '{{n}} structures',
      BioSsHelix: 'Helix',
      BioSsSheet: 'Sheet',
      BioRg: 'Rg',
      BioRamaOutliers: 'Rama. outliers',
      BioTotalSasa: 'Total SASA',
      BioDisulfides: 'Disulfides',
      BioSaltBridges: 'Salt bridges',
      BioHbonds: 'H-bonds',
      BioInterChain: 'inter-chain',
      BioNone: 'none',
      BioNoInteractions: 'No disulfides, salt bridges, or hydrogen bonds detected.',
      BioHbondApprox: 'H-bonds are approximate (heavy-atom distance, no explicit H/angle).',
      BioSeqLength: 'Residues',
      BioMW: 'Mol. weight',
      BioPI: 'pI',
      BioNetCharge: 'Charge @ pH 7',
      BioGravy: 'GRAVY',
      BioHydrophilic: 'hydrophilic',
      BioHydrophobic: 'hydrophobic',
      BioAromaticity: 'Aromaticity',
      BioExtinction: 'ε₂₈₀ (red. / cystine)',
      BioAcidic: 'Acidic',
      BioBasic: 'Basic',
      BioAromatic: 'Aromatic',
      BioExtinctionNote: 'ε at 280 nm: reduced (free Cys) / all Cys paired into cystines.',
      BioNoBackbone: 'No protein backbone to analyze.',
      BioRmsd: 'Cα RMSD',
      BioCompared: 'residues',
      BioLengthMismatch: 'different lengths — aligned the shorter',
      BioNoParent: 'No parent structure to compare against.',
      ShiftEnterForNewLine: 'Shift+Enter for new line',
      SlashClearDescription: 'Clear the conversation',
      SlashHelpDescription: 'Show available commands',
      SlashHelpIntro:
        'You can just chat naturally — describe what you want, ask a question, or request an action. A few slash-commands are also available:',
      SlashConversationCleared: 'Conversation cleared',
      ClearConversation: 'Clear conversation',
      ClearConversationConfirm:
        'Remove every message in this chat, along with the prompt bubbles on the cards and the assistant’s memory of the conversation? The crystals and molecules themselves stay. You can undo this.',
      SlashNothingToClear: 'The conversation is already empty',
      SlashQuotaDescription: 'Show remaining daily AI usage',
      SlashQuotaChecking: 'Checking your remaining AI usage…',
      SlashQuotaUnlimited: 'This account has unlimited AI usage (no daily cap).',
      SlashQuotaResult: 'You have **{{remaining}}** of {{limit}} daily AI requests left.',
      SlashQuotaResets: 'Resets {{time}}.',
      SlashQuotaBoost: 'Includes a {{multiplier}}× promotion boost through {{date}}.',
      SlashReportDescription: 'Export the conversation as a PDF lab report',
      SlashReportEmpty: 'There is no conversation to export yet.',
      SlashReportBlocked: 'The report window was blocked. Allow pop-ups for this site, then run /report again.',
      SlashReportCapturing: 'Capturing {{name}}…',
      SlashReportRendering: 'Rendering {{name}}…',
      SlashReportTourStop: 'Photographing tour stop {{name}}…',
      SlashReportAssembling: 'Assembling the report…',
      SlashQuotaFailed: 'Could not read your usage right now — please try again in a moment.',
      SlashPromoDescription: 'Redeem a promotion code that raises your daily AI limit',
      SlashPromoHowTo:
        'If you have a promotion code, paste it here on its own (or type /promo followed by the code) and I will apply it to your account.',
      SlashPromoChecking: 'Checking that promotion code…',
      SlashPromoRedeemed:
        '🎉 Promotion code accepted! Your daily AI limit is now **{{limit}}** ({{multiplier}}×) through {{date}}.',
      SlashPromoAlready:
        'You have already redeemed that promotion code. Your daily limit is {{limit}} through {{date}}.',
      SlashPromoUnknown: 'That promotion code was not recognized. Please check the spelling and try again.',
      SlashPromoInactive: 'That promotion code is no longer available.',
      SlashPromoExhausted: 'That promotion code has been fully redeemed.',
      SlashPromoThrottled: 'Too many promotion code attempts today. Please try again tomorrow.',
      SlashPromoFailed: 'Could not check that promotion code right now — please try again in a moment.',
      Listening: 'Listening...',
      SelectMolecule: 'Select a Molecule',
      TypeToSearch: 'Type in the above field to search for a molecule',
      MoleculeType: 'Molecule Type',
      MolecularName: 'Molecular Name',
      MoleculeAlreadyAdded: 'Molecule already added',
      MoleculeAlreadyInGallery:
        'is already in your gallery — I selected it for you. Ask for another copy if you really want a duplicate, or say "load it into the lab" to simulate it.',
      MoleculeNotFound: 'Molecule not found',
      RemoveSelectedMoleculeFromGallery: 'Remove selected molecule from gallery',
      RemoveSelectedStructuresFromGallery: 'Remove selected structures from gallery',
      RemoveAllMoleculesFromGallery: 'Remove all molecules from gallery',
      MakeDescriptionEditable: 'Make this description editable',
      MakeDescriptionNonEditable: 'Make this description non-editable',
      DoubleClickToMakeDescriptionEditable: 'Double-click to make this description editable',
      ClickToFlipSortingOrder: 'Click to flip the sorting order',
      WriteABriefDescriptionAboutThisProject: 'Write a brief description about this project.',
      Properties: 'Properties',
      ChooseProperties: 'Choose properties',
      ChooseDataColoring: 'Choose data coloring',
      SameColorForAllMolecules: 'Same color for all molecules',
      OneColorForEachMolecule: 'One color for each molecule',
      OneColorForEachGroup: 'One color for each group',
      GraphScreenshot: 'Take a screenshot of the graph',
      AutoscaleGraph: 'Autoscale graph',
      ChemicalElements: 'Chemical Elements',
      ChemicalElementColorKeys: 'Color Keys',
      ChemicalComposition: 'Chemical Composition',
      Gravity: 'Gravity',
      GravityViewerCoordinateSystem: 'Gravity always points downward on the screen.',
      GravityModelCoordinateSystem:
        "Gravity always points to the negative z direction in the model's coordinate system.",
      GravityNote: 'For the effect to manifest in molecular models, gravity is greatly exaggerated.',
      AtomCount: 'Atom Count',
      TotalNumberOfAtomsInModel:
        'The total number of atoms in the model. If exceeding 500, the model may run slowly. The text will turn red as a reminder.',
      ParticleCount: 'Particle Count',
      TotalNumberOfParticlesInModel:
        'The total number of particles (beads or rods) in the model. If exceeding 500, the model may run slowly. The text will turn red as a reminder.',
      BondCount: 'Bond Count',
      RadialBondCount: 'Radial Bond Count',
      AngularBondCount: 'Angular Bond Count',
      TorsionalBondCount: 'Torsional Bond Count',
      ResidueCount: 'Residue Count',
      ChainCount: 'Chain Count',
      StructureCount: 'Structure Count',
      MoleculeCount: 'Molecule Count',
      MolecularMass: 'Mass',
      PolarSurfaceArea: 'Polar Surface Area',
      PolarSurfaceAreaShort: 'Polar Surface',
      HydrogenBondDonorCount: 'Hydrogen Bond Donor Count',
      HydrogenBondDonorCountShort: 'HB Donors',
      HydrogenBondAcceptorCount: 'Hydrogen Bond Acceptor Count',
      HydrogenBondAcceptorCountShort: 'HB Acceptors',
      RotatableBondCount: 'Rotatable Bond Count',
      RotatableBondCountShort: 'Rot Bonds',
      HeavyAtomCount: 'Heavy Atom Count',
      HeavyAtomCountShort: 'Heavy Atoms',
      Complexity: 'Complexity',
      Density: 'Density',
      BoilingPoint: 'Boiling Point',
      MeltingPoint: 'Melting Point',
      StructureDescriptors: 'Structure',
      RingCount: 'Rings',
      AromaticRingCount: 'Aromatic Rings',
      AromaticRingCountShort: 'Ar Rings',
      Fsp3: 'Fsp³',
      QED: 'QED',
      LabelType: 'Label Type',
      LabelTypeTip: 'What text is shown under each gallery tile — molecule name or chemical formula.',
      MolecularFormula: 'Molecular Formula',
      GraphType: 'Graph Type',
      ParallelCoordinates: 'Parallel Coordinates',
      Relationship: 'Relationship',
      ScatterPlot: 'Scatter Plot',
      PCAProjection: 'PCA Projection',
      PCAVarianceCovered: 'variance covered',
      ColorByCluster: 'Clusters',
      ClusterLegendSingleton: 'singleton / unparseable',
      PCALoadingsTitle: 'Top property contributions per PC',
      Histogram: 'Histogram',
      HistogramBins: 'Bins',
      HistogramCount: 'Count',
      RadarChart: 'Radar Chart',
      RadarLipinskiHint: 'normalized at the Lipinski/Veber thresholds',
      RadarLipinskiThreshold: 'Lipinski threshold',
      RadarNoData: 'No items with complete values for the visible properties.',
      RadarNoCrystalData: 'No crystals with complete values for the visible properties.',
      RadarTooFewAxes: 'Pick at least 3 properties in the Choose Properties popover to render a radar.',
      RadarCompareColors: 'Compare colors',
      RadarCompareColorsTooltip:
        'Color every molecule distinctly instead of graying them out. Useful when comparing a small set side-by-side; gets cluttered past ~6 molecules.',
      LipinskiAboutTitle: 'About the Lipinski / Veber threshold',
      LipinskiWhat:
        'The dashed orange polygon marks the combined Lipinski Rule-of-Five and Veber thresholds. A molecule whose six axes all stay inside this polygon is considered drug-like by the classical orally-bioavailable filter.',
      LipinskiThresholds: 'Thresholds: MW ≤ 500 Da, logP ≤ 5, PSA ≤ 140 Å², HBD ≤ 5, HBA ≤ 10, rotatable bonds ≤ 10.',
      LipinskiNormalization:
        'Each axis is normalized so its threshold sits at radius 1 — any vertex past radius 1 means that property exceeds the rule on that axis.',
      BubbleChart: 'Bubble Chart',
      BubbleSizeBy: 'Size by',
      MoleculesAtSamePosition: 'molecules at the same position',
      CorrelationHeatmap: 'Correlation Heatmaps',
      HeatmapPearson: 'Pearson correlation',
      HeatmapDistance: 'Distance correlation',
      HeatmapNotEnoughData: 'Not enough variable properties to build a correlation heatmap.',
      HeatmapDroppedPrefix: 'Dropped from heatmap',
      HeatmapDroppedReasonTooFew: 'only {{count}} value{{plural}}; need ≥ 3',
      HeatmapDroppedReasonConstant: 'constant value',
      PearsonAboutTitle: 'About Pearson correlation',
      PearsonWhat:
        'Pearson r measures the strength of a linear relationship between two properties — how well their pairwise values fit a straight line.',
      PearsonRange:
        'Range: −1 (perfect negative slope, blue) to +1 (perfect positive slope, red); 0 (white) means no linear relationship.',
      PearsonCaveat:
        'Pearson misses non-linear dependencies — a U-shaped relationship can score r ≈ 0 even when the variables are strongly related. Check the distance-correlation panel for those.',
      DistanceCorrAboutTitle: 'About distance correlation',
      DistanceCorrWhat:
        'Distance correlation (Székely & Rizzo, 2007) captures any statistical dependency between two properties, not only linear or monotonic ones.',
      DistanceCorrRange:
        'Range: 0 to 1. 0 (white) means the variables are independent (asymptotically); 1 (purple) means perfectly dependent. It is unsigned — direction is not encoded.',
      DistanceCorrUsage:
        'Use this panel to surface relationships that Pearson misses, such as quadratic, threshold, or U-shaped effects between properties.',
      GraphCategoryProperties: 'Properties',
      GraphCategoryStructures: 'Structures',
      SimilarityHeatmap: 'Similarity Heatmap',
      EnlargeGraph: 'Enlarge graph in a popup',
      CopolymerTgCard: 'Copolymer Tg (Fox equation)',
      CopolymerTgLast: 'Tg',
      BandStructureCard: 'Band structure',
      EnergyLevelsCard: 'Energy levels',
      BandStructureGap: 'band gap',
      BandStructureReference: 'experimental estimate',
      ShowFinalState: 'Show final state',
      FinalStateShown: 'Final state loaded into the lab ({{label}}). Press Reset to return to the start.',
      FinalStateFailed: 'Could not load the final state into the lab.',
      LabStateKept: 'Lab state kept',
      LoadLabState: 'Load this state',
      LabStateLoaded: 'Lab state loaded ({{label}}). Press Reset to return to the saved start.',
      LabStateFailed: 'Could not load this state into the lab.',
      SequenceCard: 'Primary structure',
      SequenceChain: 'Chain {{id}}',
      SequenceResidues: '{{n}} residues',
      SequenceBases: '{{n}} bases',
      SequenceLiveHint: 'Hover a residue to mark it in 3-D; click to frame the camera on it.',
      SequenceNotShown: 'Select this structure in the gallery to interact with its sequence.',
      SequenceSsLegend: 'Secondary structure: helix · strand · coil',
      SequenceSsFromCartoon: 'as drawn in the cartoon',
      SequenceSsFromRecords: 'from the PDB HELIX/SHEET records',
      SequenceSsFromPhiPsi: 'from backbone φ/ψ angles',
      TourCard: 'Structure tour',
      TourBack: 'Back',
      TourNext: 'Next',
      TourRestart: 'Restart',
      TourNotShown: 'Select this structure in the gallery to replay the tour.',
      TourIncludeInReport: 'Include this stop in my lab report',
      // Tour step templates (src/project/tourText.ts). Counts use i18next plurals.
      TourStepOverview: 'Overview',
      TourStepPrimary: 'Primary structure',
      TourStepSecondary: 'Secondary structure',
      TourStepHelix: 'Helix {{n}}',
      TourStepStrand: 'Strand {{n}}',
      TourStepElementChain: '{{label}} ({{chain}})',
      TourStepTertiary: 'Tertiary structure',
      TourStepCore: 'Hydrophobic core',
      TourStepDisulfides: 'Disulfide bonds',
      TourStepActiveSite: 'Active site',
      TourStepBindingSite: '{{ligand}} binding site',
      TourStepDomain: 'Domain: {{name}}',
      TourStepPocket: '{{name}} pocket',
      TourStepBound: 'Bound {{code}}',
      TourStepQuaternary: 'Quaternary structure',
      TourStepExploded: 'Exploded assembly',
      TourStepReassembled: 'Reassembled',
      TourStepEnd: 'End of tour',
      TourChains_one: '{{count}} chain',
      TourChains_other: '{{count}} chains',
      TourResidues_one: '{{count}} residue',
      TourResidues_other: '{{count}} residues',
      TourAminoAcids_one: '{{count}} amino acid',
      TourAminoAcids_other: '{{count}} amino acids',
      TourHelices_one: '{{count}} helix',
      TourHelices_other: '{{count}} helices',
      TourStrands_one: '{{count}} strand',
      TourStrands_other: '{{count}} strands',
      TourSubunits_one: '{{count}} subunit',
      TourSubunits_other: '{{count}} subunits',
      TourApolarResidues_one: '{{count}} apolar residue',
      TourApolarResidues_other: '{{count}} apolar residues',
      TourDisulfideBonds_one: '{{count}} disulfide bond',
      TourDisulfideBonds_other: '{{count}} disulfide bonds',
      TourCopies_one: ' ({{count}} copy)',
      TourCopies_other: ' ({{count}} copies)',
      TourIfaceItem_one: '{{a}}–{{b}} ({{count}} contact)',
      TourIfaceItem_other: '{{a}}–{{b}} ({{count}} contacts)',
      TourPocketLining_one:
        ' {{count}} residue lines its pocket within 4 Å{{firstCopy}}: {{pocket}}{{more}} — the contact that holds it in place.',
      TourPocketLining_other:
        ' {{count}} residues line its pocket within 4 Å{{firstCopy}}: {{pocket}}{{more}} — the contacts that hold it in place.',
      TourChainLabel: 'chain {{id}}',
      TourTheChain: 'the chain',
      TourListSeparator: ', ',
      TourRunSeparator: '; ',
      TourMore: ', …',
      TourOverviewTitle: '{{title}}. ',
      TourOverviewText: '{{chains}}, {{residues}} in total.',
      TourOverviewBound: ' Bound groups: {{groups}}.',
      TourPrimaryRun: '{{chain}} runs from residue {{first}} (N-terminus) to {{last}} (C-terminus)',
      TourPrimaryText: 'The sequence of {{residues}}: {{runs}}. The sequence card lists every residue.',
      TourSecondaryText:
        '{{helixPct}}% helix, {{sheetPct}}% sheet, {{coilPct}}% coil: {{helices}} and {{strands}}. Helices and strands are colored on the cartoon and drawn above the sequence.',
      TourElementText: '{{label}}: residues {{spec}} ({{length}}){{on}}.',
      TourElementOn: ' on {{chain}}',
      TourTertiaryRg: ' — radius of gyration {{rg}} Å',
      TourTertiaryText:
        'The whole chain folded into its 3-D shape{{rg}}. Secondary-structure elements pack against each other; the next stops show what stabilizes the fold and where it binds.',
      TourCompItem: '{{n}} {{res}}',
      TourCoreText:
        "{{count}} ({{comp}}) are buried away from water — {{share}}% of the protein's hydrophobic side chains, mean relative solvent accessibility {{meanPct}}%. Packing them together, out of water, is what drives the chain to fold. Most buried: {{most}}{{more}}.",
      TourDisulfidesText: '{{count}} cross-link cysteine pairs: {{pairs}}{{more}}.',
      TourActiveSiteText: 'Catalytic residues (UniProt): {{sites}}.',
      TourBindingSiteText: 'Residues that bind {{ligand}} (UniProt): {{specs}}.',
      TourDomainText: '{{name}} domain: residues {{specs}} (UniProt).',
      TourPocketText: '{{what}}{{copies}} is bound at {{where}}.',
      TourPocketFirstCopy: ' (first copy)',
      TourPocketNone: ' Nothing in the chain comes within 4 Å of it.',
      TourBoundText: '{{code}}{{copies}} is bound in the structure at {{where}}.',
      TourSubunitItem: '{{id}} ({{residues}})',
      TourQuaternaryText: '{{subunits}}: {{list}}.',
      TourQuaternaryIfaces: ' Strongest interfaces: {{ifaces}}.',
      TourExplodedText:
        'The subunits pulled apart along their centroid directions — each one is a complete folded chain; the interfaces between them hold the assembly together.',
      TourReassembledText: 'The subunits back in their native arrangement.',
      TourEndText: 'The view is restored to how it was before the tour. Ask about any stop to go deeper.',
      TourStepConserved: 'Conserved residues',
      TourStepSaltBridges: 'Salt bridges',
      TourStepSurfaceCharge: 'Surface charge',
      TourSaltBridgeCount_one: '{{count}} salt bridge',
      TourSaltBridgeCount_other: '{{count}} salt bridges',
      TourSaltBridgesText:
        '{{count}} pair a positive side chain (Lys, Arg, His) with a negative one (Asp, Glu): {{pairs}}{{more}}. The dashed lines are the bridges; each is a few kJ/mol of glue holding the fold in shape.',
      TourPositiveResidues_one: '{{count}} positive residue',
      TourPositiveResidues_other: '{{count}} positive residues',
      TourNegativeResidues_one: '{{count}} negative residue',
      TourNegativeResidues_other: '{{count}} negative residues',
      TourSurfaceChargeText:
        '{{positive}} (Lys, Arg) and {{negative}} (Asp, Glu) — net charge about {{net}}. The surface is blue where the electrostatic potential is positive and red where it is negative.',
      TourSurfaceChargePos: ' Most positive around {{list}}',
      TourSurfaceChargeNeg: ' Most negative around {{list}}',
      TourSurfaceChargeNegAfter: '; most negative around {{list}}',
      TourSentenceEnd: '.',
      TourSurfaceChargeClose: ' Charged patches are where other molecules are attracted or repelled.',
      TourHomologs_one: '{{count}} homolog',
      TourHomologs_other: '{{count}} homologs',
      TourSpecies_one: '{{count}} species',
      TourSpecies_other: '{{count}} species',
      TourConservedText:
        '{{count}} ({{share}}% of {{chain}}) are kept unchanged across {{homologs}} from {{organisms}} ({{cluster}}): {{list}}{{more}}. Evolution protects what matters — these are where function lives.',
      AnnotationsCard: 'UniProt annotations',
      AnnotationsOpen: 'Open in UniProt',
      AnnotationsClickHint: 'Click a feature to highlight it in 3-D.',
      AnnotationsNotShown: 'Select this structure in the gallery to highlight features.',
      AnnotationsOmitted: '{{n}} more not shown',
      AnnotationsMore: 'More',
      AnnotationsLess: 'Less',
      RamachandranCard: 'Ramachandran plot',
      RamachandranOutliers: '{{n}} outliers',
      RamachandranAlpha: 'α-helix',
      RamachandranBeta: 'β-strand',
      RamachandranAlphaL: 'left-handed α',
      RamachandranPpII: 'polyproline II',
      RamachandranOutlier: 'outlier',
      RamachandranGlycine: 'glycine',
      RamachandranOutlierLabel: 'Ramachandran outliers',
      RamachandranHighlightOutliers: 'Highlight outliers',
      RamachandranClearHighlight: 'Clear highlight',
      RamachandranGlycineNote: '{{n}} of the outliers are glycine, which has no side chain and is allowed there',
      RamachandranSubset: 'residues {{spec}}',
      RamachandranOmitted: '{{n}} more residues not plotted',
      RamachandranLiveHint: 'Hover a point to mark its residue in 3-D; click to frame the camera on it.',
      RamachandranNotShown: 'Select this structure in the gallery to interact with the plot.',
      DisplacementCard: 'Cα displacement',
      DisplacementRmsd: 'RMSD {{v}} Å',
      DisplacementMax: 'largest shift {{v}} Å at residue {{r}}',
      DisplacementPlddtDelta: 'mean pLDDT {{v}} vs parent',
      DisplacementTruncated: 'the two chains differ in length; only the shared part is compared',
      DisplacementXLabel: 'residue ({{chain}})',
      DisplacementYLabel: 'Cα shift (Å)',
      DisplacementSeries: 'shift',
      DisplacementPlddt: 'mutant pLDDT',
      DisplacementLiveHint: 'Hover the curve to mark the residue in 3-D; click to frame the camera on it.',
      DisplacementNotShown: 'Select the parent or the mutant in the gallery to interact with the chart.',
      DisplacementHighlightMoved: 'Highlight moved residues',
      DisplacementClearHighlight: 'Clear highlight',
      DisplacementMovedLabel: 'Shifted by {{mut}}',
      DisplacementNoneMoved: 'No residue shifted more than the overall RMSD.',
      DisplacementMovedCompare: 'Moved between {{name}} and {{other}}',
      DisplacementXLabelChains: 'residue (chains {{chains}})',
      DisplacementFitOn: 'fitted on {{chains}}',
      FlexCard: 'Flexibility',
      FlexPredicted: 'predicted mobility (GNM)',
      FlexExperimental: 'crystal B-factor',
      FlexHinge: 'hinge',
      FlexHinges: 'hinges {{list}}',
      FlexXLabel: 'residue (chains {{chains}})',
      FlexColor: 'Color by mobility',
      FlexUncolor: 'Normal coloring',
      FlexAnimate: 'Animate motion',
      FlexStopMotion: 'Stop motion',
      FlexRegion: 'Simulate {{list}}',
      FlexStopRegion: 'Stop simulation',
      FlexMotionFailed: 'Could not compute the motion for this structure.',
      FlexLiveHint:
        'Hover the chart to mark a residue in 3-D; the buttons color by mobility, animate the slowest motion, and run region MD on the most flexible residues.',
      FlexNotShown: 'Select this structure in the gallery to use the buttons.',
      SeqPropsCard: 'Sequence properties',
      SeqPropsMass: 'Mass',
      SeqPropsPI: 'Isoelectric point',
      SeqPropsCharge: 'Net charge, pH 7',
      SeqPropsAromatic: 'Aromatic',
      SeqPropsLength: '{{n}} residues',
      SeqPropsChains: 'chains {{chains}}',
      SeqPropsEpsilon: 'ε₂₈₀ in M⁻¹cm⁻¹ with every cysteine paired',
      SeqPropsNetCharge: 'net charge',
      SeqPropsAcidic: 'acidic',
      SeqPropsBasic: 'basic',
      SeqPropsPolar: 'polar',
      SeqPropsHydrophobic: 'hydrophobic',
      SeqPropsGlycine: 'glycine',
      SeqPropsHint:
        "From the sequence alone. The curve is the protein's net charge as the pH changes; it crosses zero at the isoelectric point, where the protein is least soluble.",
      DisplacementMorph: 'Morph between states',
      DisplacementStopMorph: 'Stop morph',
      DisplacementMorphFailed: 'Could not align the two structures for a morph.',
      LigandSimulate: 'Simulate the binding site',
      LigandStopSimulate: 'Stop simulation',
      AlaScanCard: 'Alanine scan',
      AlaScanHot: 'hot spot',
      AlaScanModerate: 'moderate',
      AlaScanTolerant: 'tolerant',
      AlaScanHotLabel: 'Hot spots',
      AlaScanXLabel: 'Residue (N → C)',
      AlaScanHotCount_one: '{{count}} hot spot',
      AlaScanHotCount_other: '{{count}} hot spots',
      AlaScanModerateCount_one: '{{count}} moderate',
      AlaScanModerateCount_other: '{{count}} moderate',
      AlaScanResidue: 'Residue',
      AlaScanWhy: 'Why it matters',
      AlaScanBurial: 'burial',
      AlaScanHbond: 'sidechain H-bonds',
      AlaScanSaltBridge: 'salt bridges',
      AlaScanDisulfide: 'disulfide',
      AlaScanColor: 'Color by ΔΔG',
      AlaScanUncolor: 'Reset color',
      AlaScanHighlight: 'Highlight hot spots',
      AlaScanUnhighlight: 'Clear highlight',
      AlaScanCaveat: 'ΔΔG is a heuristic ranking (not kcal/mol).',
      AlaScanLiveHint: 'Hover a bar to find the residue in 3-D; click a bar or a row to frame it.',
      InteractionsCard: 'Interactions',
      InteractionsShow: 'Show in 3-D',
      InteractionsHide: 'Hide',
      InteractionsMore_one: '+{{count}} more',
      InteractionsMore_other: '+{{count}} more',
      InteractionsDisulfides: 'Disulfide bonds',
      InteractionsSaltBridges: 'Salt bridges',
      InteractionsInterChain_one: '{{count}} between chains',
      InteractionsInterChain_other: '{{count}} between chains',
      InteractionsHbonds: 'Hydrogen bonds',
      InteractionsHbondSplit: 'H-bonds: backbone–backbone / backbone–sidechain / sidechain–sidechain.',
      InteractionsTopHbonds: 'Tightest sidechain–sidechain H-bonds',
      InteractionsInterLegend: '⇄ = between chains.',
      InteractionsLiveHint: 'Click a pair to highlight its two residues.',
      LigandCard: 'Ligand binding',
      LigandBindingLabel: '{{ligand}} site',
      LigandScoreUnit: 'interaction score (lower = better)',
      LigandVdw: 'vdW',
      LigandElectrostatic: 'electrostatic',
      LigandHbondTerm: 'H-bond',
      LigandCounts: '{{hbonds}} H-bonds · {{clashes}} clashes · {{contacts}} atom contacts',
      LigandDocked: 'docked pose',
      LigandPlaced: 'placed, not docked',
      LigandHbondsWith: 'H-bonds with',
      LigandTopContacts: 'Top contacts (energy)',
      LigandClashesWith: 'Clashes with',
      LigandHighlight: 'Highlight binding residues',
      LigandUnhighlight: 'Clear highlight',
      LigandDock: 'Dock',
      LigandCaveat: 'A reduced classical score of this one pose — illustrative, not a binding affinity.',
      LigandLiveHint: 'Click a residue to frame it.',
      LigandNotShown: 'Show this structure with {{ligand}} bound to use the buttons.',
      ConservationCard: 'Conservation',
      ConservationSubtitle: '{{used}} homologs · {{organisms}} species',
      ConservationFamily:
        'UniProt family {{cluster}} ({{name}}): {{fetched}} sequences fetched, {{used}} kept after removing near-duplicates.',
      ConservationLegend: 'variable → conserved',
      ConservationGrade: 'grade {{g}}',
      ConservationTooltip: 'score {{score}} · {{identity}}% keep {{one}} · consensus {{consensus}}',
      ConservationGaps: '{{gaps}}% of homologs have no residue here',
      ConservationColor: 'Color structure',
      ConservationUncolor: 'Normal coloring',
      ConservationHighlightTop: 'Highlight most conserved',
      ConservationClearHighlight: 'Clear highlight',
      ConservationTopLabel: 'Most conserved residues',
      ConservationLiveHint: 'Hover a residue to mark it in 3-D; click to frame the camera on it.',
      ConservationNotShown: 'Select this structure in the gallery to interact with the card.',
      ConservationWhat:
        "How much each residue varies across this protein's family. Relatives of this chain from many species (its UniProt family) were aligned to it, and each position was scored by how many different amino acids the family tolerates there. Maroon = kept the same in nearly every relative — usually the active site, binding sites and the core; teal = freely variable, usually surface loops.",
      ConservationChipFamily: '{{used}} homologs · {{organisms}} species · {{cluster}}',
      ConservationChipScoring: 'scoring from the UniProt family…',
      ConservationMethod:
        'Shannon entropy per position over a query-centered alignment of the UniRef50 family; an approximation of a full multiple alignment.',
      RestoreGraph: 'Close enlarged view',
      EnlargedGraphHint: 'The graph is shown in the enlarged popup.',
      SimilarityHeatmapSubheader: 'Pairwise Tanimoto on RDKit Morgan fingerprints',
      SimilarityHeatmapAboutTitle: 'About this heatmap',
      SimilarityHeatmapWhat:
        'Pairwise structural similarity between every molecule in the gallery, computed from RDKit Morgan fingerprints (radius 2, 2048 bits — equivalent to ECFP4).',
      SimilarityHeatmapHowRead:
        'Each cell is the Tanimoto coefficient |A ∩ B| / |A ∪ B|: 1 means identical fingerprint, 0 means no shared substructures.',
      SimilarityHeatmapCaveat:
        'Morgan fingerprints (binary, radius 2) capture circular atom environments up to two bonds out, indexed as bit presence rather than count. Values are best read relatively (which molecules cluster?) — homologous series like long alkanes can saturate to identical bit vectors despite differing in chain length.',
      SimilarityProjection: 'Similarity Projection',
      SimilarityProjectionSubheader: 'MDS 2D embedding of structural similarity',
      StructurePropertyScatter: 'Distance Scatter',
      SpScatterAboutTitle: 'About the Distance Scatter',
      SpScatterWhat:
        'Each dot is a pair of molecules. The x-axis is their structural distance (1 − Tanimoto on RDKit Morgan fingerprints); the y-axis is their property distance (Euclidean over standardized numeric properties).',
      SpScatterHowRead:
        'The orange dashed line is the linear fit through the pairs — its slope is what the Mantel correlation measures. A tight cloud along the line means structure predicts properties; a diffuse cloud means the two lenses disagree.',
      SpScatterOutliers:
        'Above the line: structurally similar but property-divergent ("activity cliffs", informative in medicinal chemistry). Below the line: structurally distant but property-similar ("scaffold hops", useful for diversification).',
      SpScatterXAxisLabel: 'Structural distance (1 − Tanimoto)',
      SpScatterYAxisLabel: 'Property distance (standardized)',
      ActivityCliffs: 'Activity Cliffs',
      ActivityCliffsAboutTitle: 'About Activity Cliffs',
      ActivityCliffsWhat:
        'Every molecule pair is ranked by a "cliff score" — the property gap divided by the structural distance. High scores mark pairs that look almost the same but behave very differently; low scores mark pairs that look very different but behave the same.',
      ActivityCliffsHowRead:
        'Hover any row to highlight both molecules in the gallery. Click to jump to molecule A. Use the property dropdown to score against a single property (more interpretable) or all properties at once (matches the Distance Scatter).',
      ActivityCliffsScore:
        'Cliff score = |Δproperty| / max(0.01, 1 − Tanimoto). The 0.01 floor keeps identical-fingerprint pairs from blowing up the denominator; pairs at the floor are reported as the highest scores in the table.',
      ActivityCliffsModeCliffs: 'Cliffs',
      ActivityCliffsModeHops: 'Scaffold Hops',
      ActivityCliffsAllProperties: 'All properties',
      ActivityCliffsSubheaderCliffs:
        'Top {{shown}} of {{total}} pairs over {{molecules}} molecules, ranked by largest property gap per unit of structural change.',
      ActivityCliffsSubheaderHops:
        'Top {{shown}} of {{total}} pairs over {{molecules}} molecules, ranked by smallest property gap per unit of structural change.',
      ActivityCliffsTruncatedNote: '(showing the first {{max}})',
      ActivityCliffsColMoleculeA: 'Molecule A',
      ActivityCliffsColMoleculeB: 'Molecule B',
      ActivityCliffsColStruct: 'Δ struct',
      ActivityCliffsColProp: 'Δ prop',
      ActivityCliffsColScore: 'Score',
      ActivityCliffsReasonNoProperties:
        'No numeric property survived the live-column filter (need ≥3 finite values and non-constant).',
      ActivityCliffsReasonNoStructures: 'None of the gallery molecules could be parsed into a structure.',
      ActivityCliffsReasonNotEnoughData:
        'Fewer than 3 molecules with both a parseable structure and the chosen property.',
      ParetoFrontier: 'Pareto Frontier',
      ParetoFrontierAboutTitle: 'About the Pareto Frontier',
      ParetoFrontierWhat:
        'Each dot is a molecule plotted on two properties of your choice. The Pareto frontier is the SET of orange-highlighted dots — molecules that no other molecule beats on BOTH axes given the chosen direction for each axis (↓ minimize / ↑ maximize). The dashed line just connects them visually to show the trade-off shape; it is not itself the frontier, since no molecule lives at the interpolated points between dots.',
      ParetoFrontierHowRead:
        'Default direction is "minimize" on both axes, matching drug-likeness intuition (lower MW + lower logP = better). Click the ↓/↑ button next to each axis to flip. Hover a dot for its values; click to select that molecule in the gallery.',
      ParetoFrontierCaveat:
        'Pareto optimality only makes sense when you can rank "better" on each axis. This view is only shown for drug-discovery projects; for molecular-modeling projects, the optimization direction is rarely well-defined.',
      ParetoFrontierOnFront: 'On Pareto front',
      ParetoFrontierNoData: 'No molecules have finite values for both selected properties.',
      ScaffoldClusters: 'Structure Clusters',
      ScaffoldClustersAboutTitle: 'About Structure Clusters',
      ScaffoldClustersWhat:
        "Molecules are grouped by structural similarity (Tanimoto on Morgan fingerprints). Each cluster's representative is the molecule closest to the cluster's centroid — i.e. the one with the highest mean similarity to other members.",
      ScaffoldClustersHowRead:
        'Drag the threshold slider to control how strict membership is. Higher values produce more, smaller clusters; lower values produce fewer, larger ones. Hover a row to highlight every molecule in that cluster in the gallery. Click a name chip to select it.',
      ScaffoldClustersCaveat:
        'This is similarity-based clustering, not formal Bemis-Murcko scaffold extraction (which RDKit MinimalLib does not expose). For chemically-related galleries the resulting clusters typically share a scaffold, but the cluster identity is "group of similar molecules" rather than a SMILES scaffold name.',
      ScaffoldClustersThresholdLabel: 'Threshold',
      ScaffoldClustersSubheader: '{{clusters}} clusters across {{molecules}} molecules.',
      ScaffoldClustersUnparseableNote: '({{count}} unparseable, excluded)',
      ScaffoldClustersColSize: 'Size',
      ScaffoldClustersColCohesion: 'Cohesion',
      ScaffoldClustersColRepresentative: 'Representative',
      ScaffoldClustersColMembers: 'Members',
      ScaffoldClustersReasonNoStructures: 'None of the gallery molecules could be parsed into a structure.',
      ScaffoldClustersReasonNotEnoughData: 'Fewer than 2 molecules with parseable structures.',
      ActivityCliffsSaturationNote:
        'The Morgan fingerprint (binary, radius 2) cannot distinguish these two molecules, even though they differ in formula. Long chains and other homologous series share the same set of atom environments — only the counts differ, and binary fingerprints throw multiplicity away. The cliff score uses an ε floor on the denominator so these pairs are still rankable.',
      BitCorrelations: 'Feature Correlations',
      BitCorrelationsSubheader: 'Named structural features × properties (over {{count}} molecules)',
      BitCorrelationsAboutTitle: 'About Feature Correlations',
      BitCorrelationsWhat:
        'For each named structural feature (functional group, ring system, halogen, …), compute its Pearson correlation with each numeric property across the gallery. Rows are features whose presence varies across the set, sorted by strongest |r| across all columns.',
      BitCorrelationsHowRead:
        'Red cells mean a feature and a property correlate positively (when the feature is present, the property tends to be higher). Blue cells mean negative correlation. Hover any cell to see exactly which gallery molecules carry that feature — those cards light up with an orange border.',
      BitCorrelationsCaveat:
        'Rare features (carried by few molecules) can produce large |r| with little real signal; check the row tooltip for the molecule count. The feature set is hand-curated and covers common organic substructures — it is not exhaustive.',
      BitCorrelationsRowTooltip: '{{feature}} carried by {{count}} of {{total}} molecules.',
      BitCorrelationsNotEnoughData: 'Need at least 3 molecules with parsable structures and live numeric properties.',
      BitCorrelationsReasonNoProperties:
        'No numeric properties in the gallery survived the live-column filter (need ≥3 finite values and non-constant).',
      BitCorrelationsReasonNoStructures: 'None of the gallery molecules could be parsed into a structure.',
      BitCorrelationsReasonNotEnoughData:
        'Fewer than 3 molecules with both parseable structures and numeric properties.',
      BitCorrelationsReasonNoInformativeBits:
        'Every curated feature is either present in every molecule or absent from every molecule, so no correlation is defined. Try a more chemically diverse gallery.',
      BitCorrelationsAllConstantNote:
        'Every feature is either present in every molecule or absent from every molecule, so no correlation column is defined. The rows below still show what your gallery contains.',
      SimilarityNotEnoughData: 'Need at least 3 molecules with parsable structures to build a similarity view.',
      SimilarityComputing: 'Computing fingerprints…',
      RDKitDownloading: 'Downloading molecular library… {{loaded}} / {{total}} MB',
      RDKitDownloadingUnknownSize: 'Downloading molecular library… {{loaded}} MB',
      RDKitInitializing: 'Initializing molecular library…',
      RDKitLoadFailed: 'Could not load the molecular library. Check your network connection and try again.',
      MdsAxesTitle: 'About these axes',
      MdsAxis1Label: 'MDS1',
      MdsAxis1Explanation:
        'The axis along which the molecules spread out the most — the direction capturing the largest share of structural variation in the fingerprint space.',
      MdsAxis2Label: 'MDS2',
      MdsAxis2Explanation:
        'The next most informative direction, orthogonal to MDS1, capturing structural variation that MDS1 does not.',
      MdsVarianceLabel: 'Variance covered',
      MdsVarianceExplanation:
        'Share of pairwise structural distance preserved in this 2D plot. ≥70% means the layout is trustworthy; below 40% means lean on the similarity heatmap instead.',
      MdsAxesNoInherentMeaning:
        'Axes have no inherent chemical meaning — click molecules at the extremes to discover what each direction represents.',
      MantelReadoutLabel: 'Structure↔Property',
      MantelAboutTitle: 'About this number',
      MantelWhat:
        'Mantel correlation between the structural distance matrix (1 − Tanimoto over fingerprints) and the property distance matrix (Euclidean over standardized numeric properties).',
      MantelRange:
        'Range: −1 to +1. Positive r means structurally similar molecules also share property values; near zero means the two distance structures are independent in this gallery.',
      MantelPvalue:
        'p-value via 999 row-permutations of the structural matrix. p < 0.05 means the observed correlation is unlikely to arise from chance pairing.',
      PcaAxesTitle: 'About these axes',
      PcaAxis1Label: 'PC1',
      PcaAxis1Explanation:
        'The direction in standardized property space along which the molecules vary most — the linear combination of properties capturing the largest share of total variance.',
      PcaAxis2Label: 'PC2',
      PcaAxis2Explanation:
        'The next most informative direction, orthogonal to PC1, capturing variance that PC1 does not.',
      PcaVarianceLabel: 'Variance covered',
      PcaVarianceExplanation:
        'Share of total property variance preserved in this 2D plot. The remaining variance lives in higher-dimensional components we discarded.',
      PcaLoadingsHint:
        'Open the loadings tooltip next to the variance-covered readout to see which properties contribute most to each axis.',
      PCANotEnoughData: 'Not enough items with complete properties to run PCA (need at least 3).',
      PCANotEnoughDataCrystals: 'Not enough crystals with complete properties to run PCA (need at least 3).',
      PCANotEnoughVariance: 'Not enough property variance across items to run PCA.',
      PCANotEnoughVarianceCrystals: 'Not enough property variance across crystals to run PCA.',
      PCANotEnoughDataNanostructures:
        'Not enough nanostructures with complete properties to run PCA (need at least 3).',
      PCANotEnoughVarianceNanostructures: 'Not enough property variance across nanostructures to run PCA.',
      PCAEigenFailed: 'PCA failed: eigendecomposition could not be computed.',
      AllMolecules: 'All molecules',
      CoordinateSystemSettings: 'Coordinate System Settings',
      SelectXAxis: 'Select X Axis',
      SelectYAxis: 'Select Y Axis',
      XFormula: 'Function f(x)',
      YFormula: 'Function f(y)',
      MinimumX: 'Minimum X',
      MaximumX: 'Maximum X',
      MinimumY: 'Minimum Y',
      MaximumY: 'Maximum Y',
      NumericValues: 'Numeric Values',
      ExportCsv: 'Export CSV',
      ScatterPlotSettings: 'Scatter Plot Settings',
      GridLines: 'Grid Lines',
      HorizontalLines: 'Horizontal',
      VerticalLines: 'Vertical',
      SymbolSize: 'Symbol Size',
      LineWidth: 'Line Width',
      SortScatterPlotData: 'Sort Data',
      SortDataByXValue: 'By X',
      SortDataByYValue: 'By Y',
      RegressionAnalysis: 'Regression Analysis',
      RegressionModel: 'Regression Model',
      RawData: 'Raw Data',
      PolynomialRegressionDegree: 'Polynomial Regression Degree',
      ClickToIncludeMolecule: 'Click to include this molecule',
      ClickToExcludeMolecule: 'Click to exclude this molecule',
    },

    landscapeGate: {
      Tagline: 'AI for Molecular Sciences',
      Headline: 'Best in landscape',
      Body: 'Rotate your phone for the full lab view, gallery grid, and simulation controls.',
      ContinueInPortrait: 'Continue in portrait',
      AutoDismissHint: 'Or just rotate — this screen dismisses itself',
    },

    landingPage: {
      Tagline: 'AI for Molecular Sciences',
      Headline: 'A new interface to the molecular world',
      Subheadline: 'Click a card below to choose your project type and start building with AI',
      Skip: 'Skip',
      SkipHint: 'Continue to the app',
      ResumeAction: 'Continue last project',
      MolecularModeling: 'Molecular Modeling',
      MolecularModelingBlurb: 'Build molecules with AI, then run molecular dynamics in a 3D lab.',
      DrugDiscovery: 'Drug Discovery',
      DrugDiscoveryBlurb: 'Dock ligands into protein pockets and score binding interactively.',
      CrystalBuilder: 'Crystal Builder',
      CrystalBuilderBlurb: 'Build four types of crystals: ionic, covalent, metallic, and molecular.',
      SemiconductorDesign: 'Semiconductor Design',
      SemiconductorDesignBlurb: 'Design semiconductors and study electronic structures.',
      NanomaterialDesign: 'Nanomaterial Design',
      NanomaterialDesignBlurb:
        'Build nanotubes, quantum dots, nanoparticles and membranes, and probe how size changes their behavior.',
      PolymerBuilder: 'Polymer Builder',
      PolymerBuilderBlurb: 'Generate monomers, build chains, and perform simulations.',
      ProteinLab: 'Protein Lab',
      ProteinLabBlurb: 'Explore protein structures in 3D and study how their shape drives function.',
      DnaRnaLab: 'DNA/RNA Lab',
      DnaRnaLabBlurb: 'Build DNA and RNA, and follow how genes are transcribed and translated into proteins.',
      UnderConstruction: 'Under construction',
      MoreSectionHeader: 'More',
      Commons: 'Commons',
      CommonsBlurb: 'Browse projects shared by the AIMS community.',
      LessonCommons: 'Lesson Plans',
      LessonCommonsBlurb: 'Browse and share teaching lesson plans for AIMS.',
      Tutorial: 'Tutorial',
      TutorialBlurb: 'A guided tour to building molecules with AIMS.',
      About: 'About',
      AboutBlurb: 'Information about the team and the project.',
      CaseStudies: 'Ideas',
      CaseStudiesBlurb: 'Design and discover new materials and drugs.',
    },

    menu: {
      mainMenu: 'Main Menu',
      projectSubMenu: 'Project',
      project: {
        CreateNewProject: 'Create New Project',
        OpenProject: 'Open Project',
        RecentProjects: 'Recent Projects',
        SaveProject: 'Save Project',
        SaveProjectAs: 'Save Project As',
        Export3DPrintableModel: 'Export 3D-Printable Model…',
      },
      editSubMenu: 'Edit',
      edit: {
        Undo: 'Undo',
        Redo: 'Redo',
        Undone: 'Undone',
      },
      viewSubMenu: 'View',
      view: {
        NavigationView: 'Navigation View',
        ShowGallery: 'Show Gallery',
        ResetView: 'Reset View',
        ZoomIn: 'Zoom In',
        ZoomOut: 'Zoom Out',
        AutoRotate: 'Auto Rotate',
      },
      accessoriesSubMenu: 'Accessories',
      accessories: {
        Instruction: 'Instruction',
        PeriodicTable: 'Periodic Table of Elements',
      },
      examplesSubMenu: 'Examples',
      examples: {
        chemistrySubMenu: 'Chemistry',
        chemistry: {
          GasLaws: 'Gas Laws',
          GasLawsOfArgon: 'Gas Laws of Argon',
          GasLawsOfHydrogenMolecules: 'Gas Laws of Hydrogen Molecules',
          SimpleMolecules: 'Simple Molecules',
          MonatomicMolecules: 'Monatomic Molecules',
          SpeedDistributions: 'Speed Distributions',
          MaxwellBoltzmannSpeedDistributionArgon: 'Maxwell-Boltzmann Speed Distribution of Argon',
          SpeedDistributionsOfCarbonAndHydrogenInMolecules:
            'Speed Distributions of Carbon and Hydrogen Atoms in Molecules',
          DiatomicMolecules: 'Diatomic Molecules',
          TriatomicMolecules: 'Triatomic Molecules',
          LiquidInBox: 'Liquid in a Box',
          StatesOfMatter: 'States of Matter',
          PhaseChange: 'Phase Change',
          Melting: 'Melting',
          CrystalGrowthFromSeed: 'Crystal Growth from a Seed',
          Evaporation: 'Evaporation',
          EvaporationOfArgon: 'Evaporation of Argon',
          EvaporationOfNitrogen: 'Evaporation of Nitrogen',
          BoilingArgonAtConstantPressure: 'Boiling Argon at Constant Pressure',
          EvaporationOfWater: 'Evaporation of Water',
          SolidVsGas: 'Solid vs. Gas',
          CalorimetryArgonGas: 'Calorimetry of Argon Gas',
          HydrocarbonSeries: 'Hydrocarbon Series',
          LinearAlkanes: 'Linear Alkanes',
          Cycloalkanes: 'Cycloalkanes',
          Acenes: 'Acenes',
          OrganohalogenCompounds: 'Organohalogen Compounds',
          Chlorobenzenes: 'Chlorobenzenes',
          Isomers: 'Isomers',
          Bu2EneIsomers: 'Bu-2-ene Isomers',
          ArketamineVsEsketamine: 'Enantiomers: Arketamine vs. Esketamine',
          C8H18OIsomers: 'C₈H₁₈O Isomers',
          CisTransIsomerismOfFattyAcids: 'Cis-Trans Isomerism of Fatty Acids',
          ForeverChemicals: 'Forever Chemicals',
          ChemicalReactions: 'Chemical Reactions',
          HydrogenOxygenReaction: 'Hydrogen and Oxygen Reaction',
          MethylRadicalsToEthanes: 'Methyl Radicals to Ethanes',
          HydrogenDissociationEquilibrium: 'Hydrogen Dissociation Equilibrium',
          Polymers: 'Polymers',
          Copolymers: 'Copolymers',
          DesigningALatexPaintBinder: 'Designing a Latex Paint Binder',
        },
        biologySubMenu: 'Biology',
        biology: {
          ProteinAlphaHelix: 'Protein Alpha Helix',
          DNADoubleHelix: 'DNA Double Helix',
          LevelsOfProteinStructure: 'Levels of Protein Structure',
          Lysozyme: 'Lysozyme',
          GreenFluorescentProtein: 'Green Fluorescent Protein',
          FlexibilityOfHIV1Protease: 'Flexibility of HIV-1 Protease',
          MyoglobinAndOxygen: 'Myoglobin and Oxygen',
          Insulin: 'Insulin',
          SickleCellHemoglobin: 'Sickle-Cell Hemoglobin',
          PrionProteinPredicted: 'Prion Protein (Predicted)',
          TrypsinSpecificityPocket: 'Trypsin and Its Specificity Pocket',
        },
        materialsScienceSubMenu: 'Materials Science',
        materialsScience: {
          IonicCrystals: 'Ionic Crystals',
          MetallicCrystals: 'Metallic Crystals',
          MolecularCrystals: 'Molecular Crystals',
          CovalentCrystals: 'Covalent Crystals',
          GoldCrystal: 'Gold Crystal',
          SilverCrystal: 'Silver Crystal',
          IronCrystal: 'Iron Crystal',
          SaltCrystal: 'Salt Crystal',
          RocksaltOxides: 'Rocksalt Oxides',
          Wurtzite: 'Wurtzite',
          Rutiles: 'Rutiles',
          Fluorites: 'Fluorites',
          AlkaliHalides: 'Alkali Halides',
          MeltingOfSalt: 'Melting of Salt',
          LayeredMaterials: 'Layered Materials',
          HeatConduction: 'Heat Conduction',
          HeatConductionInSalt: 'Heat Conduction in Salt',
          SaltGrowthFromSeed: 'Salt Growth from a Seed',
          IceStructure: 'Ice Structure',
          SimpleMolecularCrystals: 'Simple Molecular Crystals',
          FCCMetals: 'Face-Centered Cubic Metals',
          BCCMetals: 'Body-Centered Cubic Metals',
          HCPMetals: 'Hexagonal Close-Packed Metals',
          Intermetalics: 'Intermetallics',
          MeltingOfCopper: 'Melting of Copper',
          CopperGrowthFromSeed: 'Copper Growth from a Seed',
          AnnealingOfAmorphousCopper: 'Annealing of Amorphous Copper',
          MeltingOfIce: 'Melting of Ice',
          NitrogenGrowthFromSeed: 'Nitrogen Growth from a Seed',
          DryIceGrowthFromSeed: 'Dry Ice Growth from a Seed',
          Semiconductors: 'Semiconductors',
          DesigningARedLaserAlloy: 'Designing a Red-Laser Alloy',
          GalliumArsenideOnSilicon: 'Gallium Arsenide on Silicon',
          MeltingOfSilicon: 'Melting of Silicon',
          HeatConductionInSilicon: 'Heat Conduction in Silicon',
          SiliconGrowthFromSeed: 'Silicon Growth from a Seed',
          MeltingOfCadmiumTelluride: 'Melting of Cadmium Telluride',
          DiamondCrystal: 'Diamond Crystal',
          PureCovalentCrystals: 'Pure Covalent Crystals',
          CompoundCovalentCrystals: 'Compound Covalent Crystals',
          MolecularSieves: 'Molecular Sieves',
          DesigningACarbonDioxideSieve: 'Designing a Carbon Dioxide Sieve',
          Graphite: 'Graphite',
          Graphenes: 'Graphene',
          SoftMatter: 'Soft Matter',
          LiquidCrystalsGayBerne: 'Modeling Liquid Crystals with Gay-Berne Particles',
          LiquidCrystalMixture: 'Mixing Long and Short Rods in a Liquid Crystal',
          GuestHostDisplay: 'A Guest-Host Liquid Crystal Display',
          SmecticLayers: 'Smectic Layers in a Liquid Crystal',
          PolymerMeltBeads: 'A Polymer Melt Near Its Glass Transition',
          HpFoldingBeads: 'A Sequence That Folds and One That Does Not',
          MicelleFormationBeads: 'Micelle Formation',
          GoFoldingBeads: 'A Protein That Folds and Holds',
        },
        nanotechnologySubMenu: 'Nanotechnology',
        nanotechnology: {
          Buckyballs: 'Buckyballs',
          CarbonNanotube: 'Carbon Nanotube',
          NanotubesMetalOrSemiconductor: 'Carbon Nanotubes: Metallic or Semiconducting?',
          MeltingOfGoldNanoparticles: 'Melting of Gold Nanoparticles',
          GrapheneNanoporeMembrane: 'A Graphene Nanopore Membrane',
          StretchingACarbonNanotube: 'Stretching a Carbon Nanotube',
          GrapheneNanoribbonFamilies: 'Graphene Nanoribbon Families',
          WhySiliconGlows: 'Why Silicon Glows When It Is Small',
          QuantumDotsVersusNanowires: 'Quantum Dots Versus Nanowires',
          DesigningANanotubeStrainSensor: 'Designing a Nanotube Strain Sensor',
          ChoosingACadmiumFreeQuantumDot: 'Choosing a Cadmium-Free Quantum Dot',
          MultiwallCarbonNanotubes: 'Multiwall Carbon Nanotubes',
          CarbonNanobuds: 'Carbon Nanobuds',
          NanotubeYJunction: 'A Nanotube Y-Junction',
          CarbonNanotubes: 'Carbon Nanotubes',
          Fullerenes: 'Fullerenes',
          GrapheneNanostructures: 'Graphene Nanostructures',
          QuantumConfinement: 'Quantum Confinement',
          NanocarOnGold: 'A Nanocar on Gold',
          FullereneFamily: 'The Fullerene Family',
        },
        biotechnologySubMenu: 'Biotechnology',
        biotechnology: {
          HIV1ProteaseInhibitor: 'HIV-1 Protease Inhibitor',
        },
      },
      Welcome: 'Welcome',
      BrowseTheCommons: 'Browse the Commons',
      BrowseLessonPlans: 'Shared Lesson Plans',
      OpenTutorial: 'Tutorial',
      Ideas: 'Ideas',
      languageSubMenu: 'Language',
      helpSubMenu: 'Help',
      AboutUs: 'About Us',
    },

    lessonPlans: {
      MenuCreate: 'Create Lesson Plan',
      MenuMyPlans: 'My Lesson Plans',
      DraftModalTitle: 'Create a lesson plan from this project',
      UntitledPlan: 'Lesson plan',
      // Library drawer + save/share
      MyPlans: 'My Lesson Plans',
      NoSavedPlans: 'No saved lesson plans yet.',
      Open: 'Open',
      Updated: 'Updated',
      GeneratedBy: 'Generated by',
      Deleted: 'Lesson plan deleted.',
      DeleteFailed: 'Could not delete the lesson plan.',
      OpenFailed: 'Could not open the lesson plan.',
      DeleteConfirm: 'Delete this lesson plan?',
      DeleteSharedConfirm: 'This plan is shared to the community. Delete it and remove it from the community?',
      SharedTag: 'Shared',
      SignInToSave: 'Sign in to save lesson plans to your account.',
      Save: 'Save',
      Saved: 'Saved',
      SavedToAccount: 'Saved to your account.',
      SaveFailed: 'Could not save the lesson plan.',
      SaveTooltip: 'Save this lesson plan to your account',
      Share: 'Share',
      Shared: 'Shared',
      SharedToCommunity: 'Shared to the lesson-plan community.',
      RemovedFromCommunity: 'Removed from the community.',
      ShareFailed: 'Could not update community sharing.',
      ShareTooltip: 'Share this lesson plan with the community',
      RemoveFromCommunityTooltip: 'Remove this lesson plan from the community',
      // Community page (/lesson-commons)
      CommunityTitle: 'Lesson Plans',
      CommunitySubtitle:
        'Browse and reuse lesson plans shared by AIMS educators. Save a copy to your account to adapt it for your class.',
      CommunityStats: '{{plans}} lesson plans · {{contributors}} contributors',
      CommunitySearch: 'Search by title, author, topic…',
      CommunityEmpty: 'No shared lesson plans yet. Be the first to share one!',
      SortRecent: 'Most recent',
      SortLikes: 'Most liked',
      SortViews: 'Most viewed',
      CopyToAccount: 'Save a copy to my account',
      CopiedToAccount: 'Saved a copy to your account.',
      CopyFailed: 'Could not copy the lesson plan.',
      SignInToLike: 'Sign in to like lesson plans.',
      SignInToCopy: 'Sign in to save a copy to your account.',
      RemoveConfirm: 'Remove "{{title}}" from the community?',
      RemoveButton: 'Remove',
      UntitledProject: 'Untitled project',
      BasedOn: 'Based on project',
      MoleculeCount: '{{count}} molecules',
      // Form
      FieldGradeLevel: 'Grade level / audience',
      GradeMiddle: 'Middle school (grades 6–8)',
      GradeHigh: 'High school (grades 9–12)',
      GradeUndergrad: 'Undergraduate (intro college)',
      GradeGraduate: 'Graduate / advanced',
      GradeGeneral: 'General audience',
      FieldAudience: 'Output',
      AudienceTeacher: 'Teacher lesson plan',
      AudienceStudent: 'Student worksheet',
      AudienceStudentKey: 'Student worksheet + answer key',
      AudienceRubric: 'Grading rubric',
      FieldTeachingMode: 'How students work',
      TeachingModeAiChat: 'AI-chat-driven',
      TeachingModeBalanced: 'Balanced (chat + hands-on)',
      TeachingModeHandsOn: 'Hands-on GUI',
      FieldFramework: 'Learning framework',
      FrameworkInquiry: 'Scientific inquiry',
      FrameworkDesign: 'Engineering design',
      FrameworkGeneral: 'General (no set cycle)',
      FieldDuration: 'Class duration',
      Duration30: 'One 30-minute class',
      Duration50: 'One 50-minute class',
      Duration90: 'One 90-minute block',
      DurationUnit: 'Multi-day unit (3–5 sessions)',
      FieldEmphasis: 'Emphasis (optional)',
      EmphasisPlaceholder: 'Anything specific to focus on, e.g. a particular concept or skill…',
      FormHint:
        'The plan is generated from the open project — its type and molecules are the target outcome students will work toward.',
      PreviewHint: 'The lesson plan will appear here as it is generated.',
      ScienceCheckPrompt: 'Check this plan for scientific accuracy?',
      CheckNow: 'Check now',
      NotNow: 'Not now',
      CheckScience: 'Check accuracy',
      CheckScienceTooltip: 'Run an AI pass to catch and correct scientific errors in this plan.',
      CheckingScience: 'Checking scientific accuracy…',
      Generate: 'Generate',
      Stop: 'Stop',
      Drafting: 'Drafting the lesson plan…',
      GenerationFailed: 'Could not generate the lesson plan.',
      Truncated:
        'The lesson plan may be cut off — the model reached its length limit. Try a shorter duration or regenerate.',
      // Iterative refinement
      RevisePlaceholder:
        'Refine the plan — e.g. "make the assessment a rubric", "shorten to 30 minutes", "add discussion questions"',
      Revise: 'Revise',
      UndoRevision: 'Undo revision',
      UndoRevisionTooltip: 'Restore the plan as it was before the last revision.',
      // Editor
      Back: 'Back',
      ModeEdit: 'Edit',
      ModeSplit: 'Split',
      ModePreview: 'Preview',
      DownloadMarkdown: 'Download Markdown',
      ExportHtml: 'Export as HTML page',
      Print: 'Print / Save as PDF',
      PrintPopupBlocked:
        'Your browser blocked the print window — the lesson plan was downloaded as HTML instead; open it and print to PDF.',
      EditorPlaceholder: 'Write your lesson plan in Markdown…',
      EmptyPreview: 'Nothing to preview yet.',
    },

    instructionPanel: {
      Rotate: 'Rotate',
      DragMouse: 'Drag mouse',
      Zoom: 'Zoom',
      MouseWheelOrKeys: 'Rotate mouse wheel or press Ctrl+[ or Ctrl+]',
      MouseWheelOrKeysMac: 'Rotate mouse wheel or press ⌘+[ or ⌘+]',
      Pan: 'Pan',
      HoldCtrlDragMouse: 'Hold Ctrl and drag mouse',
      HoldMetaDragMouse: 'Hold ⌘ and drag mouse',
      NavigationMode: 'Navigation mode',
      EnableNavigation: 'Enable navigation',
      DisableNavigation: 'Disable navigation',
      MoveForwardBack: 'Move forward/back',
      MoveForwardBackInstruction: 'Press W/S',
      MoveLeftRight: 'Move left/right',
      MoveLeftRightInstruction: 'Press A/D',
      MoveUpDown: 'Move up/down',
      MoveUpDownInstruction: 'Press Z/X',
      RollLeftRight: 'Roll left/right',
      RollLeftRightInstruction: 'Press Q/E',
      Turn: 'Turn',
      TurnInstruction: 'Press arrow keys',
      ResetView: 'Reset',
    },

    accountSettingsPanel: {
      MyAccountSettings: 'My Account Settings',
      MyID: 'My 🆔',
      ClickToCopyMyID: 'Click to copy my ID',
      IDInClipBoard: 'Your ID was copied and is ready to paste.',
      AllPublished: 'All Published',
      PublishedUnderAliases: 'Aliases',
      UserCount: 'User Count',
      PleaseSignIn: 'Please sign in to view your account.',
      ScreenName: 'Screen Name',
      ScreenNamePlaceholder: 'e.g. Ada L.',
      ScreenNameNote:
        'Anonymous accounts have no name of their own. Pick a screen name so your teacher can tell your work from everyone else’s. Your account ID stays the same either way.',
      ScreenNameSet: 'Your screen name was saved.',
      CannotSetScreenName: 'Cannot set your screen name',
    },

    experimentCard: {
      title: 'Experiment',
      hypothesis: 'Hypothesis',
      variable: 'Variable',
      predicted: 'Predicted',
      measured: 'Measured',
      confirmed: 'Confirmed',
      refuted: 'Refuted',
      inconclusive: 'Inconclusive',
      increase: 'increase',
      decrease: 'decrease',
      noChange: 'no change',
      within: 'within',
      baseline: 'Baseline',
      source: 'Source',
      unverified: 'unverified source',
      agentSaid: 'Agent said',
      overridden: 'overridden by the measured numbers',
      unchecked: "Verdict not checked against numbers — the agent's own assessment.",
    },
    designCard: {
      title: 'Design',
      goal: 'Goal',
      candidate: 'Candidate',
      constraints: 'Constraints',
      iterations: 'Iterations',
      met: 'Met',
      partiallyMet: 'Partially met',
      notMet: 'Not met',
      unverified: 'unverified',
      agentSaid: 'agent said',
      agentSaidRow: 'Agent said',
      unchecked: 'not checked',
      overridden: 'overridden by the measured numbers',
    },
    classroom: {
      MyClasses: 'My Classes',
      NoClasses: 'You have not created or joined any classes yet',
      RoleTeacher: 'Teacher',
      RoleCoTeacher: 'Co-teacher',
      RoleStudent: 'Student',
      CreateClass: 'Create a Class',
      JoinClass: 'Join a Class',
      ClassName: 'Class Name',
      ClassNamePlaceholder: 'e.g. Chemistry 101 — Fall 2026',
      ClassNameRequired: 'Please enter a class name',
      ClassDescription: 'Description (optional)',
      ClassDescriptionPlaceholder: 'What is this class about?',
      CannotCreateClass: 'Could not create the class',
      ClassCreated: 'Class created',
      JoinCodeHint: 'A join code will be generated. Share it with your students so they can join.',
      JoinCode: 'Join Code',
      EnterJoinCode: 'Enter the code from your teacher',
      JoinCodeRequired: 'Please enter a join code',
      ClassNotFound: 'No class found with that join code',
      CannotJoinOwnClass: 'You already teach this class',
      AlreadyJoined: 'You have already joined this class',
      CannotJoinClass: 'Could not join the class',
      JoinedClass: 'Joined class',
      YourName: 'Your name in this class',
      YourNamePlaceholder: 'The name your teacher will see',
      Back: 'Back to my classes',
      CopyJoinCode: 'Copy join code',
      JoinCodeCopied: 'Join code copied to clipboard',
      Teacher: 'Teacher',
      Members: 'Students',
      NoMembers: 'No students have joined yet',
      RemoveStudent: 'Remove',
      ConfirmRemoveStudent: 'Remove this student from the class?',
      CannotRemoveStudent: 'Could not remove the student',
      SubmitToClass: 'Submit to Teacher',
      SubmitProjectToClass: 'Submit Project to Teacher',
      Submit: 'Submit',
      CurrentProject: 'Current project',
      SelectClass: 'Which class',
      SubmitVisibilityNote:
        'Only the teacher and co-teachers of the class you pick can see this. Your classmates are not shown it, and it is not shared to the Commons.',
      NoJoinedClasses: 'You have not joined any classes yet — join one from your account page first.',
      SaveProjectFirst: 'Give your project a title (save it) before submitting it.',
      SubmitUnsavedWarning:
        'You have unsaved changes. Submitting sends your last saved version — save the project first so your teacher sees your latest work.',
      ProjectSubmitted: 'Project submitted to your teacher',
      CannotSubmitProject: 'Could not submit the project. Make sure it has been saved to the cloud first.',
      NoSubmissions: 'Nothing submitted yet',
      TeacherCanSeeYourWork:
        'Your teacher and any co-teachers see the projects you submit to this class — your other projects are not listed for them. Sharing a project to the Commons does not submit it to your teacher.',
      ExportRoster: 'Export CSV',
      ExportRosterTip:
        'Download the roster as a spreadsheet (CSV) — one row per submission with the student ID, screen name, email, grade, and a link that opens that piece of work. Students who have submitted nothing get a row too.',
      FormerMember: 'no longer in this class',
      MySubmittedProjects: 'Projects You Submitted',
      NoSubmittedProjects: 'No projects submitted yet',
      OpenProject: 'Open',
      Return: 'Return',
      ConfirmReturn: 'Return and remove this submitted project from the class?',
      CannotReturn: 'Could not return the project',
      LeaveClass: 'Leave Class',
      ConfirmLeaveClass: 'Leave this class? You can rejoin later with the join code.',
      LeftClass: 'You have left the class',
      CannotLeaveClass: 'Could not leave the class',
      DeleteClass: 'Delete Class',
      ConfirmDeleteClass: 'Delete this class along with its roster and submitted projects? This cannot be undone.',
      ClassDeleted: 'Class deleted',
      CannotDeleteClass: 'Could not delete the class',
      Assignments: 'Assignments',
      NewAssignment: 'New assignment',
      NoAssignments: 'No assignments yet. Create one to grade submissions against it.',
      GradeAgainst: 'Grade against',
      Points: 'points',
      Grade: 'AI Grade',
      Review: 'Review',
      Released: 'released',
      Draft: 'draft',
      NoAssignmentToGrade: 'Create an assignment first, then grade against it.',
      GradingFailed: 'Grading failed',
      GradeReleased: 'Grade released to the student',
      CannotCreateAssignment: 'Could not create the assignment',
      DeleteAssignment: 'Delete this assignment?',
      Create: 'Create',
      AssignmentTitle: 'Assignment title',
      AssignmentInstructions: 'Instructions for students (what they should do)',
      AssignmentRubric:
        'Grading rubric — the criteria to grade against (e.g. "5 pts: correct molecule; 3 pts: stable MD; 2 pts: explanation")',
      TotalPoints: 'Total points',
      ReviewGrade: 'Review grade',
      Score: 'Score',
      SaveDraft: 'Save draft',
      ReleaseToStudent: 'Release to student',
      ShowDigest: 'Show what the grader saw',
      HideDigest: 'Hide grader input',
      CoTeachers: 'Co-teachers',
      CoTeachersHint:
        'Co-teachers can view this class — the roster, submitted work, and grades — but cannot grade, return work, or change the class. Invite one by email, or by user ID (copyable from their account page). An email invite takes effect the next time they visit their account page signed in with that email.',
      NoCoTeachers: 'No co-teachers yet',
      CoTeacherIdPlaceholder: "Co-teacher's email or user ID",
      AddCoTeacher: 'Add',
      CoTeacherAdded: 'Co-teacher added',
      CannotAddCoTeacher: 'Could not add the co-teacher',
      CoTeacherAlready: 'That user is already a co-teacher',
      CoTeacherAlreadyInvited: 'That email has already been invited',
      CoTeacherInvited: 'Invitation recorded — it takes effect when they next open their account page',
      InvitePending: 'pending',
      RevokeInvite: 'Revoke',
      CannotRevokeInvite: 'Could not revoke the invitation',
      CoTeacherIsOwner: 'You already own this class',
      RemoveCoTeacher: 'Remove',
      ConfirmRemoveCoTeacher: 'Remove this co-teacher? They will no longer be able to view this class.',
      CannotRemoveCoTeacher: 'Could not remove the co-teacher',
      CoTeacherViewOnly: 'Co-teacher · view only',
      StopCoTeaching: 'Stop co-teaching',
      ConfirmStopCoTeaching: 'Stop co-teaching this class? You will no longer be able to view it.',
      StoppedCoTeaching: 'You are no longer a co-teacher of this class',
      CannotStopCoTeaching: 'Could not stop co-teaching',
    },

    avatarMenu: {
      AccountSettings: 'Account Settings',
      PrivacyStatementTitle: 'User Privacy Protection Statement',
      PrivacyStatement:
        'When you sign in, your Google ID is used to create and access your AIMS account. Only a random alphanumeric string is stored in our database as a reference. No personally identifiable data such as your name or email is ever gathered. For more information, see our ',
      SignIn: 'Sign In',
      SignInAsMe: 'Sign in as me',
      SignInAnonymously: 'Sign in anonymously',
      UpgradeToGoogle: 'Upgrade to Google account',
      SignOut: 'Sign Out',
      IfYouAreAStudent: 'If you are a student',
    },

    moleculeEdit: {
      ReplaceWith: 'Replace with…',
      ReplaceElement: 'Replace element…',
      // Submenu group headers for grouped substituent / element pickers.
      SubGroup_Alkyl: 'Alkyl',
      SubGroup_Unsaturated: 'Unsaturated',
      SubGroup_Halogen: 'Halogen',
      SubGroup_Oxygen: 'Oxygen',
      SubGroup_NitrogenSulfur: 'Nitrogen / Sulfur',
      ElemGroup_CommonOrganic: 'Common organic',
      ElemGroup_Halogen: 'Halogen',
      ElemName_C: 'C (carbon)',
      ElemName_N: 'N (nitrogen)',
      ElemName_O: 'O (oxygen)',
      ElemName_S: 'S (sulfur)',
      ElemName_P: 'P (phosphorus)',
      ElemName_B: 'B (boron)',
      ElemName_F: 'F (fluorine)',
      ElemName_Cl: 'Cl (chlorine)',
      ElemName_Br: 'Br (bromine)',
      ElemName_I: 'I (iodine)',
      DeleteAtom: 'Delete atom',
      BondOrder: 'Bond order…',
      BondToNeighbor: 'To {{element}} #{{index}} ({{order}})',
      BondOrderSet: 'Set to {{order}}',
      BreakBond: 'Break bond',
      SetFormalCharge: 'Set formal charge…',
      SetFormalChargeWithCurrent: 'Set formal charge (current: {{current}})…',
      FormalChargeSet: 'Set to {{charge}}',
      MirrorMolecule: 'Mirror molecule (invert stereochemistry)',
      AlignPrincipalAxes: 'Align to principal axes',
      NoHeavyNeighbors: 'No heavy-atom neighbors',
      CustomSubstituent: 'Custom (enter SMILES)…',
      CustomSubstituentHint:
        'Type a SMILES fragment whose first atom is the attachment point. It will replace the selected hydrogen.',
      AddBondToAtom: 'Add bond to another atom…',
      AddBondPartnerLabel: 'Bond to which atom?',
      AddBondPartnerPlaceholder: 'Pick another heavy atom',
      AddBondNoCandidates:
        'No available atoms — all heavy atoms in this molecule are already bonded to the selected one.',
      AddBondOrderLabel: 'Bond order',
      ApplyEdit: 'Apply',
      SameAtom: 'Pick a different atom for the second end of the bond.',
      BondAlreadyExists: 'These two atoms are already bonded. Use "Bond order" to change the existing bond instead.',
      EditSuccess: 'Molecule updated',
      PauseToEdit: 'Pause the simulation to edit molecules.',
      PauseSimulationToEdit: 'Click here to pause the simulation, then edit',
      EditSuccessWithSwap:
        'Molecule updated. {{count}} lab instance updated to match (kept at original position and bulk velocity).',
      NotHydrogen: 'Picked atom is not a hydrogen — pick an H to replace it.',
      IsHydrogen: 'This operation is for heavy atoms; for hydrogens, use Replace with…',
      NoParentHeavyAtom: 'Hydrogen has no heavy-atom neighbor — nothing to attach a substituent to.',
      SubstituentParseFailed: 'Could not parse substituent.',
      GraftFailed: 'Could not attach the substituent. Try again.',
      InvalidElement: 'Invalid element.',
      ValenceExceeded:
        'That swap would leave the atom with more heavy-atom bonds than the new element can sustain. Pick a different element or first delete some neighboring atoms.',
      EditRefused: 'Edit refused',
      SelectedAtom: 'Selected: {{element}} (atom #{{index}})',
      CannotDisconnect: 'Deleting this atom would split the molecule into disconnected pieces.',
      InvalidAtomIndex: 'Could not resolve picked atom in the current structure.',
      InvalidNeighborIndex: 'No bond between the two selected atoms.',
      NoChange: 'No change — atom already has that element.',
      ChargeOutOfRange: 'Formal charge must be an integer between −4 and +4.',
      ParseFailed: 'Could not parse the molecule for editing.',
      MinimizeFailed:
        'Geometry could not be minimized. The edit was applied; you can re-minimize from the molecule actions.',
      UnknownError: 'Edit failed. Please try again.',
    },

    banner: {
      BackToAIMS: 'Back to AIMS',
      Commons: 'Commons',
      Lessons: 'Lessons',
      Tutorial: 'Tutorial',
      About: 'About',
      CaseStudies: 'Ideas',
      Menu: 'Menu',
      Account: 'Account',
      SignedIn: 'Signed in',
      SignIn: 'Sign in',
      SignOut: 'Sign out',
    },

    aboutPage: {
      Title: 'About AIMS',
      ContactTitle: 'Contact',
    },

    caseStudiesPage: {
      Title: 'Ideas to get started',
      Tagline: 'How we can use AIMS for drug discovery and materials design.',
      CategoryDrugDiscovery: 'Drug Discovery',
      CategoryMaterialsDesign: 'Materials Design',
      ApproachLabel: 'Approach',
      OutcomeLabel: 'Outcome',
      TryThisIdea: 'Try this idea',
      ComingSoon: 'Coming soon',
      MinutesAbbrev: 'min',
      UsesFeaturesLabel: 'Uses',
      difficulty: {
        beginner: 'Beginner',
        intermediate: 'Intermediate',
        advanced: 'Advanced',
      },
      features: {
        docking: 'Docking lab',
        pocketAware: 'Pocket-aware AI',
        aiGeneration: 'AI generation',
        agentLoop: 'Agent loop',
        gallery: 'Gallery ranking',
        poseSearch: 'Pose search',
        mirrorAlign: 'Mirror / Align',
        pharmacophore: 'Pharmacophore',
        crystalBuilder: 'Crystal Builder',
        doping: 'Doping panel',
        crystalMD: 'Crystal molecular dynamics',
        analysisPanel: 'Analysis panel',
        irPanel: 'IR spectrum',
        commons: 'Commons share',
        guestMolecules: 'Guest molecules',
        nanostructures: 'Nanostructure builder',
        quantumConfinement: 'Quantum confinement',
      },
      studies: {
        s1: {
          Title: 'Non-peptide HIV-1 protease inhibitor optimization',
          Purpose:
            'HIV-1 protease has been the target of every successful protease-inhibitor antiretroviral since 1995. Expanding the candidate pool around a known scaffold is a canonical exercise in structure-based drug design — and a useful template for any kinase or receptor target you would approach the same way.',
          Challenge:
            'Broaden the candidate pool around a known non-peptide HIV-1 protease inhibitor scaffold without losing drug-likeness.',
          Approach1:
            'Load the 1AID protein structure into the docking lab and set the bound inhibitor as the starting pose.',
          Approach2:
            'Use pocket-aware AI generation to propose 80 scaffold variants conditioned on the active-site cleft geometry.',
          Approach3: 'Rank candidates by live docking score, Lipinski and Veber compliance, and QED in the gallery.',
          Approach4:
            'Run the Stage-3 automated pose search on the top 12 to confirm rigid- and flex-docking stability.',
          Outcome:
            'A run like this might surface 3 candidates with predicted binding scores within 0.4 kcal/mol of the published lead, all passing Lipinski + Veber — a defensible shortlist for follow-up.',
        },
        s2: {
          Title: 'Pocket-aware kinase inhibitor design',
          Purpose:
            'Protein kinases are the second-largest class of clinical drug targets, and finding a new starting-point chemotype is one of the dominant expenses in modern medicinal chemistry. Pocket-aware generative design promises to compress that search from months of fragment screening into an afternoon of in-silico iteration.',
          Challenge:
            'Find a starting-point chemotype for a kinase with a deep, hydrophobic ATP-binding pocket and no obvious small-molecule precedent.',
          Approach1: 'Detect pockets on the receptor and select the largest hydrophobic site as the target.',
          Approach2:
            'Trigger the agent loop with a "design selective kinase inhibitors that exploit this pocket" goal.',
          Approach3:
            'Iterate for 5 rounds: each round filter by docking score + pharmacophore complementarity, then feed the survivors back into AI generation.',
          Approach4: 'Export the top 5 candidates as SDF for downstream synthesis planning.',
          Outcome:
            'A novel chemotype could emerge by round 3 with a 28% better predicted score than any candidate from a single-shot generation — a credible starting point for wet-lab validation.',
        },
        s3: {
          Title: 'Antibiotic scaffold revival against resistant Staph',
          Purpose:
            'Antimicrobial resistance is on the WHO\'s top-ten global health threats list. Reviving second-line antibiotics by re-engineering their scaffolds around resistance-conferring mutations is one of the more tractable response strategies — and a model for any "reformat an existing drug for a new variant" campaign.',
          Challenge:
            'Recover potency for a second-line antibiotic against MRSA isolates whose target enzyme has mutated at the binding interface.',
          Approach1: 'Load the mutant enzyme structure and the original antibiotic as the starting ligand.',
          Approach2: 'Use Mirror Molecule + Align Principal Axes pre-processing to normalize the scaffold orientation.',
          Approach3:
            'Run AI generation to propose analogs of the antibiotic scaffold, prompting for variation around the resistance-prone contacts.',
          Approach4:
            'Re-dock the survivors against a second wild-type enzyme structure to filter for selectivity, not just potency.',
          Outcome:
            'A workflow like this might identify two analogs that recover docking score within 10% of the wild-type baseline while keeping selectivity ratios above 5×.',
        },
        s4: {
          Title: 'P-doped silicon for thin-film photovoltaics',
          Purpose:
            'Silicon photovoltaics still account for roughly 95% of the global solar market, and the absorber-layer doping profile materially affects efficiency. A quick in-silico triage of doping configurations narrows the candidate list before expensive epitaxy commits to a specific composition.',
          Challenge:
            'Compare phosphorus doping configurations for a thin-film silicon absorber layer before committing to expensive epitaxy.',
          Approach1:
            'Build a 4×4×4 Si supercell in Crystal Builder and use the doping panel to substitute Si with P at several fractions.',
          Approach2:
            'Run short molecular-dynamics trajectories on each configuration to relax atomic positions around the dopants.',
          Approach3: 'Compare structural metrics across configurations using the analysis panel.',
          Approach4:
            'Save each relaxed supercell as its own project so downstream device modeling can pick them up by name.',
          Outcome:
            'A sweep like this could converge on a ~1.6 at% P configuration as the best balance of carrier density and dopant clustering — narrowing the epitaxy candidate list without an exhaustive first-principles sweep.',
        },
        s5: {
          Title: 'Mn-doped layered oxide cathode for Li-ion batteries',
          Purpose:
            'Li-ion cathodes are the limiting factor in the cost and energy density of every EV and grid-storage battery. Mn substitution patterns in layered oxides are an active R&D area for both performance and cobalt-light cost reduction — exactly the kind of high-throughput composition triage that benefits from a fast in-silico ranking step.',
          Challenge:
            'Rank Mn substitution patterns in a layered oxide cathode by predicted Li mobility, fast enough to triage before synthesis.',
          Approach1:
            'Build the host layered oxide in Crystal Builder, then use Substitutional Doping to construct several Mn substitution patterns.',
          Approach2: 'Run crystal molecular dynamics at 300 K and 600 K to estimate Li hopping rates.',
          Approach3: 'Compare diffusion plots from the analysis panel across configurations.',
          Approach4: 'Save the top 3 configurations to the Commons as a shared sub-project.',
          Outcome:
            'A study like this might surface a Mn-rich nearest-neighbor pattern with ~1.7× higher Li mobility at 300 K than the uniform-substitution baseline — useful for narrowing a synthesis queue.',
        },
        s6: {
          Title: 'Guest molecule adsorption in a porous crystal',
          Purpose:
            'Adsorption inside porous frameworks underlies CO₂ capture, gas separation, drug delivery, and heterogeneous catalysis. Knowing where small guests settle inside a pore is the first question in any of these applications — and a molecular-dynamics-ready problem that can be answered in an afternoon.',
          Challenge:
            'Estimate how a small substrate adsorbs inside the interstitial channels of a porous silica framework before committing to expensive simulations.',
          Approach1:
            'Load a porous silica polymorph (cristobalite) from the crystal database and drop the substrate as a guest molecule in the crystal lab.',
          Approach2: 'Run short molecular dynamics to let the guest find a low-energy site inside the framework.',
          Approach3:
            "Use the IR spectrum panel to read off shifts in the substrate's characteristic stretches when adsorbed.",
          Approach4: 'Repeat for three substrate orientations and compare shifts.',
          Outcome:
            'An afternoon of work could produce a defensible adsorption geometry + a candidate binding pocket — enough to replace weeks of speculative simulation runs with one targeted calculation.',
        },
        s7: {
          Title: 'Tubulin polymerization inhibitor for cancer chemotherapy',
          Purpose:
            'Microtubule-targeted antimitotics (taxol, vincristine, colchicine) are among the most successful anticancer drugs in clinical use. The colchicine-binding site remains comparatively under-explored versus the taxane site — a promising entry point for new oncology leads.',
          Challenge:
            'Find an antimitotic candidate that binds the tubulin β-subunit interface while preserving drug-like properties for an oral oncology lead.',
          Approach1:
            'Load the tubulin protein structure into the docking lab and select the colchicine-binding cleft as the target site.',
          Approach2:
            'Use AI generation to propose analogs of a known antimitotic scaffold, biased toward the colchicine-site geometry.',
          Approach3:
            'Rank candidates by docking score, Lipinski compliance, QED, and predicted aqueous solubility (logS) in the gallery.',
          Approach4:
            'Run the Stage-3 pose search on the top 10 to verify candidates remain inside the colchicine pocket under flex-docking.',
          Outcome:
            'A workflow like this might surface 4 candidates with predicted scores within 1 kcal/mol of colchicine while passing Lipinski + QED ≥ 0.6 — a starting list for synthesis prioritization.',
        },
        s8: {
          Title: 'Solid-state Li-ion electrolyte for all-solid-state batteries',
          Purpose:
            'Solid-state batteries promise higher energy density and dramatically lower fire risk than current liquid-electrolyte Li-ion designs. Garnet-type Li₇La₃Zr₂O₁₂ (LLZO) is the leading candidate electrolyte but its room-temperature ionic conductivity needs further tuning via dopants — a high-leverage triage problem if compositions can be ranked before synthesis.',
          Challenge:
            'Triage candidate solid-electrolyte compositions for Li-ion conductivity before committing to time-consuming synthesis.',
          Approach1:
            'Build a candidate solid electrolyte (e.g., an LLZO-type garnet) in Crystal Builder and use the doping panel to introduce Al or Ta substitutions on the host lattice.',
          Approach2:
            'Run crystal molecular dynamics at 300 K, 600 K, and 900 K to estimate Li-ion hopping rates across the composition series.',
          Approach3: 'Compare ionic mobility plots from the analysis panel across compositions and temperatures.',
          Approach4: 'Save the most-conductive composition to the Commons so collaborators can pick it up by name.',
          Outcome:
            'A study like this might rank an Al-doped LLZO variant ~2× higher in Li-ion mobility at 600 K than the undoped baseline — pointing the synthesis effort at the best composition first.',
        },
        s9: {
          Title: 'KRAS G12C covalent inhibitor for non-small-cell lung cancer',
          Purpose:
            'KRAS was the "undruggable" oncology target for forty years until sotorasib (Lumakras, FDA-approved 2021) and adagrasib opened the door to G12C-mutant non-small-cell lung cancer treatment. Designing the next generation of covalent inhibitors — targeting other KRAS mutations, escaping resistance, or improving selectivity — is among the most active areas in cancer drug discovery today.',
          Challenge:
            'Design a covalent warhead that selectively engages the cryptic switch-II pocket of the KRAS G12C mutant in its GDP-bound state.',
          Approach1:
            'Load the KRAS G12C structure (PDB 6OIM) into the docking lab with the bound sotorasib analog as the reference pose.',
          Approach2:
            'Use pocket-aware AI generation to propose warhead variants that engage the G12C cysteine residue while maintaining switch-II contacts.',
          Approach3:
            'Rank candidates by docking score, predicted covalent reactivity, and drug-likeness (Lipinski + QED).',
          Approach4:
            'Run Stage-3 pose search to confirm the warhead orientation persists in the cryptic switch-II pocket under flex-docking.',
          Outcome:
            'A run like this might surface 4 candidates with predicted binding within 0.5 kcal/mol of sotorasib and an improved selectivity profile — a credible starting list for covalent-bond formation kinetics studies.',
        },
        s10: {
          Title: 'Lead-free halide perovskite absorber for next-generation solar cells',
          Purpose:
            'Halide perovskites have rocketed past 26% certified efficiency in single-junction cells over the last decade, putting them on a commercial collision course with silicon. The remaining barrier is regulatory and environmental: replacing toxic Pb with Sn (or Ge) while preserving bandgap and stability is one of the most active areas in solar materials research.',
          Challenge:
            'Compare Sn-substituted halide perovskite compositions for bandgap and lattice stability before committing to expensive synthesis.',
          Approach1: 'Build the host halide perovskite (e.g., CsPbI₃) in Crystal Builder.',
          Approach2: 'Use Substitutional Doping to enumerate Pb→Sn substitution patterns at several fractions.',
          Approach3:
            'Run short molecular dynamics trajectories at 300 K to check phase stability of each configuration.',
          Approach4: 'Compare structural metrics from the analysis panel across compositions.',
          Outcome:
            'A study like this could identify the Sn fraction that maintains a 1.3–1.4 eV bandgap window while remaining structurally stable at room temperature — narrowing the synthesis queue without an exhaustive first-principles screen.',
        },
        s11: {
          Title: 'GLP-1 receptor small-molecule modulator for metabolic disease',
          Purpose:
            'GLP-1 receptor agonists (semaglutide, tirzepatide) have transformed obesity and type-2 diabetes treatment and are now the highest-grossing drug class in the industry, with emerging cardiac, kidney, and addiction indications. Replacing the injectable peptide agonists with orally bioavailable small molecules — or with positive allosteric modulators (PAMs) that boost endogenous GLP-1 — is the single most active area in metabolic drug discovery today.',
          Challenge:
            'Design a small-molecule positive allosteric modulator that activates the GLP-1 receptor without competing for the orthosteric peptide-binding site.',
          Approach1:
            'Load the GLP-1 receptor structure (PDB 7LCI) into the docking lab with the bound small-molecule agonist as the reference pose.',
          Approach2:
            'Use pocket-aware AI generation to propose chemotypes targeting the intracellular allosteric site rather than the orthosteric peptide site.',
          Approach3: 'Rank candidates by docking score, predicted oral bioavailability (Lipinski + Veber), and QED.',
          Approach4:
            'Run Stage-3 pose search to verify the PAM stays seated in the allosteric pocket under flex-docking.',
          Outcome:
            'A workflow like this might surface 3–5 oral-suitable PAM candidates that engage the intracellular face without disrupting the peptide-binding interface — a credible starting list for ex-vivo functional assay.',
        },
        s12: {
          Title: 'Metal-organic framework absorbent for direct air carbon capture',
          Purpose:
            'Direct air capture (DAC) of CO₂ is the keystone technology for industrial decarbonization, with billions of public and private investment racing to bring per-tonne capture costs below the $100 floor. Metal-organic frameworks (MOFs) — porous crystalline materials with tunable pore chemistry — are the leading materials class for next-generation DAC, outperforming amine-based liquid sorbents on regeneration energy.',
          Challenge:
            'Screen MOF compositions for CO₂ adsorption affinity and selectivity over H₂O before committing to gram-scale synthesis.',
          Approach1: 'Build a candidate MOF structure (e.g., MOF-74 or mmen-Mg₂(dobpdc)) in Crystal Builder.',
          Approach2:
            'Drop CO₂ and H₂O guest molecules in the lab to test pore filling under realistic atmospheric mixtures.',
          Approach3: 'Run crystal molecular dynamics at 298 K to estimate the equilibrium uptake ratio.',
          Approach4: 'Compare uptake across MOF candidates with the analysis panel.',
          Outcome:
            'A study like this could rank 5 candidate MOFs by CO₂-over-water selectivity and narrow the synthesis queue to the top 2 — saving months of expensive experimental screening.',
        },
        s13: {
          Title: 'Cadmium-free quantum dot for a display pixel',
          Purpose:
            "Quantum-dot displays turn a blue backlight into pure green and red with nanocrystals whose color is set by their size. The first commercial dots were CdSe; cadmium is restricted under RoHS, so the industry moved to indium phosphide, whose emission is broader and drifts more with size — a batch's size spread becomes color impurity. Choosing the host and the size is a design decision a student can make in AIMS with the same tools.",
          Challenge:
            'Pick a cadmium-free host and a dot diameter that emits at 530 nm (the green primary), and quantify how much the color shifts per nanometer of size so the synthesis tolerance is known.',
          Approach1:
            'Open the template: a 2.4 nm indium phosphide quantum dot cut from the InP lattice sits in the gallery and in the lab.',
          Approach2:
            'Ask the chat for its energy levels: the HOMO-LUMO gap and the emission wavelength and color of the dot.',
          Approach3:
            'Ask for a gap-versus-size sweep of InP, CdTe and ZnSe dots targeting 530 nm; the chart reports the diameter each host needs and the color drift per nanometer.',
          Approach4:
            'Build the chosen dot at the chosen size, confirm its color, and run it in the lab at room temperature to watch the surface relax.',
          Outcome:
            'A study like this finds that InP reaches green near 2.4 nm with a drift of about 100 nm of wavelength per nanometer of diameter, comparable to CdTe, while ZnSe cannot reach 530 nm at any solvable size — so a ±0.1 nm size spread costs about ±10 nm of color, the tolerance a synthesis has to hit.',
        },
      },
    },

    commons: {
      Title: 'The Commons',
      Tagline:
        "A citizen science community for exploring the molecular world. Browse projects shared by the AIMS community, fork them into your own account, and contribute back when you've built something interesting.",
      ProjectSingular: 'project',
      ProjectPlural: 'projects',
      ContributorSingular: 'contributor',
      ContributorPlural: 'contributors',
      SearchPlaceholder: 'Search by title, author, or description',
      SortByTime: 'Sort by time',
      SortByRanking: 'Sort by ranking',
      SortByViews: 'Sort by views',
      FilterByAuthor: 'Projects by {{name}}',
      ClearFilter: 'Clear',
      Anonymous: 'Anonymous',
      NoSearchResults: 'No projects match your search.',
      NoProjects: 'Nothing shared yet — be the first to contribute.',
      NoPreview: 'No preview',
      Like: 'Like',
      Unlike: 'Unlike',
      SignInToLike: 'Sign in to like',
      SignInToLikeMessage: 'Sign in to like projects.',
      LikeUpdateFailed: 'Could not update like. Try again.',
      Share: 'Share',
      Fork: 'Fork into my account',
      RemoveFromCommons: 'Remove from the Commons',
      SeeMoreFrom: 'See more from {{author}}',
      ViewsAndLikesTooltip: '{{views}} views · {{likes}} likes',
      Contribute: 'Want to contribute? Use',
      ContributeMenu: 'Project > Share to the Commons',
      LinkCopied: 'Link copied to clipboard',
      CopyLink: 'Copy link',
      ForkConfirmTitle: 'Fork "{{title}}" into your account?',
      ForkConfirmBody: 'A copy will be saved to your projects so you can edit it. The original stays unchanged.',
      ForkButton: 'Fork',
      ForkFailed: 'Could not fork the project. Try again.',
      ForkSuccessTitle: 'Saved as "{{newTitle}}"',
      ForkSuccessBody: 'Open it now to start editing your copy?',
      OpenButton: 'Open',
      StayHereButton: 'Stay here',
      RemoveConfirmTitle: 'Remove "{{title}}" from the Commons?',
      RemoveButton: 'Remove',
      RemoveSuccess: 'Removed from the Commons',
      RemoveFailed: 'Could not remove. Try again.',
      Copyright: '© 2023–2026 Institute for Future Intelligence, Inc. All rights reserved.',
      Terms: 'Terms',
      Privacy: 'Privacy',
    },

    tutorial: {
      Title: 'Tutorial',
      Tagline:
        "Artificial Intelligence for Molecular Sciences (AIMS) allows you to explore the molecular world with AI extensively. AI is woven through every step: describe a molecule or crystal in plain language and watch it appear, hand the assistant your analyses and docking runs, and turn a back-and-forth into a working simulation — all in your browser. This tour walks through the moves you'll use most often.",
      Step1Title: 'Sign in to save your work',
      Step1Body:
        'Click the avatar in the top-right of the app to sign in with Google, or start anonymously to try things without an account. Anonymous projects live in this browser only — once you sign in with Google, projects you save during the anonymous session migrate to your account automatically.',
      Step2Title: 'Pick a project type',
      Step2IntroPrefix: 'From ',
      Step2IntroSuffix: ' choose one of:',
      Step2Modeling: 'Molecular Modeling',
      Step2ModelingDesc:
        ' — the general-purpose molecular-dynamics sandbox. Build or import small molecules and set them in motion: the integrator handles bonded and non-bonded forces with an optional constant-temperature heat bath, gravitational and external fields, and a reactive mode in which bonds can break and form. Watch energy, temperature, vibrational spectra, diffusion (MSD), and the pair-distribution g(r) update live as it runs. Capped at 500 atoms per simulation.',
      Step2Discovery: 'Drug Discovery',
      Step2DiscoveryDesc:
        ' — a hands-on docking workbench. Load a protein target and drop a ligand into its binding site, then score the pose for binding affinity, drag it around with a live affinity readout, relax it as a rigid body inside the pocket, or run an automated multi-start search for the best fit. A pharmacophore overlay highlights the groups most likely to drive binding, and you can simply ask the AI assistant to dock for you. The lab is larger than in other projects and includes a flight mode for navigating around the protein.',
      Step2Crystal: 'Crystal Builder',
      Step2CrystalDesc:
        ' — a builder for periodic crystals across all four families (ionic, covalent, metallic, molecular). Start from the preset library (NaCl, ice, FCC/BCC/HCP metals, diamond, graphite, zeolite, and more), edit the lattice parameters and basis atoms by hand, or describe the structure to the AI; then engineer it — place substitutional dopants on host sites, introduce vacancies and other point defects, tile out a supercell, and drop guest molecules into the cell. Run molecular dynamics on the result with a force field matched to the crystal family (TIP4P/Ice for water, EAM for metals, SW/Tersoff/BKS for covalent and oxide crystals), and read out phonon spectra, diffusion, and g(r) as it runs. The engine runs in a Web Worker, so supercells can be large.',
      Step2Semi: 'Semiconductor Design',
      Step2SemiDesc:
        ' — explore how semiconductors work and how engineers tune them. Start from a clean host crystal (silicon, germanium, GaAs, …) and modify it: place substitutional dopants to make it p- or n-type; introduce vacancies or interstitial atoms as point defects; cut a thin slab along a chosen surface (100 / 110 / 111) and optionally cap dangling bonds with hydrogen; or stack two or more crystals into a heterostructure or superlattice device. AI then predicts properties (band gap, dielectric constant, work function, …) for every variant so you can compare designs without running a full electronic-structure calculation.',
      Step2Polymer: 'Polymer Builder',
      Step2PolymerDesc:
        ' — design a polymer and study its chains. Define one or more monomers (pick them from the gallery, generate with AI, or import), grow them into a chain — a homopolymer, or a copolymer with block, random, or alternating sequencing — and choose a topology: linear, ring, star, comb, branched, dendritic, or hyperbranched. Build a single chain to measure its radius of gyration (Rg), end-to-end distance, and persistence length, or pack several chains together for interaction studies. Chains are relaxed before simulation, and AI can predict bulk properties — glass-transition and melting temperatures, density, and solubility parameter.',
      Step2Note: 'You can switch by creating another project — they coexist under your account.',
      Step3Title: 'The lab',
      Step3Body:
        'The large 3D viewport is the lab. It opens beside the project gallery — drag the divider between them to resize, or collapse the gallery to give the lab the full window (on a phone, the lab and gallery are separate tabs). Left-drag to rotate, right-drag to pan, scroll to zoom. Right-click empty space for a context menu with viewer options (style, coloring, background, axes, container box). Click an atom or bond to select it.',
      Step3ContextMenu:
        'Right-clicking a selected atom or bond opens a per-target menu — measure distances and angles, set restraints, delete, or change the element. Right-clicking a molecule opens a per-molecule menu (translate, rotate, freeze, duplicate).',
      Step4Title: 'Build your scene',
      Step4Intro: 'For Molecular Modeling and Drug Discovery projects, two ways to add molecules:',
      Step4Search: 'Search',
      Step4SearchDesc:
        ' the built-in gallery — common molecules, hydrocarbons, biomolecules, drug molecules, monatomic species, and crystals.',
      Step4AI: 'Generate with AI',
      Step4AIDesc:
        ' — describe what you want in plain English ("a polar molecule with one chlorine atom") and AIMS returns an SDF you can drop into the lab. Recent prompts are kept as conversational context so follow-ups can refer to "make it more lipophilic" or similar.',
      Step4CrystalIntro: 'For Crystal Builder and Semiconductor Design projects, three ways to define a crystal:',
      Step4Preset: 'Load a preset',
      Step4PresetDesc:
        ' — pick from the built-in library (NaCl, ice, FCC/BCC/HCP metals, diamond, graphite, zeolite, and more) using the dropdown at the top of the crystal builder panel.',
      Step4Custom: 'Compose your own',
      Step4CustomDesc:
        ' — edit lattice parameters and basis atoms in the builder, place substitutional dopants on host sites, or drop guest molecules into the cell from the lab toolbar.',
      Step4CrystalAI: 'Generate with AI',
      Step4CrystalAIDesc:
        ' — describe the structure you want ("a wurtzite ZnO crystal" or "a 2×2×2 NaCl supercell with one Cl vacancy") and AIMS proposes a crystal definition you can load directly into the lab.',
      Step4PolymerIntro: 'For Polymer Builder projects, build a chain in two moves:',
      Step4PolymerMonomer: 'Pick the monomers',
      Step4PolymerMonomerDesc:
        ' — search the gallery, generate with AI, or import a structure, then mark the head and tail atoms each repeat unit links through. Select several monomers to build a copolymer.',
      Step4PolymerChain: 'Build the chain',
      Step4PolymerChainDesc:
        ' — open the Build Chain dialog to set the length, sequence (block, random, or alternating), topology (linear, ring, star, comb, branched, dendritic, or hyperbranched), and how many chains to pack together. AIMS relaxes the chain before it drops into the lab.',
      Step5Title: 'Run a simulation',
      Step5Body:
        'Every project type except Drug Discovery can run molecular dynamics — Molecular Modeling, Polymer Builder, Crystal Builder, and Semiconductor Design all have a play button under the lab (Drug Discovery docks ligands instead; see below). Press play to start. The simulation integrates with a configurable timestep, optional constant-temperature heat bath, gravitational and external fields, and van-der-Waals + Coulombic forces (crystal and semiconductor projects also use force fields matched to the crystal family — TIP4P/Ice for water, EAM for metals, SW/Tersoff for covalent crystals). Open the analysis panel (chart icon under the lab) to see live data across multiple tabs: Energy, Speed, IR Spectrum, Phonon or Vibration, MSD, and g(r), plus a Reaction tab when reactive mode is on and a Polymer tab in Polymer Builder projects — most tabs offer an AI-assisted "interpret this" button. Molecular and polymer simulations cap at 500 atoms; crystal supercells can be larger.',
      Step6Title: 'Save the project',
      Step6Body1:
        ' writes the scene to your account on Firebase. The whole project state travels — molecules, atom positions, bonds, viewer settings, AI prompt history. You can rename, duplicate, or delete from ',
      Step6Body2: '.',
      Step7Title: 'Share to the Commons',
      Step7Body1: 'When a project is ready to show off, choose ',
      Step7Body2:
        ". AIMS captures a thumbnail of the current lab and publishes a read-only entry in the public Commons. Edits you make afterwards appear immediately to viewers — it's a live link, not a snapshot. Choose ",
      Step7Body3: ' to unpublish.',
      Step8Title: 'Discover and fork',
      Step8Body1: 'The ',
      Step8Body2: ' lists every shared project. Click a card to open it read-only, or click ',
      Step8Body3:
        " to clone it into your own account so you can experiment freely — the original stays untouched. Like projects you find interesting; click an author's name to filter the page to just their work.",
      StepDockTitle: 'Dock a ligand (Drug Discovery)',
      StepDockBody:
        'In Drug Discovery projects, drop a ligand into the lab and the docking panel appears. It scores the current pose instantly — interaction energy, an estimated ΔG / Kd, and which residues contribute most — and updates live as you drag the ligand by hand. Let AIMS find candidate pockets, then relax the pose or run an automated multi-start search to place the ligand; by default it flexes its rotatable bonds to fold into the pocket, or you can dock it rigidly. Screen your whole gallery against a pocket and rank the hits by affinity, ask the AI to design fresh ligands for it, or hand the whole job to the docking agent in plain language. A pharmacophore overlay highlights the aromatic and charged groups most likely to drive binding.',
      StepVRTitle: 'Step into the scene (VR)',
      StepVRBody:
        'On a WebXR-capable device (compatible phone, tablet, or headset), click the "Enter VR" button below the lab to immerse yourself inside the scene. Gaze-controlled floating buttons let you zoom and toggle the simulation hands-free. Dwell your gaze on an atom for about a second to select it — that selection persists when you exit VR, so atom info / restraints / context-menu actions all pick up where you left off.',
      StepAgentTitle: 'Chat with the AI agent',
      StepAgentIntro:
        'Switch the gallery to its Chat view and talk to AIMS in plain language. Simple asks get a direct answer or a generated structure; for a bigger job the chat escalates to an agent that plans and runs a whole sequence of actions on your scene — the same tools you would otherwise click — narrating each step as it goes. Beyond building structures, it can read what is in your scene, edit a structure in place, change how it looks and where the camera sits, set up and run simulations, import a structure you paste in or hand the current one back as a file, manage your gallery cards and charts, and answer questions grounded in your own data. Refer to the last result with "it" — "swap the fluorine for chlorine", "now make it space-filling", "give me its SDF" — and the agent knows what you mean. Chat is available in every project type, and what the agent can do follows the project:',
      StepAgentExampleLabel: 'EXAMPLE',
      StepAgentExamplePrompt: 'Clear the lab, load benzene from the gallery, and compute its IR spectrum.',
      StepAgentExampleBody:
        'From that one sentence the agent removes whatever is currently in the lab, finds benzene in the gallery and loads it, configures the simulation, runs the dynamics, tracks how the dipole changes as the molecule vibrates, and returns the spectrum — several tools chained together, each step narrated as it runs.',
      StepAgentModelingDesc:
        ' — look up or generate molecules; edit one in place (swap an element, add/remove an atom or bond, set a formal charge, graft a group), with properties recomputed automatically; import a structure you paste in or export one as SDF/XYZ; load it into the lab and run molecular dynamics with the temperature, thermostat, and timestep you ask for; change the render style, colors, background, and camera view; rename, delete, or hide gallery cards and set up charts; and compute analyses such as the IR spectrum, radial distribution g(r), and energy statistics.',
      StepAgentDiscoveryDesc:
        ' — find binding pockets, dock a ligand, run a pose search, screen your gallery against the target, and import or export ligand structures.',
      StepAgentCrystalDesc:
        ' — look up, generate, or edit crystals (basis atoms, lattice, dopants, defects, supercells); import or export CIF; load a supercell into the lab and set up the simulation (target temperature, NVT/NVE, thermostat, timestep); change the render style, background, and camera view; rename or delete cards; and configure charts.',
      StepAgentSemiDesc:
        ' — adjust the host crystal, place dopants and defects, cut slabs or stack devices, and run the full electronic-structure suite: band structure, density of states, effective masses, carrier statistics, optical absorption, and device band alignment.',
      StepAgentPolymerDesc: ' — build chains, predict polymer properties, and measure chain dimensions.',
      StepAgentNote:
        ' You can watch the agent work in the reasoning panel, stop it at any time, and take over by hand whenever you like.',
      StepGalleryTitle: 'Compare across your gallery',
      StepGalleryBody:
        'Saved projects open in a gallery view by default. Beyond the thumbnail grid, switch to property-space views — scatter plot, radar chart, PCA projection, Pareto frontier, similarity map, histogram, bubble chart, structure–property scatter — to find correlations or outliers across the molecules or crystals you have saved. Drug-discovery galleries also include screening tools. You can also just ask the AI assistant to set up a chart for you ("plot molecular weight against logP") and it configures the view.',

      RefHeading: 'Supplement',
      RefIntro:
        'Deep dives into the tools the tour mentioned but did not unpack. Skim the headings to find what you need.',

      RefGalleryTitle: 'Gallery analytics tools',
      RefGalleryIntro:
        "Every chart below lives under the gallery view's graph-type dropdown. Most expect at least 3–4 saved projects to be useful; a few (PCA, similarity views, scaffold clusters) need 10+ before the picture stabilizes.",
      RefScatterName: 'Scatter Plot',
      RefScatterDesc:
        'Plots one numeric property against another across every molecule in the gallery. The workhorse view for "is X related to Y?".',
      RefScatterRead1: 'Pick the X / Y axes from the property dropdowns at the top of the chart.',
      RefScatterRead2:
        "A clear diagonal cloud means the properties co-vary; a flat / circular cloud means they're independent.",
      RefScatterRead3:
        'Outliers — points far off the main cloud — are usually the most interesting molecules to open and inspect.',
      RefBubbleName: 'Bubble Chart',
      RefBubbleDesc:
        'A scatter plot that uses bubble size (and optionally color) to encode a third and fourth property — useful when you want to see four dimensions at once without leaving 2D.',
      RefBubbleRead1:
        'X / Y are positions as in a normal scatter; bubble area encodes the third property (configurable in the popover).',
      RefBubbleRead2:
        'Look for size gradients along an axis — those mean the third property tracks one of the first two.',
      RefHistName: 'Histogram',
      RefHistDesc:
        'Distribution of a single numeric property across the gallery. The simplest way to see "how spread out is X?" or "is there a long tail?".',
      RefHistRead1: 'Bin count is configurable in the popover; default ≈ √N.',
      RefHistRead2:
        'A long right tail (a few high values, many low ones) is the typical pattern for properties like molecular weight; a bimodal shape often means two distinct chemical families in your gallery.',
      RefParCoordName: 'Parallel Coordinates',
      RefParCoordDesc:
        'Every property gets its own vertical axis; each molecule is a line crossing all axes. Best for spotting which property combinations cluster vs. which are rare.',
      RefParCoordRead1:
        'Drag along an axis to filter — only molecules whose value falls in the dragged range stay highlighted.',
      RefParCoordRead2:
        'A bundle of lines that all rise on axis A and fall on axis B means A and B are negatively correlated for that subset.',
      RefRadarName: 'Radar Chart',
      RefRadarDesc:
        'Spider plot of one molecule\'s properties, all normalized so 0 is the gallery minimum and 1 is the gallery maximum. When the visible axes are exactly the six Lipinski/Veber drug-likeness criteria, the chart switches to a "Rule of Five" mode that draws a dashed reference polygon at the drug-like threshold.',
      RefRadarRead1:
        'The other molecules show as light-gray polygons for context; the selected molecule is drawn on top in color.',
      RefRadarRead2:
        'In drug-likeness mode, staying inside the dashed polygon on all axes is the qualitative "drug-like" target.',
      RefCorrHeatName: 'Correlation Heatmap',
      RefCorrHeatDesc:
        'Side-by-side Pearson (linear) and distance-correlation (any dependency) matrices across all visible numeric properties. Pairs naturally with PCA — PCA summarizes the linear correlations into 2 axes, this view shows the raw pairwise picture.',
      RefCorrHeatRead1:
        'Red = positive, blue = negative on the Pearson side; white = no relationship. Distance correlation is unsigned (0–1) on a single hue.',
      RefCorrHeatRead2:
        "A pair that's strong on distance correlation but weak on Pearson hides a non-linear or non-monotonic relationship worth a scatter-plot lookup.",
      RefPCAName: 'PCA Projection',
      RefPCADesc:
        'Projects the full property space down to 2D using principal component analysis. Molecules that share a profile across many properties end up near each other on the plot, even if no single property pair separates them.',
      RefPCARead1:
        'Axes are PC1 / PC2 — abstract combinations of the input properties, not the properties themselves. The variance fraction each axis explains is shown at the bottom.',
      RefPCARead2:
        'Tight clusters = chemically similar populations; outliers off to one side often have an unusual value in whatever property loads heaviest on that axis.',
      RefParetoName: 'Pareto Frontier',
      RefParetoDesc:
        'Pick two properties and a direction (minimize / maximize) for each. The view highlights the non-dominated set — molecules that no other molecule beats on both axes given the chosen directions. Gated to Drug Discovery projects because the optimization direction is rarely well-defined for general molecular modeling.',
      RefParetoRead1:
        'The Pareto frontier is the colored "step" through the data — those are your best-trade-off candidates.',
      RefParetoRead2:
        'Use it to triage hits: a molecule on the frontier is at least as good as any other across both axes; a molecule far from it is dominated and can be deprioritized.',
      RefStructPropName: 'Structure–Property Scatter',
      RefStructPropDesc:
        'Pairwise plot of structural distance vs. property distance for every molecule pair in the gallery. Answers "when structures change, how much do properties change?".',
      RefStructPropRead1:
        'Points along the diagonal mean structural changes track property changes — a smooth structure-activity relationship.',
      RefStructPropRead2:
        'Off-diagonal points — small structural distance with large property distance — are activity cliffs worth opening in the Activity Cliffs view.',
      RefSimHeatName: 'Similarity Heatmap',
      RefSimHeatDesc:
        'Pairwise Tanimoto similarity (on Morgan fingerprints) between every molecule pair. The structural counterpart to the Correlation Heatmap.',
      RefSimHeatRead1:
        'Cells in [0, 1]: 1 = identical structures, 0 = no shared features. Rows / columns are molecules.',
      RefSimHeatRead2:
        'Bright blocks along the diagonal are clusters of structurally related molecules; uniform mid-values mean a structurally diverse gallery.',
      RefSimProjName: 'Similarity Projection',
      RefSimProjDesc:
        '2D MDS embedding of the fingerprint-distance matrix — structurally related molecules cluster on the plot, regardless of their property values. Pair it with the PCA Projection to ask "do structural clusters also share properties?".',
      RefSimProjRead1: 'Distance between two points roughly tracks their Tanimoto distance; axis units are arbitrary.',
      RefSimProjRead2:
        "If structural clusters here line up with property clusters in PCA, you have a smooth SAR; if they don't, your gallery has activity cliffs.",
      RefScaffoldName: 'Scaffold Clusters',
      RefScaffoldDesc:
        'Groups molecules by Tanimoto similarity into "structural families" with a configurable cohesion threshold. Each cluster\'s representative is the molecule with the highest mean similarity to the rest.',
      RefScaffoldRead1: 'Slide the threshold up for more, tighter clusters; down for fewer, looser ones.',
      RefScaffoldRead2:
        'Use the representatives as a small, diverse subset that covers the gallery without redundant near-duplicates.',
      RefCliffsName: 'Activity Cliffs',
      RefCliffsDesc:
        'Ranks every molecule pair by SALI score (|Δproperty| / structural distance). Two modes: "Cliffs" finds near-identical structures with divergent properties (the most informative pairs for "what drives this property?"); "Hops" finds distant structures with similar properties (useful for scaffold-hopping).',
      RefCliffsRead1:
        'Sort can be by aggregate score across visible properties, or constrained to one property at a time for cleaner interpretation.',
      RefCliffsRead2:
        'A high cliff score means a small structural change produced a large property change — usually the most teachable pair in the gallery.',
      RefBitCorrName: 'Bit Correlations',
      RefBitCorrDesc:
        "Heatmap of fingerprint-bit ↔ numeric-property Pearson correlations. Surfaces which structural fragments push a property up or down across the gallery — a poor-man's QSAR table without fitting a regression model.",
      RefBitCorrRead1:
        'Same blue↔white↔red palette as the Correlation Heatmap; rows are the most-informative fingerprint bits, columns are properties.',
      RefBitCorrRead2:
        'Hover a row to preview the substructure the bit encodes; a strong red cell means molecules carrying that fragment trend high on that property.',

      RefDockTitle: 'Docking panel',
      RefDockIntro:
        'The panel sits at the top-right of the lab in Drug Discovery projects whenever a ligand is loaded. It runs from a live score through pose refinement, automated search, whole-gallery screening, and AI-driven ligand design.',
      RefDockScoreName: 'Binding score',
      RefDockScoreDesc:
        'Instant readout for the ligand in its current position, broken into van-der-Waals, electrostatic, and hydrogen-bond terms, with hydrogen-bond and steric-clash counts. Computed on the spot with no minimization, so it reflects exactly where you put the ligand right now.',
      RefDockScoreRead1:
        'Lower (more negative) scores = stronger binding. The value updates whenever the ligand moves.',
      RefDockScoreRead2:
        'Green = favorable, amber = unfavorable, red = clashing. A red score means the ligand overlaps the receptor and needs to move.',
      RefDockAffinityName: 'Binding affinity & top contributors',
      RefDockAffinityDesc:
        'Below the raw score, the panel estimates a binding free energy (ΔG) and dissociation constant (Kd) from the interaction energy plus a rotatable-bond entropy penalty, and lists the receptor residues contributing the most favorable energy at the current pose.',
      RefDockAffinityRead1: 'Treat ΔG / Kd as a relative guide for ranking poses, not an absolute measurement.',
      RefDockAffinityRead2:
        'Top Contributors tells you which residues the ligand is actually binding — useful for reasoning about selectivity.',
      RefDockLiveName: 'Live readout',
      RefDockLiveDesc:
        'The score streams continuously while you drag the ligand by hand — same calculation as the binding score, just re-evaluated every move so you can "feel" the affinity landscape with your cursor.',
      RefDockLiveRead1:
        "Watch for the value dropping as you slide along a pocket wall — that's the favorable contact direction.",
      RefDockLiveRead2: 'Green/amber/red coloring updates live too, so clashes are obvious the moment you create them.',
      RefDockFlexibleName: 'Flexible ligand',
      RefDockFlexibleDesc:
        "When on (the default), the ligand's rotatable bonds are free to rotate during relax and search so it can fold into the pocket, and a conformation-RMSD readout shows how far it has bent from its input shape. Turn it off to dock the ligand as a rigid body.",
      RefDockFlexibleRead1:
        'Flexible docking finds better poses but is slower and penalizes large, strained distortions.',
      RefDockFlexibleRead2:
        'Rigid docking is faster and fine when the ligand is already in a sensible conformation or has few rotatable bonds.',
      RefDockPocketsName: 'Find pockets',
      RefDockPocketsDesc:
        'Scans the receptor for candidate binding pockets and lists them ranked by volume, each annotated with its lining residues. Click a pocket to target it as the site for search and screening; click again to clear.',
      RefDockPocketsRead1: 'Set a pocket before Screen Gallery or Design Ligands — both need a defined site.',
      RefDockPocketsRead2:
        "The biggest pocket isn't always the active site; cross-check against known catalytic residues.",
      RefDockRelaxName: 'Relax the pose',
      RefDockRelaxDesc:
        'Minimizes the ligand in a held-fixed pocket — translating, rotating, and (when Flexible Ligand is on) flexing its rotatable bonds to settle into the nearest favorable pose. A few hundred steps, no global repositioning.',
      RefDockRelaxRead1:
        "Best for refining a pose that's already in the right ballpark — relax won't escape a wrong pocket.",
      RefDockRelaxRead2:
        'The settled score is the affinity for that pocket / orientation; reset and try a different start if you suspect a better basin.',
      RefDockSearchName: 'Automated search',
      RefDockSearchDesc:
        'Multi-start search: spawns several relax runs from randomized starting poses (respecting the Flexible Ligand and Pharmacophore-guided toggles), keeps the best, and lists the top poses for click-to-apply. Takes seconds to minutes depending on receptor size and the number of starts.',
      RefDockSearchRead1: 'Treat the top hit as a strong "first pass" pose — inspect it before drawing conclusions.',
      RefDockSearchRead2:
        "If several starts converge to the same pose, you can be confident it's the best within the search region.",
      RefDockPharmGuidedName: 'Pharmacophore-guided search',
      RefDockPharmGuidedDesc:
        "A toggle that biases relax and search toward aligning the ligand's aromatic rings and charged groups with complementary features on the receptor — steering the optimizer toward chemically sensible interactions rather than raw energy alone.",
      RefDockPharmGuidedRead1:
        'Distinct from the pharmacophore overlay below, which only draws the features; this one acts on the search.',
      RefDockPharmGuidedRead2:
        'Helps when a pocket has an obvious anchor — a salt bridge or π-stack — you want the ligand to find.',
      RefDockScreenName: 'Screen the gallery',
      RefDockScreenDesc:
        'Virtual screening: docks every selected gallery molecule at the chosen pocket, ranks them by ΔG, and lets you click any row to load that candidate at its best pose. Export the ranked table as CSV, or hit Ask AI for a medicinal-chemist-style readout of the results.',
      RefDockScreenRead1: 'Needs a pocket selected first; the button shows how many candidates are queued.',
      RefDockScreenRead2: 'Use it to triage a library down to the handful worth inspecting by hand.',
      RefDockDesignName: 'Design ligands (AI)',
      RefDockDesignDesc:
        'Asks the AI to propose new ligand structures tailored to the selected pocket — a generative complement to screening an existing library.',
      RefDockDesignRead1: 'Needs a pocket selected so the model knows what to design against.',
      RefDockDesignRead2: 'Generated molecules are starting points to score and refine, not finished candidates.',
      RefDockAgentName: 'Docking agent',
      RefDockAgentDesc:
        'Opens a chat where you describe a docking goal in plain language ("find the best pose in the active site, then screen my gallery") and the agent drives the panel — detecting pockets, searching, screening — on your behalf.',
      RefDockAgentRead1: 'The agent uses the same tools described here; watch it work and take over at any point.',
      RefDockAgentRead2: "Good for multi-step jobs you'd otherwise click through stage by stage.",
      RefDockPharmName: 'Pharmacophore overlay',
      RefDockPharmDesc:
        'A display toggle that highlights aromatic rings (purple spheres) and net-charged groups (red / blue spheres) on the ligand — the moieties most likely to drive specific interactions with the receptor. Toggle on to plan a pose; toggle off to see clean geometry.',
      RefDockPharmRead1:
        'Position the pharmacophore spheres into complementary pocket features (charged groups into salt bridges, aromatics into π-stacks) before relaxing.',

      RefVRTitle: 'VR controls',
      RefVRIntro:
        'AIMS implements its VR experience with WebXR, so it runs on any device that supports the spec — modern Android phones, recent iPhones, and dedicated headsets (Quest, Vision Pro, etc.). No login or app install — the browser tab itself becomes immersive.',
      RefVREnterName: 'Enter / Exit VR',
      RefVREnterDesc:
        'The "Enter VR" button under the lab appears whenever the browser reports WebXR support. On a phone, it asks for motion-sensor permission and then drops you into a single-eye immersive view; on a headset, it requests the immersive session and you put the device on.',
      RefVREnterRead1:
        "If the button doesn't appear, your browser / device doesn't expose WebXR. Chrome on Android and Safari on iOS 17+ both work for the phone path.",
      RefVREnterRead2: 'Exit anytime by clicking the floating "X" button or the system back gesture.',
      RefVRGazeName: 'Gaze-controlled navigation buttons',
      RefVRGazeDesc:
        "Three floating disc buttons hover at the bottom edge of the view: zoom out, sim play/pause, zoom in. They're activated by looking at them — a progress arc fills around the disc as your gaze dwells, and the action fires when the arc completes (about a second).",
      RefVRGazeRead1: 'Look away mid-dwell and the arc resets — no accidental commits if you glance across.',
      RefVRGazeRead2:
        "The play/pause button mirrors the simulation's actual state, so the icon flips between ▶ and ⏸ to show what action it'll trigger.",
      RefVRAtomName: 'Atom-selection by gaze dwell',
      RefVRAtomDesc:
        'Look at any atom for about a second to select it. A reticle ring fills as you dwell, and the selection commits when the ring completes. The selected atom is the same selection your lab uses outside VR — info panels, halos, and context-menu actions all reflect what you chose in-headset.',
      RefVRAtomRead1:
        "The reticle only fills while you're holding on a single atom; glancing to a different atom resets the timer.",
      RefVRAtomRead2:
        'Useful for inspecting busy regions where pointer-clicking would be fiddly — your gaze is already a precise pointer.',
      RefVRExitName: 'Behavior after exit',
      RefVRExitDesc:
        'When you exit VR, the selection and view state persist. The atom you selected in headset is still selected when the desktop lab redraws; any sim toggle you flipped stays flipped. You can pick up exactly where you left off.',

      ReadyTitle: 'Ready to start?',
      ReadyBody1: 'Head back to the app and create your first project — or browse the ',
      ReadyBody2: ' for inspiration first.',
      CommonsLink: 'Commons',
      MenuProjectNew: 'Project > New',
      MenuProjectSave: 'Project > Save',
      MenuProjectOpen: 'Project > Open',
      MenuShareCommons: 'Project > Share to the Commons',
      MenuRemoveCommons: 'Remove from the Commons',
      ForkKbd: 'Fork',
      Copyright: '© 2023–2026 Institute for Future Intelligence, Inc. All rights reserved.',
      Terms: 'Terms',
      Privacy: 'Privacy',
    },
  },
};
